'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
const _excluded = ["maxScrollSize"],
  _excluded2 = ["onScroll", "onSectionRendered", "scrollLeft", "scrollToColumn", "scrollTop", "scrollToRow"],
  _excluded3 = ["rowIndex"],
  _excluded4 = ["columnIndex", "rowIndex"],
  _excluded5 = ["columnIndex"];
import * as React from 'react';
import React__default, { PureComponent, Component } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';

/**
 * This HOC decorates a virtualized component and responds to arrow-key events by scrolling one row or column at a time.
 */

class ArrowKeyStepper extends React.PureComponent {
  constructor(props) {
    super(props);
    this._columnStartIndex = 0;
    this._columnStopIndex = 0;
    this._rowStartIndex = 0;
    this._rowStopIndex = 0;
    this._onKeyDown = event => {
      const {
        columnCount,
        disabled,
        mode,
        rowCount
      } = this.props;
      if (disabled) {
        return;
      }
      const {
        scrollToColumn: scrollToColumnPrevious,
        scrollToRow: scrollToRowPrevious
      } = this._getScrollState();
      let {
        scrollToColumn,
        scrollToRow
      } = this._getScrollState();

      // The above cases all prevent default event event behavior.
      // This is to keep the grid from scrolling after the snap-to update.
      switch (event.key) {
        case 'ArrowDown':
          scrollToRow = mode === 'cells' ? Math.min(scrollToRow + 1, rowCount - 1) : Math.min(this._rowStopIndex + 1, rowCount - 1);
          break;
        case 'ArrowLeft':
          scrollToColumn = mode === 'cells' ? Math.max(scrollToColumn - 1, 0) : Math.max(this._columnStartIndex - 1, 0);
          break;
        case 'ArrowRight':
          scrollToColumn = mode === 'cells' ? Math.min(scrollToColumn + 1, columnCount - 1) : Math.min(this._columnStopIndex + 1, columnCount - 1);
          break;
        case 'ArrowUp':
          scrollToRow = mode === 'cells' ? Math.max(scrollToRow - 1, 0) : Math.max(this._rowStartIndex - 1, 0);
          break;
      }
      if (scrollToColumn !== scrollToColumnPrevious || scrollToRow !== scrollToRowPrevious) {
        event.preventDefault();
        this._updateScrollState({
          scrollToColumn,
          scrollToRow
        });
      }
    };
    this._onSectionRendered = ({
      columnStartIndex,
      columnStopIndex,
      rowStartIndex,
      rowStopIndex
    }) => {
      this._columnStartIndex = columnStartIndex;
      this._columnStopIndex = columnStopIndex;
      this._rowStartIndex = rowStartIndex;
      this._rowStopIndex = rowStopIndex;
    };
    this.state = {
      scrollToColumn: props.scrollToColumn,
      scrollToRow: props.scrollToRow
    };
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.isControlled) {
      return;
    }
    const {
      scrollToColumn,
      scrollToRow
    } = nextProps;
    const {
      scrollToColumn: prevScrollToColumn,
      scrollToRow: prevScrollToRow
    } = this.props;
    if (prevScrollToColumn !== scrollToColumn && prevScrollToRow !== scrollToRow) {
      this.setState({
        scrollToColumn,
        scrollToRow
      });
    } else if (prevScrollToColumn !== scrollToColumn) {
      this.setState({
        scrollToColumn
      });
    } else if (prevScrollToRow !== scrollToRow) {
      this.setState({
        scrollToRow
      });
    }
  }
  setScrollIndexes({
    scrollToColumn,
    scrollToRow
  }) {
    this.setState({
      scrollToRow,
      scrollToColumn
    });
  }
  render() {
    const {
      className,
      children
    } = this.props;
    const {
      scrollToColumn,
      scrollToRow
    } = this._getScrollState();
    return /*#__PURE__*/React.createElement("div", {
      className: className,
      onKeyDown: this._onKeyDown
    }, children({
      onSectionRendered: this._onSectionRendered,
      scrollToColumn,
      scrollToRow
    }));
  }
  _getScrollState() {
    return this.props.isControlled ? this.props : this.state;
  }
  _updateScrollState({
    scrollToColumn,
    scrollToRow
  }) {
    const {
      isControlled,
      onScrollToChange
    } = this.props;
    if (typeof onScrollToChange === 'function') {
      onScrollToChange({
        scrollToColumn,
        scrollToRow
      });
    }
    if (!isControlled) {
      this.setState({
        scrollToColumn,
        scrollToRow
      });
    }
  }
}

/**
 * Detect Element Resize.
 * https://github.com/sdecima/javascript-detect-element-resize
 * Sebastian Decima
 *
 * Forked from version 0.5.3; includes the following modifications:
 * 1) Guard against unsafe 'window' and 'document' references (to support SSR).
 * 2) Defer initialization code via a top-level function wrapper (to support SSR).
 * 3) Avoid unnecessary reflows by not measuring size for scroll events bubbling from children.
 * 4) Add nonce for style element.
 **/
ArrowKeyStepper.defaultProps = {
  disabled: false,
  isControlled: false,
  mode: 'edges',
  scrollToColumn: 0,
  scrollToRow: 0
};
function createDetectElementResize(nonce) {
  // Check `document` and `window` in case of server-side rendering
  var _window;
  if (typeof window !== 'undefined') {
    _window = window;
  } else if (typeof self !== 'undefined') {
    _window = self;
  } else {
    _window = global;
  }
  var attachEvent = typeof document !== 'undefined' && document.attachEvent;
  if (!attachEvent) {
    var requestFrame = function () {
      var raf = _window.requestAnimationFrame || _window.mozRequestAnimationFrame || _window.webkitRequestAnimationFrame || function (fn) {
        return _window.setTimeout(fn, 20);
      };
      return function (fn) {
        return raf(fn);
      };
    }();
    var cancelFrame = function () {
      var cancel = _window.cancelAnimationFrame || _window.mozCancelAnimationFrame || _window.webkitCancelAnimationFrame || _window.clearTimeout;
      return function (id) {
        return cancel(id);
      };
    }();
    var resetTriggers = function resetTriggers(element) {
      var triggers = element.__resizeTriggers__,
        expand = triggers.firstElementChild,
        contract = triggers.lastElementChild,
        expandChild = expand.firstElementChild;
      contract.scrollLeft = contract.scrollWidth;
      contract.scrollTop = contract.scrollHeight;
      expandChild.style.width = expand.offsetWidth + 1 + 'px';
      expandChild.style.height = expand.offsetHeight + 1 + 'px';
      expand.scrollLeft = expand.scrollWidth;
      expand.scrollTop = expand.scrollHeight;
    };
    var checkTriggers = function checkTriggers(element) {
      return element.offsetWidth != element.__resizeLast__.width || element.offsetHeight != element.__resizeLast__.height;
    };
    var scrollListener = function scrollListener(e) {
      // Don't measure (which forces) reflow for scrolls that happen inside of children!
      if (e.target.className.indexOf('contract-trigger') < 0 && e.target.className.indexOf('expand-trigger') < 0) {
        return;
      }
      var element = this;
      resetTriggers(this);
      if (this.__resizeRAF__) {
        cancelFrame(this.__resizeRAF__);
      }
      this.__resizeRAF__ = requestFrame(function () {
        if (checkTriggers(element)) {
          element.__resizeLast__.width = element.offsetWidth;
          element.__resizeLast__.height = element.offsetHeight;
          element.__resizeListeners__.forEach(function (fn) {
            fn.call(element, e);
          });
        }
      });
    };

    /* Detect CSS Animations support to detect element display/re-attach */
    var animation = false,
      keyframeprefix = '',
      animationstartevent = 'animationstart',
      domPrefixes = 'Webkit Moz O ms'.split(' '),
      startEvents = 'webkitAnimationStart animationstart oAnimationStart MSAnimationStart'.split(' '),
      pfx = '';
    {
      var elm = document.createElement('fakeelement');
      if (elm.style.animationName !== undefined) {
        animation = true;
      }
      if (animation === false) {
        for (var i = 0; i < domPrefixes.length; i++) {
          if (elm.style[domPrefixes[i] + 'AnimationName'] !== undefined) {
            pfx = domPrefixes[i];
            keyframeprefix = '-' + pfx.toLowerCase() + '-';
            animationstartevent = startEvents[i];
            animation = true;
            break;
          }
        }
      }
    }
    var animationName = 'resizeanim';
    var animationKeyframes = '@' + keyframeprefix + 'keyframes ' + animationName + ' { from { opacity: 0; } to { opacity: 0; } } ';
    var animationStyle = keyframeprefix + 'animation: 1ms ' + animationName + '; ';
  }
  var createStyles = function createStyles(doc) {
    if (!doc.getElementById('detectElementResize')) {
      //opacity:0 works around a chrome bug https://code.google.com/p/chromium/issues/detail?id=286360
      var css = (animationKeyframes ? animationKeyframes : '') + '.resize-triggers { ' + (animationStyle ? animationStyle : '') + 'visibility: hidden; opacity: 0; } ' + '.resize-triggers, .resize-triggers > div, .contract-trigger:before { content: " "; display: block; position: absolute; top: 0; left: 0; height: 100%; width: 100%; overflow: hidden; z-index: -1; } .resize-triggers > div { background: #eee; overflow: auto; } .contract-trigger:before { width: 200%; height: 200%; }',
        head = doc.head || doc.getElementsByTagName('head')[0],
        style = doc.createElement('style');
      style.id = 'detectElementResize';
      style.type = 'text/css';
      if (nonce != null) {
        style.setAttribute('nonce', nonce);
      }
      if (style.styleSheet) {
        style.styleSheet.cssText = css;
      } else {
        style.appendChild(doc.createTextNode(css));
      }
      head.appendChild(style);
    }
  };
  var addResizeListener = function addResizeListener(element, fn) {
    if (attachEvent) {
      element.attachEvent('onresize', fn);
    } else {
      if (!element.__resizeTriggers__) {
        var doc = element.ownerDocument;
        var elementStyle = _window.getComputedStyle(element);
        if (elementStyle && elementStyle.position == 'static') {
          element.style.position = 'relative';
        }
        createStyles(doc);
        element.__resizeLast__ = {};
        element.__resizeListeners__ = [];
        (element.__resizeTriggers__ = doc.createElement('div')).className = 'resize-triggers';
        element.__resizeTriggers__.innerHTML = '<div class="expand-trigger"><div></div></div>' + '<div class="contract-trigger"></div>';
        element.appendChild(element.__resizeTriggers__);
        resetTriggers(element);
        element.addEventListener('scroll', scrollListener, true);

        /* Listen for a css animation to detect element display/re-attach */
        if (animationstartevent) {
          element.__resizeTriggers__.__animationListener__ = function animationListener(e) {
            if (e.animationName == animationName) {
              resetTriggers(element);
            }
          };
          element.__resizeTriggers__.addEventListener(animationstartevent, element.__resizeTriggers__.__animationListener__);
        }
      }
      element.__resizeListeners__.push(fn);
    }
  };
  var removeResizeListener = function removeResizeListener(element, fn) {
    if (attachEvent) {
      element.detachEvent('onresize', fn);
    } else {
      element.__resizeListeners__.splice(element.__resizeListeners__.indexOf(fn), 1);
      if (!element.__resizeListeners__.length) {
        element.removeEventListener('scroll', scrollListener, true);
        if (element.__resizeTriggers__.__animationListener__) {
          element.__resizeTriggers__.removeEventListener(animationstartevent, element.__resizeTriggers__.__animationListener__);
          element.__resizeTriggers__.__animationListener__ = null;
        }
        try {
          element.__resizeTriggers__ = !element.removeChild(element.__resizeTriggers__);
        } catch (e) {
          // Preact compat; see developit/preact-compat/issues/228
        }
      }
    }
  };
  return {
    addResizeListener,
    removeResizeListener
  };
}
class AutoSizer extends React.PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      height: this.props.defaultHeight || 0,
      width: this.props.defaultWidth || 0
    };
    this._onResize = () => {
      const {
        disableHeight,
        disableWidth,
        onResize
      } = this.props;
      if (this._parentNode) {
        // Guard against AutoSizer component being removed from the DOM immediately after being added.
        // This can result in invalid style values which can result in NaN values if we don't handle them.
        // See issue #150 for more context.

        const height = this._parentNode.offsetHeight || 0;
        const width = this._parentNode.offsetWidth || 0;
        const style = window.getComputedStyle(this._parentNode) || {};
        const paddingLeft = parseInt(style.paddingLeft, 10) || 0;
        const paddingRight = parseInt(style.paddingRight, 10) || 0;
        const paddingTop = parseInt(style.paddingTop, 10) || 0;
        const paddingBottom = parseInt(style.paddingBottom, 10) || 0;
        const newHeight = height - paddingTop - paddingBottom;
        const newWidth = width - paddingLeft - paddingRight;
        if (!disableHeight && this.state.height !== newHeight || !disableWidth && this.state.width !== newWidth) {
          this.setState({
            height: height - paddingTop - paddingBottom,
            width: width - paddingLeft - paddingRight
          });
          onResize({
            height,
            width
          });
        }
      }
    };
    this._setRef = autoSizer => {
      this._autoSizer = autoSizer;
    };
  }
  componentDidMount() {
    const {
      nonce
    } = this.props;
    if (this._autoSizer && this._autoSizer.parentNode && this._autoSizer.parentNode.ownerDocument && this._autoSizer.parentNode.ownerDocument.defaultView && this._autoSizer.parentNode instanceof this._autoSizer.parentNode.ownerDocument.defaultView.HTMLElement) {
      // Delay access of parentNode until mount.
      // This handles edge-cases where the component has already been unmounted before its ref has been set,
      // As well as libraries like react-lite which have a slightly different lifecycle.
      this._parentNode = this._autoSizer.parentNode;

      // Defer requiring resize handler in order to support server-side rendering.
      // See issue #41
      this._detectElementResize = createDetectElementResize(nonce);
      this._detectElementResize.addResizeListener(this._parentNode, this._onResize);
      this._onResize();
    }
  }
  componentWillUnmount() {
    if (this._detectElementResize && this._parentNode) {
      this._detectElementResize.removeResizeListener(this._parentNode, this._onResize);
    }
  }
  render() {
    const {
      children,
      className,
      disableHeight,
      disableWidth,
      style
    } = this.props;
    const {
      height,
      width
    } = this.state;

    // Outer div should not force width/height since that may prevent containers from shrinking.
    // Inner component should overflow and use calculated width/height.
    // See issue #68 for more information.
    const outerStyle = {
      overflow: 'visible'
    };
    const childParams = {};
    if (!disableHeight) {
      outerStyle.height = 0;
      childParams.height = height;
    }
    if (!disableWidth) {
      outerStyle.width = 0;
      childParams.width = width;
    }

    /**
     * TODO: Avoid rendering children before the initial measurements have been collected.
     * At best this would just be wasting cycles.
     * Add this check into version 10 though as it could break too many ref callbacks in version 9.
     * Note that if default width/height props were provided this would still work with SSR.
    if (
      height !== 0 &&
      width !== 0
    ) {
      child = children({ height, width })
    }
    */

    return /*#__PURE__*/React.createElement("div", {
      className: className,
      ref: this._setRef,
      style: Object.assign({}, outerStyle, style)
    }, children(childParams));
  }
}
AutoSizer.defaultProps = {
  onResize: () => {},
  disableHeight: false,
  disableWidth: false,
  style: {}
};
const DEFAULT_HEIGHT = 30;
const DEFAULT_WIDTH = 100;

// Enables more intelligent mapping of a given column and row index to an item ID.
// This prevents a cell cache from being invalidated when its parent collection is modified.

/**
 * Caches measurements for a given cell.
 */
class CellMeasurerCache {
  constructor(params = {}) {
    this._cellHeightCache = {};
    this._cellWidthCache = {};
    this._columnWidthCache = {};
    this._rowHeightCache = {};
    this._columnCount = 0;
    this._rowCount = 0;
    this.columnWidth = ({
      index
    }) => {
      const key = this._keyMapper(0, index);
      return this._columnWidthCache.hasOwnProperty(key) ? this._columnWidthCache[key] : this._defaultWidth;
    };
    this.rowHeight = ({
      index
    }) => {
      const key = this._keyMapper(index, 0);
      return this._rowHeightCache.hasOwnProperty(key) ? this._rowHeightCache[key] : this._defaultHeight;
    };
    const {
      defaultHeight,
      defaultWidth,
      fixedHeight,
      fixedWidth,
      keyMapper,
      minHeight,
      minWidth
    } = params;
    this._hasFixedHeight = fixedHeight === true;
    this._hasFixedWidth = fixedWidth === true;
    this._minHeight = minHeight || 0;
    this._minWidth = minWidth || 0;
    this._keyMapper = keyMapper || defaultKeyMapper;
    this._defaultHeight = Math.max(this._minHeight, typeof defaultHeight === 'number' ? defaultHeight : DEFAULT_HEIGHT);
    this._defaultWidth = Math.max(this._minWidth, typeof defaultWidth === 'number' ? defaultWidth : DEFAULT_WIDTH);
    if (process.env.NODE_ENV !== 'production') {
      if (this._hasFixedHeight === false && this._hasFixedWidth === false) {
        console.warn("CellMeasurerCache should only measure a cell's width or height. " + 'You have configured CellMeasurerCache to measure both. ' + 'This will result in poor performance.');
      }
      if (this._hasFixedHeight === false && this._defaultHeight === 0) {
        console.warn('Fixed height CellMeasurerCache should specify a :defaultHeight greater than 0. ' + 'Failing to do so will lead to unnecessary layout and poor performance.');
      }
      if (this._hasFixedWidth === false && this._defaultWidth === 0) {
        console.warn('Fixed width CellMeasurerCache should specify a :defaultWidth greater than 0. ' + 'Failing to do so will lead to unnecessary layout and poor performance.');
      }
    }
  }
  clear(rowIndex, columnIndex = 0) {
    const key = this._keyMapper(rowIndex, columnIndex);
    delete this._cellHeightCache[key];
    delete this._cellWidthCache[key];
    this._updateCachedColumnAndRowSizes(rowIndex, columnIndex);
  }
  clearAll() {
    this._cellHeightCache = {};
    this._cellWidthCache = {};
    this._columnWidthCache = {};
    this._rowHeightCache = {};
    this._rowCount = 0;
    this._columnCount = 0;
  }
  get defaultHeight() {
    return this._defaultHeight;
  }
  get defaultWidth() {
    return this._defaultWidth;
  }
  hasFixedHeight() {
    return this._hasFixedHeight;
  }
  hasFixedWidth() {
    return this._hasFixedWidth;
  }
  getHeight(rowIndex, columnIndex = 0) {
    if (this._hasFixedHeight) {
      return this._defaultHeight;
    } else {
      const key = this._keyMapper(rowIndex, columnIndex);
      return this._cellHeightCache.hasOwnProperty(key) ? Math.max(this._minHeight, this._cellHeightCache[key]) : this._defaultHeight;
    }
  }
  getWidth(rowIndex, columnIndex = 0) {
    if (this._hasFixedWidth) {
      return this._defaultWidth;
    } else {
      const key = this._keyMapper(rowIndex, columnIndex);
      return this._cellWidthCache.hasOwnProperty(key) ? Math.max(this._minWidth, this._cellWidthCache[key]) : this._defaultWidth;
    }
  }
  has(rowIndex, columnIndex = 0) {
    const key = this._keyMapper(rowIndex, columnIndex);
    return this._cellHeightCache.hasOwnProperty(key);
  }
  set(rowIndex, columnIndex, width, height) {
    const key = this._keyMapper(rowIndex, columnIndex);
    if (columnIndex >= this._columnCount) {
      this._columnCount = columnIndex + 1;
    }
    if (rowIndex >= this._rowCount) {
      this._rowCount = rowIndex + 1;
    }

    // Size is cached per cell so we don't have to re-measure if cells are re-ordered.
    this._cellHeightCache[key] = height;
    this._cellWidthCache[key] = width;
    this._updateCachedColumnAndRowSizes(rowIndex, columnIndex);
  }
  _updateCachedColumnAndRowSizes(rowIndex, columnIndex) {
    // :columnWidth and :rowHeight are derived based on all cells in a column/row.
    // Pre-cache these derived values for faster lookup later.
    // Reads are expected to occur more frequently than writes in this case.
    // Only update non-fixed dimensions though to avoid doing unnecessary work.
    if (!this._hasFixedWidth) {
      let columnWidth = 0;
      for (let i = 0; i < this._rowCount; i++) {
        columnWidth = Math.max(columnWidth, this.getWidth(i, columnIndex));
      }
      const columnKey = this._keyMapper(0, columnIndex);
      this._columnWidthCache[columnKey] = columnWidth;
    }
    if (!this._hasFixedHeight) {
      let rowHeight = 0;
      for (let i = 0; i < this._columnCount; i++) {
        rowHeight = Math.max(rowHeight, this.getHeight(rowIndex, i));
      }
      const rowKey = this._keyMapper(rowIndex, 0);
      this._rowHeightCache[rowKey] = rowHeight;
    }
  }
}
function defaultKeyMapper(rowIndex, columnIndex) {
  return `${rowIndex}-${columnIndex}`;
}

/**
 * Wraps a cell and measures its rendered content.
 * Measurements are stored in a per-cell cache.
 * Cached-content is not be re-measured.
 */
class CellMeasurer extends React.PureComponent {
  constructor(...args) {
    super(...args);
    this._measure = () => {
      const {
        cache,
        columnIndex = 0,
        parent,
        rowIndex = this.props.index || 0
      } = this.props;
      const {
        height,
        width
      } = this._getCellMeasurements();
      if (height !== cache.getHeight(rowIndex, columnIndex) || width !== cache.getWidth(rowIndex, columnIndex)) {
        cache.set(rowIndex, columnIndex, width, height);
        if (parent && typeof parent.recomputeGridSize === 'function') {
          parent.recomputeGridSize({
            columnIndex,
            rowIndex
          });
        }
      }
    };
    this._registerChild = element => {
      if (element && !(element instanceof Element)) {
        console.warn('CellMeasurer registerChild expects to be passed Element or null');
      }
      this._child = element;
      if (element) {
        this._maybeMeasureCell();
      }
    };
  }
  componentDidMount() {
    this._maybeMeasureCell();
  }
  componentDidUpdate() {
    this._maybeMeasureCell();
  }
  render() {
    const {
      children
    } = this.props;
    return typeof children === 'function' ? children({
      measure: this._measure,
      registerChild: this._registerChild
    }) : children;
  }
  _getCellMeasurements() {
    const {
      cache
    } = this.props;
    const node = this._child || findDOMNode(this);

    // TODO Check for a bad combination of fixedWidth and missing numeric width or vice versa with height

    if (node && node.ownerDocument && node.ownerDocument.defaultView && node instanceof node.ownerDocument.defaultView.HTMLElement) {
      const styleWidth = node.style.width;
      const styleHeight = node.style.height;

      // If we are re-measuring a cell that has already been measured,
      // It will have a hard-coded width/height from the previous measurement.
      // The fact that we are measuring indicates this measurement is probably stale,
      // So explicitly clear it out (eg set to "auto") so we can recalculate.
      // See issue #593 for more info.
      // Even if we are measuring initially- if we're inside of a MultiGrid component,
      // Explicitly clear width/height before measuring to avoid being tainted by another Grid.
      // eg top/left Grid renders before bottom/right Grid
      // Since the CellMeasurerCache is shared between them this taints derived cell size values.
      if (!cache.hasFixedWidth()) {
        node.style.width = 'auto';
      }
      if (!cache.hasFixedHeight()) {
        node.style.height = 'auto';
      }
      const height = Math.ceil(node.offsetHeight);
      const width = Math.ceil(node.offsetWidth);

      // Reset after measuring to avoid breaking styles; see #660
      if (styleWidth) {
        node.style.width = styleWidth;
      }
      if (styleHeight) {
        node.style.height = styleHeight;
      }
      return {
        height,
        width
      };
    } else {
      return {
        height: 0,
        width: 0
      };
    }
  }
  _maybeMeasureCell() {
    const {
      cache,
      columnIndex = 0,
      parent,
      rowIndex = this.props.index || 0
    } = this.props;
    if (!cache.has(rowIndex, columnIndex)) {
      const {
        height,
        width
      } = this._getCellMeasurements();
      cache.set(rowIndex, columnIndex, width, height);

      // If size has changed, let Grid know to re-render.
      if (parent && typeof parent.invalidateCellSizeAfterRender === 'function') {
        parent.invalidateCellSizeAfterRender({
          columnIndex,
          rowIndex
        });
      }
    }
  }
}

// Used for DEV mode warning check
CellMeasurer.__internalCellMeasurerFlag = false;
if (process.env.NODE_ENV !== 'production') {
  CellMeasurer.__internalCellMeasurerFlag = true;
}

/**
 * Helper utility that updates the specified callback whenever any of the specified indices have changed.
 */
function createCallbackMemoizer(requireAllKeys = true) {
  let cachedIndices = {};
  return ({
    callback,
    indices
  }) => {
    const keys = Object.keys(indices);
    const allInitialized = !requireAllKeys || keys.every(key => {
      const value = indices[key];
      return Array.isArray(value) ? value.length > 0 : value >= 0;
    });
    const indexChanged = keys.length !== Object.keys(cachedIndices).length || keys.some(key => {
      const cachedValue = cachedIndices[key];
      const value = indices[key];
      return Array.isArray(value) ? cachedValue.join(',') !== value.join(',') : cachedValue !== value;
    });
    cachedIndices = indices;
    if (allInitialized && indexChanged) {
      callback(indices);
    }
  };
}
function getDefaultExportFromCjs(x) {
  return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}
function createCommonjsModule(fn) {
  var module = {
    exports: {}
  };
  return fn(module, module.exports), module.exports;
}
var inDOM = createCommonjsModule(function (module, exports) {
  Object.defineProperty(exports, "__esModule", {
    value: true
  });
  exports.default = !!(typeof window !== 'undefined' && window.document && window.document.createElement);
  module.exports = exports['default'];
});
var scrollbarSize = createCommonjsModule(function (module, exports) {
  Object.defineProperty(exports, "__esModule", {
    value: true
  });
  exports.default = function (recalc) {
    if (!size || recalc) {
      if (_inDOM2.default) {
        var scrollDiv = document.createElement('div');
        scrollDiv.style.position = 'absolute';
        scrollDiv.style.top = '-9999px';
        scrollDiv.style.width = '50px';
        scrollDiv.style.height = '50px';
        scrollDiv.style.overflow = 'scroll';
        document.body.appendChild(scrollDiv);
        size = scrollDiv.offsetWidth - scrollDiv.clientWidth;
        document.body.removeChild(scrollDiv);
      }
    }
    return size;
  };
  var _inDOM2 = _interopRequireDefault(inDOM);
  function _interopRequireDefault(obj) {
    return obj && obj.__esModule ? obj : {
      default: obj
    };
  }
  var size = void 0;
  module.exports = exports['default'];
});
var scrollbarSize$1 = /*@__PURE__*/getDefaultExportFromCjs(scrollbarSize);

// @TODO Merge Collection and CollectionView

/**
 * Specifies the number of milliseconds during which to disable pointer events while a scroll is in progress.
 * This improves performance and makes scrolling smoother.
 */
const IS_SCROLLING_TIMEOUT$1 = 150;

/**
 * Controls whether the Grid updates the DOM element's scrollLeft/scrollTop based on the current state or just observes it.
 * This prevents Grid from interrupting mouse-wheel animations (see issue #2).
 */
const SCROLL_POSITION_CHANGE_REASONS$1 = {
  OBSERVED: 'observed',
  REQUESTED: 'requested'
};

/**
 * Monitors changes in properties (eg. cellCount) and state (eg. scroll offsets) to determine when rendering needs to occur.
 * This component does not render any visible content itself; it defers to the specified :cellLayoutManager.
 */
class CollectionView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      isScrolling: false,
      scrollLeft: 0,
      scrollTop: 0
    };
    this._calculateSizeAndPositionDataOnNextUpdate = false;

    // Invokes callbacks only when their values have changed.
    this._onSectionRenderedMemoizer = createCallbackMemoizer();
    this._onScrollMemoizer = createCallbackMemoizer(false);

    // Bind functions to instance so they don't lose context when passed around.
    this._invokeOnSectionRenderedHelper = this._invokeOnSectionRenderedHelper.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._setScrollingContainerRef = this._setScrollingContainerRef.bind(this);
    this._updateScrollPositionForScrollToCell = this._updateScrollPositionForScrollToCell.bind(this);
  }

  /**
   * Forced recompute of cell sizes and positions.
   * This function should be called if cell sizes have changed but nothing else has.
   * Since cell positions are calculated by callbacks, the collection view has no way of detecting when the underlying data has changed.
   */
  recomputeCellSizesAndPositions() {
    this._calculateSizeAndPositionDataOnNextUpdate = true;
    this.forceUpdate();
  }

  /* ---------------------------- Component lifecycle methods ---------------------------- */

  componentDidMount() {
    const {
      cellLayoutManager,
      scrollLeft,
      scrollToCell,
      scrollTop
    } = this.props;

    // If this component was first rendered server-side, scrollbar size will be undefined.
    // In that event we need to remeasure.
    if (!this._scrollbarSizeMeasured) {
      this._scrollbarSize = scrollbarSize$1();
      this._scrollbarSizeMeasured = true;
      this.setState({});
    }
    if (scrollToCell >= 0) {
      this._updateScrollPositionForScrollToCell();
    } else if (scrollLeft >= 0 || scrollTop >= 0) {
      this._setScrollPosition({
        scrollLeft,
        scrollTop
      });
    }

    // Update onSectionRendered callback.
    this._invokeOnSectionRenderedHelper();
    const {
      height: totalHeight,
      width: totalWidth
    } = cellLayoutManager.getTotalSize();

    // Initialize onScroll callback.
    this._invokeOnScrollMemoizer({
      scrollLeft: scrollLeft || 0,
      scrollTop: scrollTop || 0,
      totalHeight,
      totalWidth
    });
  }
  componentDidUpdate(prevProps, prevState) {
    const {
      height,
      scrollToAlignment,
      scrollToCell,
      width
    } = this.props;
    const {
      scrollLeft,
      scrollPositionChangeReason,
      scrollTop
    } = this.state;

    // Make sure requested changes to :scrollLeft or :scrollTop get applied.
    // Assigning to scrollLeft/scrollTop tells the browser to interrupt any running scroll animations,
    // And to discard any pending async changes to the scroll position that may have happened in the meantime (e.g. on a separate scrolling thread).
    // So we only set these when we require an adjustment of the scroll position.
    // See issue #2 for more information.
    if (scrollPositionChangeReason === SCROLL_POSITION_CHANGE_REASONS$1.REQUESTED) {
      if (scrollLeft >= 0 && scrollLeft !== prevState.scrollLeft && scrollLeft !== this._scrollingContainer.scrollLeft) {
        this._scrollingContainer.scrollLeft = scrollLeft;
      }
      if (scrollTop >= 0 && scrollTop !== prevState.scrollTop && scrollTop !== this._scrollingContainer.scrollTop) {
        this._scrollingContainer.scrollTop = scrollTop;
      }
    }

    // Update scroll offsets if the current :scrollToCell values requires it
    if (height !== prevProps.height || scrollToAlignment !== prevProps.scrollToAlignment || scrollToCell !== prevProps.scrollToCell || width !== prevProps.width) {
      this._updateScrollPositionForScrollToCell();
    }

    // Update onRowsRendered callback if start/stop indices have changed
    this._invokeOnSectionRenderedHelper();
  }
  UNSAFE_componentWillMount() {
    const {
      cellLayoutManager
    } = this.props;
    cellLayoutManager.calculateSizeAndPositionData();

    // If this component is being rendered server-side, getScrollbarSize() will return undefined.
    // We handle this case in componentDidMount()
    this._scrollbarSize = scrollbarSize$1();
    if (this._scrollbarSize === undefined) {
      this._scrollbarSizeMeasured = false;
      this._scrollbarSize = 0;
    } else {
      this._scrollbarSizeMeasured = true;
    }
  }
  componentWillUnmount() {
    if (this._disablePointerEventsTimeoutId) {
      clearTimeout(this._disablePointerEventsTimeoutId);
    }
  }

  /**
   * @private
   * This method updates scrollLeft/scrollTop in state for the following conditions:
   * 1) Empty content (0 rows or columns)
   * 2) New scroll props overriding the current state
   * 3) Cells-count or cells-size has changed, making previous scroll offsets invalid
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      scrollLeft,
      scrollTop
    } = this.state;
    if (nextProps.cellCount === 0 && (scrollLeft !== 0 || scrollTop !== 0)) {
      this._setScrollPosition({
        scrollLeft: 0,
        scrollTop: 0
      });
    } else if (nextProps.scrollLeft !== this.props.scrollLeft || nextProps.scrollTop !== this.props.scrollTop) {
      this._setScrollPosition({
        scrollLeft: nextProps.scrollLeft,
        scrollTop: nextProps.scrollTop
      });
    }
    if (nextProps.cellCount !== this.props.cellCount || nextProps.cellLayoutManager !== this.props.cellLayoutManager || this._calculateSizeAndPositionDataOnNextUpdate) {
      nextProps.cellLayoutManager.calculateSizeAndPositionData();
    }
    if (this._calculateSizeAndPositionDataOnNextUpdate) {
      this._calculateSizeAndPositionDataOnNextUpdate = false;
    }
  }
  render() {
    const {
      autoHeight,
      cellCount,
      cellLayoutManager,
      className,
      height,
      horizontalOverscanSize,
      id,
      noContentRenderer,
      style,
      verticalOverscanSize,
      width
    } = this.props;
    const {
      isScrolling,
      scrollLeft,
      scrollTop
    } = this.state;
    const {
      height: totalHeight,
      width: totalWidth
    } = cellLayoutManager.getTotalSize();

    // Safely expand the rendered area by the specified overscan amount
    const left = Math.max(0, scrollLeft - horizontalOverscanSize);
    const top = Math.max(0, scrollTop - verticalOverscanSize);
    const right = Math.min(totalWidth, scrollLeft + width + horizontalOverscanSize);
    const bottom = Math.min(totalHeight, scrollTop + height + verticalOverscanSize);
    const childrenToDisplay = height > 0 && width > 0 ? cellLayoutManager.cellRenderers({
      height: bottom - top,
      isScrolling,
      width: right - left,
      x: left,
      y: top
    }) : [];
    const collectionStyle = {
      boxSizing: 'border-box',
      direction: 'ltr',
      height: autoHeight ? 'auto' : height,
      position: 'relative',
      WebkitOverflowScrolling: 'touch',
      width,
      willChange: 'transform'
    };

    // Force browser to hide scrollbars when we know they aren't necessary.
    // Otherwise once scrollbars appear they may not disappear again.
    // For more info see issue #116
    const verticalScrollBarSize = totalHeight > height ? this._scrollbarSize : 0;
    const horizontalScrollBarSize = totalWidth > width ? this._scrollbarSize : 0;

    // Also explicitly init styles to 'auto' if scrollbars are required.
    // This works around an obscure edge case where external CSS styles have not yet been loaded,
    // But an initial scroll index of offset is set as an external prop.
    // Without this style, Grid would render the correct range of cells but would NOT update its internal offset.
    // This was originally reported via clauderic/react-infinite-calendar/issues/23
    collectionStyle.overflowX = totalWidth + verticalScrollBarSize <= width ? 'hidden' : 'auto';
    collectionStyle.overflowY = totalHeight + horizontalScrollBarSize <= height ? 'hidden' : 'auto';
    return /*#__PURE__*/React__default.createElement("div", {
      ref: this._setScrollingContainerRef,
      "aria-label": this.props['aria-label'],
      className: cn('ReactVirtualized__Collection', className),
      id: id,
      onScroll: this._onScroll,
      role: "grid",
      style: Object.assign({}, collectionStyle, style),
      tabIndex: 0
    }, cellCount > 0 && /*#__PURE__*/React__default.createElement("div", {
      className: "ReactVirtualized__Collection__innerScrollContainer",
      style: {
        height: totalHeight,
        maxHeight: totalHeight,
        maxWidth: totalWidth,
        overflow: 'hidden',
        pointerEvents: isScrolling ? 'none' : '',
        width: totalWidth
      }
    }, childrenToDisplay), cellCount === 0 && noContentRenderer());
  }

  /* ---------------------------- Helper methods ---------------------------- */

  /**
   * Sets an :isScrolling flag for a small window of time.
   * This flag is used to disable pointer events on the scrollable portion of the Collection.
   * This prevents jerky/stuttery mouse-wheel scrolling.
   */
  _enablePointerEventsAfterDelay() {
    if (this._disablePointerEventsTimeoutId) {
      clearTimeout(this._disablePointerEventsTimeoutId);
    }
    this._disablePointerEventsTimeoutId = setTimeout(() => {
      const {
        isScrollingChange
      } = this.props;
      isScrollingChange(false);
      this._disablePointerEventsTimeoutId = null;
      this.setState({
        isScrolling: false
      });
    }, IS_SCROLLING_TIMEOUT$1);
  }
  _invokeOnSectionRenderedHelper() {
    const {
      cellLayoutManager,
      onSectionRendered
    } = this.props;
    this._onSectionRenderedMemoizer({
      callback: onSectionRendered,
      indices: {
        indices: cellLayoutManager.getLastRenderedIndices()
      }
    });
  }
  _invokeOnScrollMemoizer({
    scrollLeft,
    scrollTop,
    totalHeight,
    totalWidth
  }) {
    this._onScrollMemoizer({
      callback: ({
        scrollLeft,
        scrollTop
      }) => {
        const {
          height,
          onScroll,
          width
        } = this.props;
        onScroll({
          clientHeight: height,
          clientWidth: width,
          scrollHeight: totalHeight,
          scrollLeft,
          scrollTop,
          scrollWidth: totalWidth
        });
      },
      indices: {
        scrollLeft,
        scrollTop
      }
    });
  }
  _setScrollingContainerRef(ref) {
    this._scrollingContainer = ref;
  }
  _setScrollPosition({
    scrollLeft,
    scrollTop
  }) {
    const newState = {
      scrollPositionChangeReason: SCROLL_POSITION_CHANGE_REASONS$1.REQUESTED
    };
    if (scrollLeft >= 0) {
      newState.scrollLeft = scrollLeft;
    }
    if (scrollTop >= 0) {
      newState.scrollTop = scrollTop;
    }
    if (scrollLeft >= 0 && scrollLeft !== this.state.scrollLeft || scrollTop >= 0 && scrollTop !== this.state.scrollTop) {
      this.setState(newState);
    }
  }
  _updateScrollPositionForScrollToCell() {
    const {
      cellLayoutManager,
      height,
      scrollToAlignment,
      scrollToCell,
      width
    } = this.props;
    const {
      scrollLeft,
      scrollTop
    } = this.state;
    if (scrollToCell >= 0) {
      const scrollPosition = cellLayoutManager.getScrollPositionForCell({
        align: scrollToAlignment,
        cellIndex: scrollToCell,
        height,
        scrollLeft,
        scrollTop,
        width
      });
      if (scrollPosition.scrollLeft !== scrollLeft || scrollPosition.scrollTop !== scrollTop) {
        this._setScrollPosition(scrollPosition);
      }
    }
  }
  _onScroll(event) {
    // In certain edge-cases React dispatches an onScroll event with an invalid target.scrollLeft / target.scrollTop.
    // This invalid event can be detected by comparing event.target to this component's scrollable DOM element.
    // See issue #404 for more information.
    if (event.target !== this._scrollingContainer) {
      return;
    }

    // Prevent pointer events from interrupting a smooth scroll
    this._enablePointerEventsAfterDelay();

    // When this component is shrunk drastically, React dispatches a series of back-to-back scroll events,
    // Gradually converging on a scrollTop that is within the bounds of the new, smaller height.
    // This causes a series of rapid renders that is slow for long lists.
    // We can avoid that by doing some simple bounds checking to ensure that scrollTop never exceeds the total height.
    const {
      cellLayoutManager,
      height,
      isScrollingChange,
      width
    } = this.props;
    const scrollbarSize = this._scrollbarSize;
    const {
      height: totalHeight,
      width: totalWidth
    } = cellLayoutManager.getTotalSize();
    const scrollLeft = Math.max(0, Math.min(totalWidth - width + scrollbarSize, event.target.scrollLeft));
    const scrollTop = Math.max(0, Math.min(totalHeight - height + scrollbarSize, event.target.scrollTop));

    // Certain devices (like Apple touchpad) rapid-fire duplicate events.
    // Don't force a re-render if this is the case.
    // The mouse may move faster then the animation frame does.
    // Use requestAnimationFrame to avoid over-updating.
    if (this.state.scrollLeft !== scrollLeft || this.state.scrollTop !== scrollTop) {
      // Browsers with cancelable scroll events (eg. Firefox) interrupt scrolling animations if scrollTop/scrollLeft is set.
      // Other browsers (eg. Safari) don't scroll as well without the help under certain conditions (DOM or style changes during scrolling).
      // All things considered, this seems to be the best current work around that I'm aware of.
      // For more information see https://github.com/bvaughn/react-virtualized/pull/124
      const scrollPositionChangeReason = event.cancelable ? SCROLL_POSITION_CHANGE_REASONS$1.OBSERVED : SCROLL_POSITION_CHANGE_REASONS$1.REQUESTED;

      // Synchronously set :isScrolling the first time (since _setNextState will reschedule its animation frame each time it's called)
      if (!this.state.isScrolling) {
        isScrollingChange(true);
      }
      this.setState({
        isScrolling: true,
        scrollLeft,
        scrollPositionChangeReason,
        scrollTop
      });
    }
    this._invokeOnScrollMemoizer({
      scrollLeft,
      scrollTop,
      totalWidth,
      totalHeight
    });
  }
}
CollectionView.defaultProps = {
  'aria-label': 'grid',
  horizontalOverscanSize: 0,
  noContentRenderer: () => null,
  onScroll: () => null,
  onSectionRendered: () => null,
  scrollToAlignment: 'auto',
  scrollToCell: -1,
  style: {},
  verticalOverscanSize: 0
};
CollectionView.propTypes = process.env.NODE_ENV !== "production" ? {
  'aria-label': PropTypes.string,
  /**
   * Removes fixed height from the scrollingContainer so that the total height
   * of rows can stretch the window. Intended for use with WindowScroller
   */
  autoHeight: PropTypes.bool,
  /**
   * Number of cells in collection.
   */
  cellCount: PropTypes.number.isRequired,
  /**
   * Calculates cell sizes and positions and manages rendering the appropriate cells given a specified window.
   */
  cellLayoutManager: PropTypes.object.isRequired,
  /**
   * Optional custom CSS class name to attach to root Collection element.
   */
  className: PropTypes.string,
  /**
   * Height of Collection; this property determines the number of visible (vs virtualized) rows.
   */
  height: PropTypes.number.isRequired,
  /**
   * Optional custom id to attach to root Collection element.
   */
  id: PropTypes.string,
  /**
   * Enables the `Collection` to horiontally "overscan" its content similar to how `Grid` does.
   * This can reduce flicker around the edges when a user scrolls quickly.
   */
  horizontalOverscanSize: PropTypes.number.isRequired,
  isScrollingChange: PropTypes.func,
  /**
   * Optional renderer to be used in place of rows when either :rowCount or :cellCount is 0.
   */
  noContentRenderer: PropTypes.func.isRequired,
  /**
   * Callback invoked whenever the scroll offset changes within the inner scrollable region.
   * This callback can be used to sync scrolling between lists, tables, or grids.
   * ({ clientHeight, clientWidth, scrollHeight, scrollLeft, scrollTop, scrollWidth }): void
   */
  onScroll: PropTypes.func.isRequired,
  /**
   * Callback invoked with information about the section of the Collection that was just rendered.
   * This callback is passed a named :indices parameter which is an Array of the most recently rendered section indices.
   */
  onSectionRendered: PropTypes.func.isRequired,
  /**
   * Horizontal offset.
   */
  scrollLeft: PropTypes.number,
  /**
   * Controls scroll-to-cell behavior of the Grid.
   * The default ("auto") scrolls the least amount possible to ensure that the specified cell is fully visible.
   * Use "start" to align cells to the top/left of the Grid and "end" to align bottom/right.
   */
  scrollToAlignment: PropTypes.oneOf(['auto', 'end', 'start', 'center']).isRequired,
  /**
   * Cell index to ensure visible (by forcefully scrolling if necessary).
   */
  scrollToCell: PropTypes.number.isRequired,
  /**
   * Vertical offset.
   */
  scrollTop: PropTypes.number,
  /**
   * Optional custom inline style to attach to root Collection element.
   */
  style: PropTypes.object,
  /**
   * Enables the `Collection` to vertically "overscan" its content similar to how `Grid` does.
   * This can reduce flicker around the edges when a user scrolls quickly.
   */
  verticalOverscanSize: PropTypes.number.isRequired,
  /**
   * Width of Collection; this property determines the number of visible (vs virtualized) columns.
   */
  width: PropTypes.number.isRequired
} : {};

/** @rlow */

/**
 * A section of the Window.
 * Window Sections are used to group nearby cells.
 * This enables us to more quickly determine which cells to display in a given region of the Window.
 * Sections have a fixed size and contain 0 to many cells (tracked by their indices).
 */
class Section {
  constructor({
    height,
    width,
    x,
    y
  }) {
    this.height = height;
    this.width = width;
    this.x = x;
    this.y = y;
    this._indexMap = {};
    this._indices = [];
  }

  /** Add a cell to this section. */
  addCellIndex({
    index
  }) {
    if (!this._indexMap[index]) {
      this._indexMap[index] = true;
      this._indices.push(index);
    }
  }

  /** Get all cell indices that have been added to this section. */
  getCellIndices() {
    return this._indices;
  }

  /** Intended for debugger/test purposes only */
  toString() {
    return `${this.x},${this.y} ${this.width}x${this.height}`;
  }
}

/**
 * Window Sections are used to group nearby cells.
 * This enables us to more quickly determine which cells to display in a given region of the Window.
 * 
 */
const SECTION_SIZE = 100;
/**
 * Contains 0 to many Sections.
 * Grows (and adds Sections) dynamically as cells are registered.
 * Automatically adds cells to the appropriate Section(s).
 */
class SectionManager {
  constructor(sectionSize = SECTION_SIZE) {
    this._sectionSize = sectionSize;
    this._cellMetadata = [];
    this._sections = {};
  }

  /**
   * Gets all cell indices contained in the specified region.
   * A region may encompass 1 or more Sections.
   */
  getCellIndices({
    height,
    width,
    x,
    y
  }) {
    const indices = {};
    this.getSections({
      height,
      width,
      x,
      y
    }).forEach(section => section.getCellIndices().forEach(index => {
      indices[index] = index;
    }));

    // Object keys are strings; this function returns numbers
    return Object.keys(indices).map(index => indices[index]);
  }

  /** Get size and position information for the cell specified. */
  getCellMetadata({
    index
  }) {
    return this._cellMetadata[index];
  }

  /** Get all Sections overlapping the specified region. */
  getSections({
    height,
    width,
    x,
    y
  }) {
    const sectionXStart = Math.floor(x / this._sectionSize);
    const sectionXStop = Math.floor((x + width - 1) / this._sectionSize);
    const sectionYStart = Math.floor(y / this._sectionSize);
    const sectionYStop = Math.floor((y + height - 1) / this._sectionSize);
    const sections = [];
    for (let sectionX = sectionXStart; sectionX <= sectionXStop; sectionX++) {
      for (let sectionY = sectionYStart; sectionY <= sectionYStop; sectionY++) {
        const key = `${sectionX}.${sectionY}`;
        if (!this._sections[key]) {
          this._sections[key] = new Section({
            height: this._sectionSize,
            width: this._sectionSize,
            x: sectionX * this._sectionSize,
            y: sectionY * this._sectionSize
          });
        }
        sections.push(this._sections[key]);
      }
    }
    return sections;
  }

  /** Total number of Sections based on the currently registered cells. */
  getTotalSectionCount() {
    return Object.keys(this._sections).length;
  }

  /** Intended for debugger/test purposes only */
  toString() {
    return Object.keys(this._sections).map(index => this._sections[index].toString());
  }

  /** Adds a cell to the appropriate Sections and registers it metadata for later retrievable. */
  registerCell({
    cellMetadatum,
    index
  }) {
    this._cellMetadata[index] = cellMetadatum;
    this.getSections(cellMetadatum).forEach(section => section.addCellIndex({
      index
    }));
  }
}
function calculateSizeAndPositionData({
  cellCount,
  cellSizeAndPositionGetter,
  sectionSize
}) {
  const cellMetadata = [];
  const sectionManager = new SectionManager(sectionSize);
  let height = 0;
  let width = 0;
  for (let index = 0; index < cellCount; index++) {
    const cellMetadatum = cellSizeAndPositionGetter({
      index
    });
    if (cellMetadatum.height == null || isNaN(cellMetadatum.height) || cellMetadatum.width == null || isNaN(cellMetadatum.width) || cellMetadatum.x == null || isNaN(cellMetadatum.x) || cellMetadatum.y == null || isNaN(cellMetadatum.y)) {
      throw Error(`Invalid metadata returned for cell ${index}:
        x:${cellMetadatum.x}, y:${cellMetadatum.y}, width:${cellMetadatum.width}, height:${cellMetadatum.height}`);
    }
    height = Math.max(height, cellMetadatum.y + cellMetadatum.height);
    width = Math.max(width, cellMetadatum.x + cellMetadatum.width);
    cellMetadata[index] = cellMetadatum;
    sectionManager.registerCell({
      cellMetadatum,
      index
    });
  }
  return {
    cellMetadata,
    height,
    sectionManager,
    width
  };
}

/**
 * Determines a new offset that ensures a certain cell is visible, given the current offset.
 * If the cell is already visible then the current offset will be returned.
 * If the current offset is too great or small, it will be adjusted just enough to ensure the specified index is visible.
 *
 * @param align Desired alignment within container; one of "auto" (default), "start", or "end"
 * @param cellOffset Offset (x or y) position for cell
 * @param cellSize Size (width or height) of cell
 * @param containerSize Total size (width or height) of the container
 * @param currentOffset Container's current (x or y) offset
 * @return Offset to use to ensure the specified cell is visible
 */
function getUpdatedOffsetForIndex({
  align = 'auto',
  cellOffset,
  cellSize,
  containerSize,
  currentOffset
}) {
  const maxOffset = cellOffset;
  const minOffset = maxOffset - containerSize + cellSize;
  switch (align) {
    case 'start':
      return maxOffset;
    case 'end':
      return minOffset;
    case 'center':
      return maxOffset - (containerSize - cellSize) / 2;
    default:
      return Math.max(minOffset, Math.min(maxOffset, currentOffset));
  }
}

/**
 * Renders scattered or non-linear data.
 * Unlike Grid, which renders checkerboard data, Collection can render arbitrarily positioned- even overlapping- data.
 */
class Collection extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this._cellMetadata = [];
    this._lastRenderedCellIndices = [];

    // Cell cache during scroll (for perforamnce)
    this._cellCache = [];
    this._isScrollingChange = this._isScrollingChange.bind(this);
    this._setCollectionViewRef = this._setCollectionViewRef.bind(this);
  }
  forceUpdate() {
    if (this._collectionView !== undefined) {
      this._collectionView.forceUpdate();
    }
  }

  /** See Collection#recomputeCellSizesAndPositions */
  recomputeCellSizesAndPositions() {
    this._cellCache = [];
    this._collectionView.recomputeCellSizesAndPositions();
  }

  /** React lifecycle methods */

  render() {
    const _this$props = this.props,
      props = Object.assign({}, (_objectDestructuringEmpty(_this$props), _this$props));
    return /*#__PURE__*/React__default.createElement(CollectionView, Object.assign({
      cellLayoutManager: this,
      isScrollingChange: this._isScrollingChange,
      ref: this._setCollectionViewRef
    }, props));
  }

  /** CellLayoutManager interface */

  calculateSizeAndPositionData() {
    const {
      cellCount,
      cellSizeAndPositionGetter,
      sectionSize
    } = this.props;
    const data = calculateSizeAndPositionData({
      cellCount,
      cellSizeAndPositionGetter,
      sectionSize
    });
    this._cellMetadata = data.cellMetadata;
    this._sectionManager = data.sectionManager;
    this._height = data.height;
    this._width = data.width;
  }

  /**
   * Returns the most recently rendered set of cell indices.
   */
  getLastRenderedIndices() {
    return this._lastRenderedCellIndices;
  }

  /**
   * Calculates the minimum amount of change from the current scroll position to ensure the specified cell is (fully) visible.
   */
  getScrollPositionForCell({
    align,
    cellIndex,
    height,
    scrollLeft,
    scrollTop,
    width
  }) {
    const {
      cellCount
    } = this.props;
    if (cellIndex >= 0 && cellIndex < cellCount) {
      const cellMetadata = this._cellMetadata[cellIndex];
      scrollLeft = getUpdatedOffsetForIndex({
        align,
        cellOffset: cellMetadata.x,
        cellSize: cellMetadata.width,
        containerSize: width,
        currentOffset: scrollLeft,
        targetIndex: cellIndex
      });
      scrollTop = getUpdatedOffsetForIndex({
        align,
        cellOffset: cellMetadata.y,
        cellSize: cellMetadata.height,
        containerSize: height,
        currentOffset: scrollTop,
        targetIndex: cellIndex
      });
    }
    return {
      scrollLeft,
      scrollTop
    };
  }
  getTotalSize() {
    return {
      height: this._height,
      width: this._width
    };
  }
  cellRenderers({
    height,
    isScrolling,
    width,
    x,
    y
  }) {
    const {
      cellGroupRenderer,
      cellRenderer
    } = this.props;

    // Store for later calls to getLastRenderedIndices()
    this._lastRenderedCellIndices = this._sectionManager.getCellIndices({
      height,
      width,
      x,
      y
    });
    return cellGroupRenderer({
      cellCache: this._cellCache,
      cellRenderer,
      cellSizeAndPositionGetter: ({
        index
      }) => this._sectionManager.getCellMetadata({
        index
      }),
      indices: this._lastRenderedCellIndices,
      isScrolling
    });
  }
  _isScrollingChange(isScrolling) {
    if (!isScrolling) {
      this._cellCache = [];
    }
  }
  _setCollectionViewRef(ref) {
    this._collectionView = ref;
  }
}
Collection.defaultProps = {
  'aria-label': 'grid',
  cellGroupRenderer: defaultCellGroupRenderer
};
Collection.propTypes = process.env.NODE_ENV !== "production" ? {
  'aria-label': PropTypes.string,
  /**
   * Number of cells in Collection.
   */
  cellCount: PropTypes.number.isRequired,
  /**
   * Responsible for rendering a group of cells given their indices.
   * Should implement the following interface: ({
   *   cellSizeAndPositionGetter:Function,
   *   indices: Array<number>,
   *   cellRenderer: Function
   * }): Array<PropTypes.node>
   */
  cellGroupRenderer: PropTypes.func.isRequired,
  /**
   * Responsible for rendering a cell given an row and column index.
   * Should implement the following interface: ({ index: number, key: string, style: object }): PropTypes.element
   */
  cellRenderer: PropTypes.func.isRequired,
  /**
   * Callback responsible for returning size and offset/position information for a given cell (index).
   * ({ index: number }): { height: number, width: number, x: number, y: number }
   */
  cellSizeAndPositionGetter: PropTypes.func.isRequired,
  /**
   * Optionally override the size of the sections a Collection's cells are split into.
   */
  sectionSize: PropTypes.number
} : {};
function defaultCellGroupRenderer({
  cellCache,
  cellRenderer,
  cellSizeAndPositionGetter,
  indices,
  isScrolling
}) {
  return indices.map(index => {
    const cellMetadata = cellSizeAndPositionGetter({
      index
    });
    let cellRendererProps = {
      index,
      isScrolling,
      key: index,
      style: {
        height: cellMetadata.height,
        left: cellMetadata.x,
        position: 'absolute',
        top: cellMetadata.y,
        width: cellMetadata.width
      }
    };

    // Avoid re-creating cells while scrolling.
    // This can lead to the same cell being created many times and can cause performance issues for "heavy" cells.
    // If a scroll is in progress- cache and reuse cells.
    // This cache will be thrown away once scrolling complets.
    if (isScrolling) {
      if (!(index in cellCache)) {
        cellCache[index] = cellRenderer(cellRendererProps);
      }
      return cellCache[index];
    } else {
      return cellRenderer(cellRendererProps);
    }
  }).filter(renderedCell => !!renderedCell);
}

/**
 * High-order component that auto-calculates column-widths for `Grid` cells.
 */
class ColumnSizer extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this._registerChild = this._registerChild.bind(this);
  }
  componentDidUpdate(prevProps) {
    const {
      columnMaxWidth,
      columnMinWidth,
      columnCount,
      width
    } = this.props;
    if (columnMaxWidth !== prevProps.columnMaxWidth || columnMinWidth !== prevProps.columnMinWidth || columnCount !== prevProps.columnCount || width !== prevProps.width) {
      if (this._registeredChild) {
        this._registeredChild.recomputeGridSize();
      }
    }
  }
  render() {
    const {
      children,
      columnMaxWidth,
      columnMinWidth,
      columnCount,
      width
    } = this.props;
    const safeColumnMinWidth = columnMinWidth || 1;
    const safeColumnMaxWidth = columnMaxWidth ? Math.min(columnMaxWidth, width) : width;
    let columnWidth = width / columnCount;
    columnWidth = Math.max(safeColumnMinWidth, columnWidth);
    columnWidth = Math.min(safeColumnMaxWidth, columnWidth);
    columnWidth = Math.floor(columnWidth);
    let adjustedWidth = Math.min(width, columnWidth * columnCount);
    return children({
      adjustedWidth,
      columnWidth,
      getColumnWidth: () => columnWidth,
      registerChild: this._registerChild
    });
  }
  _registerChild(child) {
    if (child && typeof child.recomputeGridSize !== 'function') {
      throw Error('Unexpected child type registered; only Grid/MultiGrid children are supported.');
    }
    this._registeredChild = child;
    if (this._registeredChild) {
      this._registeredChild.recomputeGridSize();
    }
  }
}
ColumnSizer.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Function responsible for rendering a virtualized Grid.
   * This function should implement the following signature:
   * ({ adjustedWidth, getColumnWidth, registerChild }) => PropTypes.element
   *
   * The specified :getColumnWidth function should be passed to the Grid's :columnWidth property.
   * The :registerChild should be passed to the Grid's :ref property.
   * The :adjustedWidth property is optional; it reflects the lesser of the overall width or the width of all columns.
   */
  children: PropTypes.func.isRequired,
  /** Optional maximum allowed column width */
  columnMaxWidth: PropTypes.number,
  /** Optional minimum allowed column width */
  columnMinWidth: PropTypes.number,
  /** Number of columns in Grid or Table child */
  columnCount: PropTypes.number.isRequired,
  /** Width of Grid or Table child */
  width: PropTypes.number.isRequired
} : {};

/**
 * Helper method that determines when to recalculate row or column metadata.
 */
function calculateSizeAndPositionDataAndUpdateScrollOffset({
  cellCount,
  cellSize,
  computeMetadataCallback,
  computeMetadataCallbackProps,
  nextCellsCount,
  nextCellSize,
  nextScrollToIndex,
  scrollToIndex,
  updateScrollOffsetForScrollToIndex
}) {
  // Don't compare cell sizes if they are functions because inline functions would cause infinite loops.
  // In that event users should use the manual recompute methods to inform of changes.
  if (cellCount !== nextCellsCount || (typeof cellSize === 'number' || typeof nextCellSize === 'number') && cellSize !== nextCellSize) {
    computeMetadataCallback(computeMetadataCallbackProps);

    // Updated cell metadata may have hidden the previous scrolled-to item.
    // In this case we should also update the scrollTop to ensure it stays visible.
    if (scrollToIndex >= 0 && scrollToIndex === nextScrollToIndex) {
      updateScrollOffsetForScrollToIndex();
    }
  }
}

/**
 * Just-in-time calculates and caches size and position information for a collection of cells.
 */
class CellSizeAndPositionManager {
  // Cache of size and position data for cells, mapped by cell index.
  // Note that invalid values may exist in this map so only rely on cells up to this._lastMeasuredIndex

  // Measurements for cells up to this index can be trusted; cells afterward should be estimated.

  // Used in deferred mode to track which cells have been queued for measurement.

  constructor({
    cellCount,
    cellSizeGetter,
    estimatedCellSize
  }) {
    this._cellSizeAndPositionData = {};
    this._lastMeasuredIndex = -1;
    this._lastBatchedIndex = -1;
    this._cellSizeGetter = cellSizeGetter;
    this._cellCount = cellCount;
    this._estimatedCellSize = estimatedCellSize;
  }
  areOffsetsAdjusted() {
    return false;
  }
  configure({
    cellCount,
    estimatedCellSize
  }) {
    this._cellCount = cellCount;
    this._estimatedCellSize = estimatedCellSize;
  }
  getCellCount() {
    return this._cellCount;
  }
  getEstimatedCellSize() {
    return this._estimatedCellSize;
  }
  getLastMeasuredIndex() {
    return this._lastMeasuredIndex;
  }
  getOffsetAdjustment() {
    return 0;
  }

  /**
   * This method returns the size and position for the cell at the specified index.
   * It just-in-time calculates (or used cached values) for cells leading up to the index.
   */
  getSizeAndPositionOfCell(index) {
    if (index < 0 || index >= this._cellCount) {
      throw Error(`Requested index ${index} is outside of range 0..${this._cellCount}`);
    }
    if (index > this._lastMeasuredIndex) {
      let lastMeasuredCellSizeAndPosition = this.getSizeAndPositionOfLastMeasuredCell();
      let offset = lastMeasuredCellSizeAndPosition.offset + lastMeasuredCellSizeAndPosition.size;
      for (var i = this._lastMeasuredIndex + 1; i <= index; i++) {
        let size = this._cellSizeGetter({
          index: i
        });

        // undefined or NaN probably means a logic error in the size getter.
        // null means we're using CellMeasurer and haven't yet measured a given index.
        if (size === undefined || isNaN(size)) {
          throw Error(`Invalid size returned for cell ${i} of value ${size}`);
        } else if (size === null) {
          this._cellSizeAndPositionData[i] = {
            offset,
            size: 0
          };
          this._lastBatchedIndex = index;
        } else {
          this._cellSizeAndPositionData[i] = {
            offset,
            size
          };
          offset += size;
          this._lastMeasuredIndex = index;
        }
      }
    }
    return this._cellSizeAndPositionData[index];
  }
  getSizeAndPositionOfLastMeasuredCell() {
    return this._lastMeasuredIndex >= 0 ? this._cellSizeAndPositionData[this._lastMeasuredIndex] : {
      offset: 0,
      size: 0
    };
  }

  /**
   * Total size of all cells being measured.
   * This value will be completely estimated initially.
   * As cells are measured, the estimate will be updated.
   */
  getTotalSize() {
    const lastMeasuredCellSizeAndPosition = this.getSizeAndPositionOfLastMeasuredCell();
    const totalSizeOfMeasuredCells = lastMeasuredCellSizeAndPosition.offset + lastMeasuredCellSizeAndPosition.size;
    const numUnmeasuredCells = this._cellCount - this._lastMeasuredIndex - 1;
    const totalSizeOfUnmeasuredCells = numUnmeasuredCells * this._estimatedCellSize;
    return totalSizeOfMeasuredCells + totalSizeOfUnmeasuredCells;
  }

  /**
   * Determines a new offset that ensures a certain cell is visible, given the current offset.
   * If the cell is already visible then the current offset will be returned.
   * If the current offset is too great or small, it will be adjusted just enough to ensure the specified index is visible.
   *
   * @param align Desired alignment within container; one of "auto" (default), "start", or "end"
   * @param containerSize Size (width or height) of the container viewport
   * @param currentOffset Container's current (x or y) offset
   * @param totalSize Total size (width or height) of all cells
   * @return Offset to use to ensure the specified cell is visible
   */
  getUpdatedOffsetForIndex({
    align = 'auto',
    containerSize,
    currentOffset,
    targetIndex
  }) {
    if (containerSize <= 0) {
      return 0;
    }
    const datum = this.getSizeAndPositionOfCell(targetIndex);
    const maxOffset = datum.offset;
    const minOffset = maxOffset - containerSize + datum.size;
    let idealOffset;
    switch (align) {
      case 'start':
        idealOffset = maxOffset;
        break;
      case 'end':
        idealOffset = minOffset;
        break;
      case 'center':
        idealOffset = maxOffset - (containerSize - datum.size) / 2;
        break;
      default:
        idealOffset = Math.max(minOffset, Math.min(maxOffset, currentOffset));
        break;
    }
    const totalSize = this.getTotalSize();
    return Math.max(0, Math.min(totalSize - containerSize, idealOffset));
  }
  getVisibleCellRange(params) {
    let {
      containerSize,
      offset
    } = params;
    const totalSize = this.getTotalSize();
    if (totalSize === 0) {
      return {};
    }
    const maxOffset = offset + containerSize;
    const start = this._findNearestCell(offset);
    const datum = this.getSizeAndPositionOfCell(start);
    offset = datum.offset + datum.size;
    let stop = start;
    while (offset < maxOffset && stop < this._cellCount - 1) {
      stop++;
      offset += this.getSizeAndPositionOfCell(stop).size;
    }
    return {
      start,
      stop
    };
  }

  /**
   * Clear all cached values for cells after the specified index.
   * This method should be called for any cell that has changed its size.
   * It will not immediately perform any calculations; they'll be performed the next time getSizeAndPositionOfCell() is called.
   */
  resetCell(index) {
    this._lastMeasuredIndex = Math.min(this._lastMeasuredIndex, index - 1);
  }
  _binarySearch(high, low, offset) {
    while (low <= high) {
      const middle = low + Math.floor((high - low) / 2);
      const currentOffset = this.getSizeAndPositionOfCell(middle).offset;
      if (currentOffset === offset) {
        return middle;
      } else if (currentOffset < offset) {
        low = middle + 1;
      } else if (currentOffset > offset) {
        high = middle - 1;
      }
    }
    if (low > 0) {
      return low - 1;
    } else {
      return 0;
    }
  }
  _exponentialSearch(index, offset) {
    let interval = 1;
    while (index < this._cellCount && this.getSizeAndPositionOfCell(index).offset < offset) {
      index += interval;
      interval *= 2;
    }
    return this._binarySearch(Math.min(index, this._cellCount - 1), Math.floor(index / 2), offset);
  }

  /**
   * Searches for the cell (index) nearest the specified offset.
   *
   * If no exact match is found the next lowest cell index will be returned.
   * This allows partially visible cells (with offsets just before/above the fold) to be visible.
   */
  _findNearestCell(offset) {
    if (isNaN(offset)) {
      throw Error(`Invalid offset ${offset} specified`);
    }

    // Our search algorithms find the nearest match at or below the specified offset.
    // So make sure the offset is at least 0 or no match will be found.
    offset = Math.max(0, offset);
    const lastMeasuredCellSizeAndPosition = this.getSizeAndPositionOfLastMeasuredCell();
    const lastMeasuredIndex = Math.max(0, this._lastMeasuredIndex);
    if (lastMeasuredCellSizeAndPosition.offset >= offset) {
      // If we've already measured cells within this range just use a binary search as it's faster.
      return this._binarySearch(lastMeasuredIndex, 0, offset);
    } else {
      // If we haven't yet measured this high, fallback to an exponential search with an inner binary search.
      // The exponential search avoids pre-computing sizes for the full set of cells as a binary search would.
      // The overall complexity for this approach is O(log n).
      return this._exponentialSearch(lastMeasuredIndex, offset);
    }
  }
}
const DEFAULT_MAX_ELEMENT_SIZE = 1500000;
const CHROME_MAX_ELEMENT_SIZE = 1.67771e7;
const isBrowser = () => typeof window !== 'undefined';
const isChrome = () => !!window.chrome && !!window.chrome.webstore;
const getMaxElementSize = () => {
  if (isBrowser()) {
    if (isChrome()) {
      return CHROME_MAX_ELEMENT_SIZE;
    }
  }
  return DEFAULT_MAX_ELEMENT_SIZE;
};

/**
 * Extends CellSizeAndPositionManager and adds scaling behavior for lists that are too large to fit within a browser's native limits.
 */
class ScalingCellSizeAndPositionManager {
  constructor(_ref) {
    let {
        maxScrollSize = getMaxElementSize()
      } = _ref,
      params = _objectWithoutPropertiesLoose(_ref, _excluded);
    // Favor composition over inheritance to simplify IE10 support
    this._cellSizeAndPositionManager = new CellSizeAndPositionManager(params);
    this._maxScrollSize = maxScrollSize;
  }
  areOffsetsAdjusted() {
    return this._cellSizeAndPositionManager.getTotalSize() > this._maxScrollSize;
  }
  configure(params) {
    this._cellSizeAndPositionManager.configure(params);
  }
  getCellCount() {
    return this._cellSizeAndPositionManager.getCellCount();
  }
  getEstimatedCellSize() {
    return this._cellSizeAndPositionManager.getEstimatedCellSize();
  }
  getLastMeasuredIndex() {
    return this._cellSizeAndPositionManager.getLastMeasuredIndex();
  }

  /**
   * Number of pixels a cell at the given position (offset) should be shifted in order to fit within the scaled container.
   * The offset passed to this function is scaled (safe) as well.
   */
  getOffsetAdjustment({
    containerSize,
    offset // safe
  }) {
    const totalSize = this._cellSizeAndPositionManager.getTotalSize();
    const safeTotalSize = this.getTotalSize();
    const offsetPercentage = this._getOffsetPercentage({
      containerSize,
      offset,
      totalSize: safeTotalSize
    });
    return Math.round(offsetPercentage * (safeTotalSize - totalSize));
  }
  getSizeAndPositionOfCell(index) {
    return this._cellSizeAndPositionManager.getSizeAndPositionOfCell(index);
  }
  getSizeAndPositionOfLastMeasuredCell() {
    return this._cellSizeAndPositionManager.getSizeAndPositionOfLastMeasuredCell();
  }

  /** See CellSizeAndPositionManager#getTotalSize */
  getTotalSize() {
    return Math.min(this._maxScrollSize, this._cellSizeAndPositionManager.getTotalSize());
  }

  /** See CellSizeAndPositionManager#getUpdatedOffsetForIndex */
  getUpdatedOffsetForIndex({
    align = 'auto',
    containerSize,
    currentOffset,
    // safe
    targetIndex
  }) {
    currentOffset = this._safeOffsetToOffset({
      containerSize,
      offset: currentOffset
    });
    const offset = this._cellSizeAndPositionManager.getUpdatedOffsetForIndex({
      align,
      containerSize,
      currentOffset,
      targetIndex
    });
    return this._offsetToSafeOffset({
      containerSize,
      offset
    });
  }

  /** See CellSizeAndPositionManager#getVisibleCellRange */
  getVisibleCellRange({
    containerSize,
    offset // safe
  }) {
    offset = this._safeOffsetToOffset({
      containerSize,
      offset
    });
    return this._cellSizeAndPositionManager.getVisibleCellRange({
      containerSize,
      offset
    });
  }
  resetCell(index) {
    this._cellSizeAndPositionManager.resetCell(index);
  }
  _getOffsetPercentage({
    containerSize,
    offset,
    // safe
    totalSize
  }) {
    return totalSize <= containerSize ? 0 : offset / (totalSize - containerSize);
  }
  _offsetToSafeOffset({
    containerSize,
    offset // unsafe
  }) {
    const totalSize = this._cellSizeAndPositionManager.getTotalSize();
    const safeTotalSize = this.getTotalSize();
    if (totalSize === safeTotalSize) {
      return offset;
    } else {
      const offsetPercentage = this._getOffsetPercentage({
        containerSize,
        offset,
        totalSize
      });
      return Math.round(offsetPercentage * (safeTotalSize - containerSize));
    }
  }
  _safeOffsetToOffset({
    containerSize,
    offset // safe
  }) {
    const totalSize = this._cellSizeAndPositionManager.getTotalSize();
    const safeTotalSize = this.getTotalSize();
    if (totalSize === safeTotalSize) {
      return offset;
    } else {
      const offsetPercentage = this._getOffsetPercentage({
        containerSize,
        offset,
        totalSize: safeTotalSize
      });
      return Math.round(offsetPercentage * (totalSize - containerSize));
    }
  }
}
const SCROLL_DIRECTION_BACKWARD = -1;
const SCROLL_DIRECTION_FORWARD$1 = 1;

/**
 * Calculates the number of cells to overscan before and after a specified range.
 * This function ensures that overscanning doesn't exceed the available cells.
 */

function defaultOverscanIndicesGetter$1({
  cellCount,
  overscanCellsCount,
  scrollDirection,
  startIndex,
  stopIndex
}) {
  if (scrollDirection === SCROLL_DIRECTION_FORWARD$1) {
    return {
      overscanStartIndex: Math.max(0, startIndex),
      overscanStopIndex: Math.min(cellCount - 1, stopIndex + overscanCellsCount)
    };
  } else {
    return {
      overscanStartIndex: Math.max(0, startIndex - overscanCellsCount),
      overscanStopIndex: Math.min(cellCount - 1, stopIndex)
    };
  }
}

/**
 * Helper function that determines when to update scroll offsets to ensure that a scroll-to-index remains visible.
 * This function also ensures that the scroll ofset isn't past the last column/row of cells.
 */

function updateScrollIndexHelper({
  cellSize,
  cellSizeAndPositionManager,
  previousCellsCount,
  previousCellSize,
  previousScrollToAlignment,
  previousScrollToIndex,
  previousSize,
  scrollOffset,
  scrollToAlignment,
  scrollToIndex,
  size,
  sizeJustIncreasedFromZero,
  updateScrollIndexCallback
}) {
  const cellCount = cellSizeAndPositionManager.getCellCount();
  const hasScrollToIndex = scrollToIndex >= 0 && scrollToIndex < cellCount;
  const sizeHasChanged = size !== previousSize || sizeJustIncreasedFromZero || !previousCellSize || typeof cellSize === 'number' && cellSize !== previousCellSize;

  // If we have a new scroll target OR if height/row-height has changed,
  // We should ensure that the scroll target is visible.
  if (hasScrollToIndex && (sizeHasChanged || scrollToAlignment !== previousScrollToAlignment || scrollToIndex !== previousScrollToIndex)) {
    updateScrollIndexCallback(scrollToIndex);

    // If we don't have a selected item but list size or number of children have decreased,
    // Make sure we aren't scrolled too far past the current content.
  } else if (!hasScrollToIndex && cellCount > 0 && (size < previousSize || cellCount < previousCellsCount)) {
    // We need to ensure that the current scroll offset is still within the collection's range.
    // To do this, we don't need to measure everything; CellMeasurer would perform poorly.
    // Just check to make sure we're still okay.
    // Only adjust the scroll position if we've scrolled below the last set of rows.
    if (scrollOffset > cellSizeAndPositionManager.getTotalSize() - size) {
      updateScrollIndexCallback(cellCount - 1);
    }
  }
}

/**
 * Default implementation of cellRangeRenderer used by Grid.
 * This renderer supports cell-caching while the user is scrolling.
 */
function defaultCellRangeRenderer({
  cellCache,
  cellRenderer,
  columnSizeAndPositionManager,
  columnStartIndex,
  columnStopIndex,
  deferredMeasurementCache,
  horizontalOffsetAdjustment,
  isScrolling,
  parent,
  // Grid (or List or Table)
  rowSizeAndPositionManager,
  rowStartIndex,
  rowStopIndex,
  styleCache,
  verticalOffsetAdjustment,
  visibleColumnIndices,
  visibleRowIndices
}) {
  const renderedCells = [];

  // Browsers have native size limits for elements (eg Chrome 33M pixels, IE 1.5M pixes).
  // User cannot scroll beyond these size limitations.
  // In order to work around this, ScalingCellSizeAndPositionManager compresses offsets.
  // We should never cache styles for compressed offsets though as this can lead to bugs.
  // See issue #576 for more.
  const areOffsetsAdjusted = columnSizeAndPositionManager.areOffsetsAdjusted() || rowSizeAndPositionManager.areOffsetsAdjusted();
  const canCacheStyle = !isScrolling && !areOffsetsAdjusted;
  for (let rowIndex = rowStartIndex; rowIndex <= rowStopIndex; rowIndex++) {
    let rowDatum = rowSizeAndPositionManager.getSizeAndPositionOfCell(rowIndex);
    for (let columnIndex = columnStartIndex; columnIndex <= columnStopIndex; columnIndex++) {
      let columnDatum = columnSizeAndPositionManager.getSizeAndPositionOfCell(columnIndex);
      let isVisible = columnIndex >= visibleColumnIndices.start && columnIndex <= visibleColumnIndices.stop && rowIndex >= visibleRowIndices.start && rowIndex <= visibleRowIndices.stop;
      let key = `${rowIndex}-${columnIndex}`;
      let style;

      // Cache style objects so shallow-compare doesn't re-render unnecessarily.
      if (canCacheStyle && styleCache[key]) {
        style = styleCache[key];
      } else {
        // In deferred mode, cells will be initially rendered before we know their size.
        // Don't interfere with CellMeasurer's measurements by setting an invalid size.
        if (deferredMeasurementCache && !deferredMeasurementCache.has(rowIndex, columnIndex)) {
          // Position not-yet-measured cells at top/left 0,0,
          // And give them width/height of 'auto' so they can grow larger than the parent Grid if necessary.
          // Positioning them further to the right/bottom influences their measured size.
          style = {
            height: 'auto',
            left: 0,
            position: 'absolute',
            top: 0,
            width: 'auto'
          };
        } else {
          style = {
            height: rowDatum.size,
            left: columnDatum.offset + horizontalOffsetAdjustment,
            position: 'absolute',
            top: rowDatum.offset + verticalOffsetAdjustment,
            width: columnDatum.size
          };
          styleCache[key] = style;
        }
      }
      let cellRendererParams = {
        columnIndex,
        isScrolling,
        isVisible,
        key,
        parent,
        rowIndex,
        style
      };
      let renderedCell;

      // Avoid re-creating cells while scrolling.
      // This can lead to the same cell being created many times and can cause performance issues for "heavy" cells.
      // If a scroll is in progress- cache and reuse cells.
      // This cache will be thrown away once scrolling completes.
      // However if we are scaling scroll positions and sizes, we should also avoid caching.
      // This is because the offset changes slightly as scroll position changes and caching leads to stale values.
      // For more info refer to issue #395
      if (isScrolling && !horizontalOffsetAdjustment && !verticalOffsetAdjustment) {
        if (!cellCache[key]) {
          cellCache[key] = cellRenderer(cellRendererParams);
        }
        renderedCell = cellCache[key];

        // If the user is no longer scrolling, don't cache cells.
        // This makes dynamic cell content difficult for users and would also lead to a heavier memory footprint.
      } else {
        renderedCell = cellRenderer(cellRendererParams);
      }
      if (renderedCell == null || renderedCell === false) {
        continue;
      }
      if (process.env.NODE_ENV !== 'production') {
        warnAboutMissingStyle(parent, renderedCell);
      }
      renderedCells.push(renderedCell);
    }
  }
  return renderedCells;
}
function warnAboutMissingStyle(parent, renderedCell) {
  if (process.env.NODE_ENV !== 'production') {
    if (renderedCell) {
      // If the direct child is a CellMeasurer, then we should check its child
      // See issue #611
      if (renderedCell.type && renderedCell.type.__internalCellMeasurerFlag) {
        renderedCell = renderedCell.props.children;
      }
      if (renderedCell && renderedCell.props && renderedCell.props.style === undefined && parent.__warnedAboutMissingStyle !== true) {
        parent.__warnedAboutMissingStyle = true;
        console.warn('Rendered cell should include style property for positioning.');
      }
    }
  }
}

// Properly handle server-side rendering.
let win;
if (typeof window !== 'undefined') {
  win = window;
} else if (typeof self !== 'undefined') {
  win = self;
} else {
  win = {};
}

// requestAnimationFrame() shim by Paul Irish
// http://paulirish.com/2011/requestanimationframe-for-smart-animating/
const request = win.requestAnimationFrame || win.webkitRequestAnimationFrame || win.mozRequestAnimationFrame || win.oRequestAnimationFrame || win.msRequestAnimationFrame || function (callback) {
  return win.setTimeout(callback, 1000 / 60);
};
const cancel = win.cancelAnimationFrame || win.webkitCancelAnimationFrame || win.mozCancelAnimationFrame || win.oCancelAnimationFrame || win.msCancelAnimationFrame || function (id) {
  win.clearTimeout(id);
};
const raf = request;
const caf = cancel;
const cancelAnimationTimeout = frame => caf(frame.id);

/**
 * Recursively calls requestAnimationFrame until a specified delay has been met or exceeded.
 * When the delay time has been reached the function you're timing out will be called.
 *
 * Credit: Joe Lambert (https://gist.github.com/joelambert/1002116#file-requesttimeout-js)
 */
const requestAnimationTimeout = (callback, delay) => {
  const start = Date.now();
  const timeout = () => {
    if (Date.now() - start >= delay) {
      callback.call();
    } else {
      frame.id = raf(timeout);
    }
  };
  const frame = {
    id: raf(timeout)
  };
  return frame;
};

/**
 * Specifies the number of milliseconds during which to disable pointer events while a scroll is in progress.
 * This improves performance and makes scrolling smoother.
 */
const DEFAULT_SCROLLING_RESET_TIME_INTERVAL$1 = 150;

/**
 * Controls whether the Grid updates the DOM element's scrollLeft/scrollTop based on the current state or just observes it.
 * This prevents Grid from interrupting mouse-wheel animations (see issue #2).
 */
const SCROLL_POSITION_CHANGE_REASONS = {
  OBSERVED: 'observed',
  REQUESTED: 'requested'
};
const renderNull = () => null;
/**
 * Renders tabular data with virtualization along the vertical and horizontal axes.
 * Row heights and column widths must be known ahead of time and specified as properties.
 */
class Grid extends React.PureComponent {
  // Invokes onSectionRendered callback only when start/stop row or column indices change

  // See defaultCellRangeRenderer() for more information on the usage of these caches

  constructor(props) {
    super(props);
    this.state = {
      isScrolling: false,
      scrollDirectionHorizontal: SCROLL_DIRECTION_FORWARD$1,
      scrollDirectionVertical: SCROLL_DIRECTION_FORWARD$1,
      scrollLeft: 0,
      scrollTop: 0,
      scrollPositionChangeReason: null
    };
    this._onGridRenderedMemoizer = createCallbackMemoizer();
    this._onScrollMemoizer = createCallbackMemoizer(false);
    this._deferredInvalidateColumnIndex = null;
    this._deferredInvalidateRowIndex = null;
    this._recomputeScrollLeftFlag = false;
    this._recomputeScrollTopFlag = false;
    this._horizontalScrollBarSize = 0;
    this._verticalScrollBarSize = 0;
    this._scrollbarPresenceChanged = false;
    this._cellCache = {};
    this._styleCache = {};
    this._scrollbarSizeMeasured = false;
    this._renderedColumnStartIndex = 0;
    this._renderedColumnStopIndex = 0;
    this._renderedRowStartIndex = 0;
    this._renderedRowStopIndex = 0;
    this._debounceScrollEndedCallback = () => {
      this._disablePointerEventsTimeoutId = null;
      this._resetStyleCache();
    };
    this._invokeOnGridRenderedHelper = () => {
      const {
        onSectionRendered
      } = this.props;
      this._onGridRenderedMemoizer({
        callback: onSectionRendered,
        indices: {
          columnOverscanStartIndex: this._columnStartIndex,
          columnOverscanStopIndex: this._columnStopIndex,
          columnStartIndex: this._renderedColumnStartIndex,
          columnStopIndex: this._renderedColumnStopIndex,
          rowOverscanStartIndex: this._rowStartIndex,
          rowOverscanStopIndex: this._rowStopIndex,
          rowStartIndex: this._renderedRowStartIndex,
          rowStopIndex: this._renderedRowStopIndex
        }
      });
    };
    this._setScrollingContainerRef = ref => {
      this._scrollingContainer = ref;
      if (typeof this.props.elementRef === 'function') {
        this.props.elementRef(ref);
      } else if (typeof this.props.elementRef === 'object') {
        this.props.elementRef.current = ref;
      }
    };
    this._onScroll = event => {
      // In certain edge-cases React dispatches an onScroll event with an invalid target.scrollLeft / target.scrollTop.
      // This invalid event can be detected by comparing event.target to this component's scrollable DOM element.
      // See issue #404 for more information.
      if (event.target === this._scrollingContainer) {
        this.handleScrollEvent(event.target);
      }
    };
    this._columnWidthGetter = this._wrapSizeGetter(props.columnWidth);
    this._rowHeightGetter = this._wrapSizeGetter(props.rowHeight);
    this._columnSizeAndPositionManager = new ScalingCellSizeAndPositionManager({
      cellCount: props.columnCount,
      cellSizeGetter: params => this._columnWidthGetter(params),
      estimatedCellSize: this._getEstimatedColumnSize(props)
    });
    this._rowSizeAndPositionManager = new ScalingCellSizeAndPositionManager({
      cellCount: props.rowCount,
      cellSizeGetter: params => this._rowHeightGetter(params),
      estimatedCellSize: this._getEstimatedRowSize(props)
    });
  }

  /**
   * Gets offsets for a given cell and alignment.
   */
  getOffsetForCell({
    alignment = this.props.scrollToAlignment,
    columnIndex = this.props.scrollToColumn,
    rowIndex = this.props.scrollToRow
  } = {}) {
    const offsetProps = Object.assign({}, this.props, {
      scrollToAlignment: alignment,
      scrollToColumn: columnIndex,
      scrollToRow: rowIndex
    });
    return {
      scrollLeft: this._getCalculatedScrollLeft(offsetProps),
      scrollTop: this._getCalculatedScrollTop(offsetProps)
    };
  }

  /**
   * This method handles a scroll event originating from an external scroll control.
   * It's an advanced method and should probably not be used unless you're implementing a custom scroll-bar solution.
   */
  handleScrollEvent({
    scrollLeft: scrollLeftParam = 0,
    scrollTop: scrollTopParam = 0
  }) {
    // On iOS, we can arrive at negative offsets by swiping past the start.
    // To prevent flicker here, we make playing in the negative offset zone cause nothing to happen.
    if (scrollTopParam < 0) {
      return;
    }

    // Prevent pointer events from interrupting a smooth scroll
    this._debounceScrollEnded();
    const {
      autoHeight,
      autoWidth,
      height,
      width
    } = this.props;

    // When this component is shrunk drastically, React dispatches a series of back-to-back scroll events,
    // Gradually converging on a scrollTop that is within the bounds of the new, smaller height.
    // This causes a series of rapid renders that is slow for long lists.
    // We can avoid that by doing some simple bounds checking to ensure that scroll offsets never exceed their bounds.
    const scrollbarSize = this._scrollbarSize;
    const totalRowsHeight = this._rowSizeAndPositionManager.getTotalSize();
    const totalColumnsWidth = this._columnSizeAndPositionManager.getTotalSize();
    const scrollLeft = Math.min(Math.max(0, totalColumnsWidth - width + scrollbarSize), scrollLeftParam);
    const scrollTop = Math.min(Math.max(0, totalRowsHeight - height + scrollbarSize), scrollTopParam);

    // Certain devices (like Apple touchpad) rapid-fire duplicate events.
    // Don't force a re-render if this is the case.
    // The mouse may move faster then the animation frame does.
    // Use requestAnimationFrame to avoid over-updating.
    if (this.state.scrollLeft !== scrollLeft || this.state.scrollTop !== scrollTop) {
      // Track scrolling direction so we can more efficiently overscan rows to reduce empty space around the edges while scrolling.
      // Don't change direction for an axis unless scroll offset has changed.
      const scrollDirectionHorizontal = scrollLeft !== this.state.scrollLeft ? scrollLeft > this.state.scrollLeft ? SCROLL_DIRECTION_FORWARD$1 : SCROLL_DIRECTION_BACKWARD : this.state.scrollDirectionHorizontal;
      const scrollDirectionVertical = scrollTop !== this.state.scrollTop ? scrollTop > this.state.scrollTop ? SCROLL_DIRECTION_FORWARD$1 : SCROLL_DIRECTION_BACKWARD : this.state.scrollDirectionVertical;
      const newState = {
        isScrolling: true,
        scrollDirectionHorizontal,
        scrollDirectionVertical,
        scrollPositionChangeReason: SCROLL_POSITION_CHANGE_REASONS.OBSERVED
      };
      if (!autoHeight) {
        newState.scrollTop = scrollTop;
      }
      if (!autoWidth) {
        newState.scrollLeft = scrollLeft;
      }
      this.setState(newState);
    }
    this._invokeOnScrollMemoizer({
      scrollLeft,
      scrollTop,
      totalColumnsWidth,
      totalRowsHeight
    });
  }

  /**
   * Invalidate Grid size and recompute visible cells.
   * This is a deferred wrapper for recomputeGridSize().
   * It sets a flag to be evaluated on cDM/cDU to avoid unnecessary renders.
   * This method is intended for advanced use-cases like CellMeasurer.
   */
  // @TODO (bvaughn) Add automated test coverage for this.
  invalidateCellSizeAfterRender({
    columnIndex,
    rowIndex
  }) {
    this._deferredInvalidateColumnIndex = typeof this._deferredInvalidateColumnIndex === 'number' ? Math.min(this._deferredInvalidateColumnIndex, columnIndex) : columnIndex;
    this._deferredInvalidateRowIndex = typeof this._deferredInvalidateRowIndex === 'number' ? Math.min(this._deferredInvalidateRowIndex, rowIndex) : rowIndex;
  }

  /**
   * Pre-measure all columns and rows in a Grid.
   * Typically cells are only measured as needed and estimated sizes are used for cells that have not yet been measured.
   * This method ensures that the next call to getTotalSize() returns an exact size (as opposed to just an estimated one).
   */
  measureAllCells() {
    const {
      columnCount,
      rowCount
    } = this.props;
    this._columnSizeAndPositionManager.getSizeAndPositionOfCell(columnCount - 1);
    this._rowSizeAndPositionManager.getSizeAndPositionOfCell(rowCount - 1);
  }

  /**
   * Forced recompute of row heights and column widths.
   * This function should be called if dynamic column or row sizes have changed but nothing else has.
   * Since Grid only receives :columnCount and :rowCount it has no way of detecting when the underlying data changes.
   */
  recomputeGridSize({
    columnIndex = 0,
    rowIndex = 0
  } = {}) {
    const {
      scrollToColumn,
      scrollToRow
    } = this.props;
    this._columnSizeAndPositionManager.resetCell(columnIndex);
    this._rowSizeAndPositionManager.resetCell(rowIndex);

    // Cell sizes may be determined by a function property.
    // In this case the cDU handler can't know if they changed.
    // Store this flag to let the next cDU pass know it needs to recompute the scroll offset.
    this._recomputeScrollLeftFlag = scrollToColumn >= 0 && columnIndex <= scrollToColumn;
    this._recomputeScrollTopFlag = scrollToRow >= 0 && rowIndex <= scrollToRow;

    // Clear cell cache in case we are scrolling;
    // Invalid row heights likely mean invalid cached content as well.
    this._cellCache = {};
    this._styleCache = {};
    this.forceUpdate();
  }

  /**
   * Ensure column and row are visible.
   */
  scrollToCell({
    columnIndex,
    rowIndex
  }) {
    const {
      columnCount
    } = this.props;
    const props = this.props;

    // Don't adjust scroll offset for single-column grids (eg List, Table).
    // This can cause a funky scroll offset because of the vertical scrollbar width.
    if (columnCount > 1 && columnIndex !== undefined) {
      this._updateScrollLeftForScrollToColumn(Object.assign({}, props, {
        scrollToColumn: columnIndex
      }));
    }
    if (rowIndex !== undefined) {
      this._updateScrollTopForScrollToRow(Object.assign({}, props, {
        scrollToRow: rowIndex
      }));
    }
  }
  componentDidMount() {
    const {
      getScrollbarSize,
      height,
      scrollLeft,
      scrollToColumn,
      scrollTop,
      scrollToRow,
      width
    } = this.props;

    // If cell sizes have been invalidated (eg we are using CellMeasurer) then reset cached positions.
    // We must do this at the start of the method as we may calculate and update scroll position below.
    this._handleInvalidatedGridSize();

    // If this component was first rendered server-side, scrollbar size will be undefined.
    // In that event we need to remeasure.
    if (!this._scrollbarSizeMeasured) {
      this._scrollbarSize = getScrollbarSize();
      this._scrollbarSizeMeasured = true;
      this.setState({});
    }
    if (typeof scrollLeft === 'number' && scrollLeft >= 0 || typeof scrollTop === 'number' && scrollTop >= 0) {
      this.scrollToPosition({
        scrollLeft,
        scrollTop
      });
    }

    // Don't update scroll offset if the size is 0; we don't render any cells in this case.
    // Setting a state may cause us to later thing we've updated the offce when we haven't.
    const sizeIsBiggerThanZero = height > 0 && width > 0;
    if (scrollToColumn >= 0 && sizeIsBiggerThanZero) {
      this._updateScrollLeftForScrollToColumn();
    }
    if (scrollToRow >= 0 && sizeIsBiggerThanZero) {
      this._updateScrollTopForScrollToRow();
    }

    // Update onRowsRendered callback
    this._invokeOnGridRenderedHelper();

    // Initialize onScroll callback
    this._invokeOnScrollMemoizer({
      scrollLeft: scrollLeft || 0,
      scrollTop: scrollTop || 0,
      totalColumnsWidth: this._columnSizeAndPositionManager.getTotalSize(),
      totalRowsHeight: this._rowSizeAndPositionManager.getTotalSize()
    });
    this._maybeCallOnScrollbarPresenceChange();
  }

  /**
   * @private
   * This method updates scrollLeft/scrollTop in state for the following conditions:
   * 1) New scroll-to-cell props have been set
   */
  componentDidUpdate(prevProps, prevState) {
    const {
      autoHeight,
      autoWidth,
      columnCount,
      height,
      rowCount,
      scrollToAlignment,
      scrollToColumn,
      scrollToRow,
      width
    } = this.props;
    const {
      scrollLeft,
      scrollPositionChangeReason,
      scrollTop
    } = this.state;

    // If cell sizes have been invalidated (eg we are using CellMeasurer) then reset cached positions.
    // We must do this at the start of the method as we may calculate and update scroll position below.
    this._handleInvalidatedGridSize();

    // Handle edge case where column or row count has only just increased over 0.
    // In this case we may have to restore a previously-specified scroll offset.
    // For more info see bvaughn/react-virtualized/issues/218
    const columnOrRowCountJustIncreasedFromZero = columnCount > 0 && prevProps.columnCount === 0 || rowCount > 0 && prevProps.rowCount === 0;

    // Make sure requested changes to :scrollLeft or :scrollTop get applied.
    // Assigning to scrollLeft/scrollTop tells the browser to interrupt any running scroll animations,
    // And to discard any pending async changes to the scroll position that may have happened in the meantime (e.g. on a separate scrolling thread).
    // So we only set these when we require an adjustment of the scroll position.
    // See issue #2 for more information.
    if (scrollPositionChangeReason === SCROLL_POSITION_CHANGE_REASONS.REQUESTED) {
      // @TRICKY :autoHeight and :autoWidth properties instructs Grid to leave :scrollTop and :scrollLeft management to an external HOC (eg WindowScroller).
      // In this case we should avoid checking scrollingContainer.scrollTop and scrollingContainer.scrollLeft since it forces layout/flow.
      if (!autoWidth && scrollLeft >= 0 && (scrollLeft !== prevState.scrollLeft && scrollLeft !== this._scrollingContainer.scrollLeft || columnOrRowCountJustIncreasedFromZero)) {
        this._scrollingContainer.scrollLeft = scrollLeft;
      }
      if (!autoHeight && scrollTop >= 0 && (scrollTop !== prevState.scrollTop && scrollTop !== this._scrollingContainer.scrollTop || columnOrRowCountJustIncreasedFromZero)) {
        this._scrollingContainer.scrollTop = scrollTop;
      }
    }

    // Special case where the previous size was 0:
    // In this case we don't show any windowed cells at all.
    // So we should always recalculate offset afterwards.
    const sizeJustIncreasedFromZero = (prevProps.width === 0 || prevProps.height === 0) && height > 0 && width > 0;

    // Update scroll offsets if the current :scrollToColumn or :scrollToRow values requires it
    // @TODO Do we also need this check or can the one in componentWillUpdate() suffice?
    if (this._recomputeScrollLeftFlag) {
      this._recomputeScrollLeftFlag = false;
      this._updateScrollLeftForScrollToColumn(this.props);
    } else {
      updateScrollIndexHelper({
        cellSizeAndPositionManager: this._columnSizeAndPositionManager,
        previousCellsCount: prevProps.columnCount,
        previousCellSize: prevProps.columnWidth,
        previousScrollToAlignment: prevProps.scrollToAlignment,
        previousScrollToIndex: prevProps.scrollToColumn,
        previousSize: prevProps.width,
        scrollOffset: scrollLeft,
        scrollToAlignment,
        scrollToIndex: scrollToColumn,
        size: width,
        sizeJustIncreasedFromZero,
        updateScrollIndexCallback: () => this._updateScrollLeftForScrollToColumn(this.props)
      });
    }
    if (this._recomputeScrollTopFlag) {
      this._recomputeScrollTopFlag = false;
      this._updateScrollTopForScrollToRow(this.props);
    } else {
      updateScrollIndexHelper({
        cellSizeAndPositionManager: this._rowSizeAndPositionManager,
        previousCellsCount: prevProps.rowCount,
        previousCellSize: prevProps.rowHeight,
        previousScrollToAlignment: prevProps.scrollToAlignment,
        previousScrollToIndex: prevProps.scrollToRow,
        previousSize: prevProps.height,
        scrollOffset: scrollTop,
        scrollToAlignment,
        scrollToIndex: scrollToRow,
        size: height,
        sizeJustIncreasedFromZero,
        updateScrollIndexCallback: () => this._updateScrollTopForScrollToRow(this.props)
      });
    }

    // Update onRowsRendered callback if start/stop indices have changed
    this._invokeOnGridRenderedHelper();

    // Changes to :scrollLeft or :scrollTop should also notify :onScroll listeners
    if (scrollLeft !== prevState.scrollLeft || scrollTop !== prevState.scrollTop) {
      const totalRowsHeight = this._rowSizeAndPositionManager.getTotalSize();
      const totalColumnsWidth = this._columnSizeAndPositionManager.getTotalSize();
      this._invokeOnScrollMemoizer({
        scrollLeft,
        scrollTop,
        totalColumnsWidth,
        totalRowsHeight
      });
    }
    this._maybeCallOnScrollbarPresenceChange();
  }
  UNSAFE_componentWillMount() {
    const {
      getScrollbarSize
    } = this.props;

    // If this component is being rendered server-side, getScrollbarSize() will return undefined.
    // We handle this case in componentDidMount()
    this._scrollbarSize = getScrollbarSize();
    if (this._scrollbarSize === undefined) {
      this._scrollbarSizeMeasured = false;
      this._scrollbarSize = 0;
    } else {
      this._scrollbarSizeMeasured = true;
    }
    this._calculateChildrenToRender();
  }
  componentWillUnmount() {
    if (this._disablePointerEventsTimeoutId) {
      cancelAnimationTimeout(this._disablePointerEventsTimeoutId);
    }
  }

  /**
   * @private
   * This method updates scrollLeft/scrollTop in state for the following conditions:
   * 1) Empty content (0 rows or columns)
   * 2) New scroll props overriding the current state
   * 3) Cells-count or cells-size has changed, making previous scroll offsets invalid
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      scrollLeft,
      scrollTop
    } = this.state;
    if (nextProps.columnCount === 0 && scrollLeft !== 0 || nextProps.rowCount === 0 && scrollTop !== 0) {
      this.scrollToPosition({
        scrollLeft: 0,
        scrollTop: 0
      });
    } else if (nextProps.scrollLeft !== this.props.scrollLeft || nextProps.scrollTop !== this.props.scrollTop) {
      const newState = {};
      if (nextProps.scrollLeft != null) {
        newState.scrollLeft = nextProps.scrollLeft;
      }
      if (nextProps.scrollTop != null) {
        newState.scrollTop = nextProps.scrollTop;
      }
      this.scrollToPosition(newState);
    }
    if (nextProps.columnWidth !== this.props.columnWidth || nextProps.rowHeight !== this.props.rowHeight) {
      this._styleCache = {};
    }
    this._columnWidthGetter = this._wrapSizeGetter(nextProps.columnWidth);
    this._rowHeightGetter = this._wrapSizeGetter(nextProps.rowHeight);
    this._columnSizeAndPositionManager.configure({
      cellCount: nextProps.columnCount,
      estimatedCellSize: this._getEstimatedColumnSize(nextProps)
    });
    this._rowSizeAndPositionManager.configure({
      cellCount: nextProps.rowCount,
      estimatedCellSize: this._getEstimatedRowSize(nextProps)
    });
    let {
      columnCount,
      rowCount
    } = this.props;

    // Special case when either cols or rows were 0
    // This would prevent any cells from rendering
    // So we need to reset row scroll if cols changed from 0 (and vice versa)
    if (columnCount === 0 || rowCount === 0) {
      columnCount = 0;
      rowCount = 0;
    }

    // If scrolling is controlled outside this component, clear cache when scrolling stops
    if (nextProps.autoHeight && nextProps.isScrolling === false && this.props.isScrolling === true) {
      this._resetStyleCache();
    }

    // Update scroll offsets if the size or number of cells have changed, invalidating the previous value
    calculateSizeAndPositionDataAndUpdateScrollOffset({
      cellCount: columnCount,
      cellSize: typeof this.props.columnWidth === 'number' ? this.props.columnWidth : null,
      computeMetadataCallback: () => this._columnSizeAndPositionManager.resetCell(0),
      computeMetadataCallbackProps: nextProps,
      nextCellsCount: nextProps.columnCount,
      nextCellSize: typeof nextProps.columnWidth === 'number' ? nextProps.columnWidth : null,
      nextScrollToIndex: nextProps.scrollToColumn,
      scrollToIndex: this.props.scrollToColumn,
      updateScrollOffsetForScrollToIndex: () => this._updateScrollLeftForScrollToColumn(nextProps, this.state)
    });
    calculateSizeAndPositionDataAndUpdateScrollOffset({
      cellCount: rowCount,
      cellSize: typeof this.props.rowHeight === 'number' ? this.props.rowHeight : null,
      computeMetadataCallback: () => this._rowSizeAndPositionManager.resetCell(0),
      computeMetadataCallbackProps: nextProps,
      nextCellsCount: nextProps.rowCount,
      nextCellSize: typeof nextProps.rowHeight === 'number' ? nextProps.rowHeight : null,
      nextScrollToIndex: nextProps.scrollToRow,
      scrollToIndex: this.props.scrollToRow,
      updateScrollOffsetForScrollToIndex: () => this._updateScrollTopForScrollToRow(nextProps, this.state)
    });
  }
  UNSAFE_componentWillUpdate(nextProps, nextState) {
    this._calculateChildrenToRender(nextProps, nextState);
  }
  render() {
    const {
      autoContainerWidth,
      autoHeight,
      autoWidth,
      className,
      containerProps,
      containerRole,
      containerStyle,
      height,
      id,
      noContentRenderer,
      role,
      style,
      tabIndex,
      width
    } = this.props;
    const isScrolling = this._isScrolling();
    const gridStyle = {
      boxSizing: 'border-box',
      direction: 'ltr',
      height: autoHeight ? 'auto' : height,
      position: 'relative',
      width: autoWidth ? 'auto' : width,
      WebkitOverflowScrolling: 'touch',
      willChange: 'transform'
    };
    const totalColumnsWidth = this._columnSizeAndPositionManager.getTotalSize();
    const totalRowsHeight = this._rowSizeAndPositionManager.getTotalSize();

    // Force browser to hide scrollbars when we know they aren't necessary.
    // Otherwise once scrollbars appear they may not disappear again.
    // For more info see issue #116
    const verticalScrollBarSize = totalRowsHeight > height ? this._scrollbarSize : 0;
    const horizontalScrollBarSize = totalColumnsWidth > width ? this._scrollbarSize : 0;
    if (horizontalScrollBarSize !== this._horizontalScrollBarSize || verticalScrollBarSize !== this._verticalScrollBarSize) {
      this._horizontalScrollBarSize = horizontalScrollBarSize;
      this._verticalScrollBarSize = verticalScrollBarSize;
      this._scrollbarPresenceChanged = true;
    }

    // Also explicitly init styles to 'auto' if scrollbars are required.
    // This works around an obscure edge case where external CSS styles have not yet been loaded,
    // But an initial scroll index of offset is set as an external prop.
    // Without this style, Grid would render the correct range of cells but would NOT update its internal offset.
    // This was originally reported via clauderic/react-infinite-calendar/issues/23
    gridStyle.overflowX = totalColumnsWidth + verticalScrollBarSize <= width ? 'hidden' : 'auto';
    gridStyle.overflowY = totalRowsHeight + horizontalScrollBarSize <= height ? 'hidden' : 'auto';
    const childrenToDisplay = this._childrenToDisplay;
    const showNoContentRenderer = childrenToDisplay.length === 0 && height > 0 && width > 0;
    return /*#__PURE__*/React.createElement("div", Object.assign({
      ref: this._setScrollingContainerRef
    }, containerProps, {
      "aria-label": this.props['aria-label'],
      "aria-readonly": this.props['aria-readonly'],
      className: cn('ReactVirtualized__Grid', className),
      id: id,
      onScroll: this._onScroll,
      role: role,
      style: Object.assign({}, gridStyle, style),
      tabIndex: tabIndex
    }), childrenToDisplay.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "ReactVirtualized__Grid__innerScrollContainer",
      role: containerRole,
      style: Object.assign({
        width: autoContainerWidth ? 'auto' : totalColumnsWidth,
        height: totalRowsHeight,
        maxWidth: totalColumnsWidth,
        maxHeight: totalRowsHeight,
        overflow: 'hidden',
        pointerEvents: isScrolling ? 'none' : '',
        position: 'relative'
      }, containerStyle)
    }, childrenToDisplay), showNoContentRenderer && noContentRenderer());
  }

  /* ---------------------------- Helper methods ---------------------------- */

  _calculateChildrenToRender(props = this.props, state = this.state) {
    const {
      cellRenderer,
      cellRangeRenderer,
      columnCount,
      deferredMeasurementCache,
      height,
      overscanColumnCount,
      overscanIndicesGetter,
      overscanRowCount,
      rowCount,
      width
    } = props;
    const {
      scrollDirectionHorizontal,
      scrollDirectionVertical,
      scrollLeft,
      scrollTop
    } = state;
    const isScrolling = this._isScrolling(props, state);
    this._childrenToDisplay = [];

    // Render only enough columns and rows to cover the visible area of the grid.
    if (height > 0 && width > 0) {
      const visibleColumnIndices = this._columnSizeAndPositionManager.getVisibleCellRange({
        containerSize: width,
        offset: scrollLeft
      });
      const visibleRowIndices = this._rowSizeAndPositionManager.getVisibleCellRange({
        containerSize: height,
        offset: scrollTop
      });
      const horizontalOffsetAdjustment = this._columnSizeAndPositionManager.getOffsetAdjustment({
        containerSize: width,
        offset: scrollLeft
      });
      const verticalOffsetAdjustment = this._rowSizeAndPositionManager.getOffsetAdjustment({
        containerSize: height,
        offset: scrollTop
      });

      // Store for _invokeOnGridRenderedHelper()
      this._renderedColumnStartIndex = visibleColumnIndices.start;
      this._renderedColumnStopIndex = visibleColumnIndices.stop;
      this._renderedRowStartIndex = visibleRowIndices.start;
      this._renderedRowStopIndex = visibleRowIndices.stop;
      const overscanColumnIndices = overscanIndicesGetter({
        direction: 'horizontal',
        cellCount: columnCount,
        overscanCellsCount: overscanColumnCount,
        scrollDirection: scrollDirectionHorizontal,
        startIndex: typeof visibleColumnIndices.start === 'number' ? visibleColumnIndices.start : 0,
        stopIndex: typeof visibleColumnIndices.stop === 'number' ? visibleColumnIndices.stop : -1
      });
      const overscanRowIndices = overscanIndicesGetter({
        direction: 'vertical',
        cellCount: rowCount,
        overscanCellsCount: overscanRowCount,
        scrollDirection: scrollDirectionVertical,
        startIndex: typeof visibleRowIndices.start === 'number' ? visibleRowIndices.start : 0,
        stopIndex: typeof visibleRowIndices.stop === 'number' ? visibleRowIndices.stop : -1
      });

      // Store for _invokeOnGridRenderedHelper()
      this._columnStartIndex = overscanColumnIndices.overscanStartIndex;
      this._columnStopIndex = overscanColumnIndices.overscanStopIndex;
      this._rowStartIndex = overscanRowIndices.overscanStartIndex;
      this._rowStopIndex = overscanRowIndices.overscanStopIndex;

      // Advanced use-cases (eg CellMeasurer) require batched measurements to determine accurate sizes.
      if (deferredMeasurementCache) {
        // If rows have a dynamic height, scan the rows we are about to render.
        // If any have not yet been measured, then we need to render all columns initially,
        // Because the height of the row is equal to the tallest cell within that row,
        // (And so we can't know the height without measuring all column-cells first).
        if (!deferredMeasurementCache.hasFixedHeight()) {
          for (let rowIndex = this._rowStartIndex; rowIndex <= this._rowStopIndex; rowIndex++) {
            if (!deferredMeasurementCache.has(rowIndex, 0)) {
              this._columnStartIndex = 0;
              this._columnStopIndex = columnCount - 1;
              break;
            }
          }
        }

        // If columns have a dynamic width, scan the columns we are about to render.
        // If any have not yet been measured, then we need to render all rows initially,
        // Because the width of the column is equal to the widest cell within that column,
        // (And so we can't know the width without measuring all row-cells first).
        if (!deferredMeasurementCache.hasFixedWidth()) {
          for (let columnIndex = this._columnStartIndex; columnIndex <= this._columnStopIndex; columnIndex++) {
            if (!deferredMeasurementCache.has(0, columnIndex)) {
              this._rowStartIndex = 0;
              this._rowStopIndex = rowCount - 1;
              break;
            }
          }
        }
      }
      this._childrenToDisplay = cellRangeRenderer({
        cellCache: this._cellCache,
        cellRenderer,
        columnSizeAndPositionManager: this._columnSizeAndPositionManager,
        columnStartIndex: this._columnStartIndex,
        columnStopIndex: this._columnStopIndex,
        deferredMeasurementCache,
        horizontalOffsetAdjustment,
        isScrolling,
        parent: this,
        rowSizeAndPositionManager: this._rowSizeAndPositionManager,
        rowStartIndex: this._rowStartIndex,
        rowStopIndex: this._rowStopIndex,
        scrollLeft,
        scrollTop,
        styleCache: this._styleCache,
        verticalOffsetAdjustment,
        visibleColumnIndices,
        visibleRowIndices
      });
    }
  }

  /**
   * Sets an :isScrolling flag for a small window of time.
   * This flag is used to disable pointer events on the scrollable portion of the Grid.
   * This prevents jerky/stuttery mouse-wheel scrolling.
   */
  _debounceScrollEnded() {
    const {
      scrollingResetTimeInterval
    } = this.props;
    if (this._disablePointerEventsTimeoutId) {
      cancelAnimationTimeout(this._disablePointerEventsTimeoutId);
    }
    this._disablePointerEventsTimeoutId = requestAnimationTimeout(this._debounceScrollEndedCallback, scrollingResetTimeInterval);
  }
  _getEstimatedColumnSize(props) {
    return typeof props.columnWidth === 'number' ? props.columnWidth : props.estimatedColumnSize;
  }
  _getEstimatedRowSize(props) {
    return typeof props.rowHeight === 'number' ? props.rowHeight : props.estimatedRowSize;
  }

  /**
   * Check for batched CellMeasurer size invalidations.
   * This will occur the first time one or more previously unmeasured cells are rendered.
   */
  _handleInvalidatedGridSize() {
    if (typeof this._deferredInvalidateColumnIndex === 'number' && typeof this._deferredInvalidateRowIndex === 'number') {
      const columnIndex = this._deferredInvalidateColumnIndex;
      const rowIndex = this._deferredInvalidateRowIndex;
      this._deferredInvalidateColumnIndex = null;
      this._deferredInvalidateRowIndex = null;
      this.recomputeGridSize({
        columnIndex,
        rowIndex
      });
    }
  }
  _invokeOnScrollMemoizer({
    scrollLeft,
    scrollTop,
    totalColumnsWidth,
    totalRowsHeight
  }) {
    this._onScrollMemoizer({
      callback: ({
        scrollLeft,
        scrollTop
      }) => {
        const {
          height,
          onScroll,
          width
        } = this.props;
        onScroll({
          clientHeight: height,
          clientWidth: width,
          scrollHeight: totalRowsHeight,
          scrollLeft,
          scrollTop,
          scrollWidth: totalColumnsWidth
        });
      },
      indices: {
        scrollLeft,
        scrollTop
      }
    });
  }
  _isScrolling(props = this.props, state = this.state) {
    // If isScrolling is defined in props, use it to override the value in state
    // This is a performance optimization for WindowScroller + Grid
    return Object.hasOwnProperty.call(props, 'isScrolling') ? Boolean(props.isScrolling) : Boolean(state.isScrolling);
  }
  _maybeCallOnScrollbarPresenceChange() {
    if (this._scrollbarPresenceChanged) {
      const {
        onScrollbarPresenceChange
      } = this.props;
      this._scrollbarPresenceChanged = false;
      onScrollbarPresenceChange({
        horizontal: this._horizontalScrollBarSize > 0,
        size: this._scrollbarSize,
        vertical: this._verticalScrollBarSize > 0
      });
    }
  }
  /**
   * Scroll to the specified offset(s).
   * Useful for animating position changes.
   */
  scrollToPosition({
    scrollLeft,
    scrollTop
  }) {
    const newState = {
      scrollPositionChangeReason: SCROLL_POSITION_CHANGE_REASONS.REQUESTED
    };
    if (typeof scrollLeft === 'number' && scrollLeft >= 0) {
      newState.scrollDirectionHorizontal = scrollLeft > this.state.scrollLeft ? SCROLL_DIRECTION_FORWARD$1 : SCROLL_DIRECTION_BACKWARD;
      newState.scrollLeft = scrollLeft;
    }
    if (typeof scrollTop === 'number' && scrollTop >= 0) {
      newState.scrollDirectionVertical = scrollTop > this.state.scrollTop ? SCROLL_DIRECTION_FORWARD$1 : SCROLL_DIRECTION_BACKWARD;
      newState.scrollTop = scrollTop;
    }
    if (typeof scrollLeft === 'number' && scrollLeft >= 0 && scrollLeft !== this.state.scrollLeft || typeof scrollTop === 'number' && scrollTop >= 0 && scrollTop !== this.state.scrollTop) {
      this.setState(newState);
    }
  }
  _wrapSizeGetter(value) {
    return typeof value === 'function' ? value : () => value;
  }
  _getCalculatedScrollLeft(props = this.props, state = this.state) {
    const {
      columnCount,
      height,
      scrollToAlignment,
      scrollToColumn,
      width
    } = props;
    const {
      scrollLeft
    } = state;
    if (columnCount > 0) {
      const finalColumn = columnCount - 1;
      const targetIndex = scrollToColumn < 0 ? finalColumn : Math.min(finalColumn, scrollToColumn);
      const totalRowsHeight = this._rowSizeAndPositionManager.getTotalSize();
      const scrollBarSize = totalRowsHeight > height ? this._scrollbarSize : 0;
      return this._columnSizeAndPositionManager.getUpdatedOffsetForIndex({
        align: scrollToAlignment,
        containerSize: width - scrollBarSize,
        currentOffset: scrollLeft,
        targetIndex
      });
    }
  }
  _updateScrollLeftForScrollToColumn(props = this.props, state = this.state) {
    const {
      scrollLeft
    } = state;
    const calculatedScrollLeft = this._getCalculatedScrollLeft(props, state);
    if (typeof calculatedScrollLeft === 'number' && calculatedScrollLeft >= 0 && scrollLeft !== calculatedScrollLeft) {
      this.scrollToPosition({
        scrollLeft: calculatedScrollLeft,
        scrollTop: -1
      });
    }
  }
  _getCalculatedScrollTop(props = this.props, state = this.state) {
    const {
      height,
      rowCount,
      scrollToAlignment,
      scrollToRow,
      width
    } = props;
    const {
      scrollTop
    } = state;
    if (rowCount > 0) {
      const finalRow = rowCount - 1;
      const targetIndex = scrollToRow < 0 ? finalRow : Math.min(finalRow, scrollToRow);
      const totalColumnsWidth = this._columnSizeAndPositionManager.getTotalSize();
      const scrollBarSize = totalColumnsWidth > width ? this._scrollbarSize : 0;
      return this._rowSizeAndPositionManager.getUpdatedOffsetForIndex({
        align: scrollToAlignment,
        containerSize: height - scrollBarSize,
        currentOffset: scrollTop,
        targetIndex
      });
    }
  }
  _resetStyleCache() {
    const styleCache = this._styleCache;

    // Reset cell and style caches once scrolling stops.
    // This makes Grid simpler to use (since cells commonly change).
    // And it keeps the caches from growing too large.
    // Performance is most sensitive when a user is scrolling.
    this._cellCache = {};
    this._styleCache = {};

    // Copy over the visible cell styles so avoid unnecessary re-render.
    for (let rowIndex = this._rowStartIndex; rowIndex <= this._rowStopIndex; rowIndex++) {
      for (let columnIndex = this._columnStartIndex; columnIndex <= this._columnStopIndex; columnIndex++) {
        let key = `${rowIndex}-${columnIndex}`;
        this._styleCache[key] = styleCache[key];
      }
    }
    this.setState({
      isScrolling: false
    });
  }
  _updateScrollTopForScrollToRow(props = this.props, state = this.state) {
    const {
      scrollTop
    } = state;
    const calculatedScrollTop = this._getCalculatedScrollTop(props, state);
    if (typeof calculatedScrollTop === 'number' && calculatedScrollTop >= 0 && scrollTop !== calculatedScrollTop) {
      this.scrollToPosition({
        scrollLeft: -1,
        scrollTop: calculatedScrollTop
      });
    }
  }
}
Grid.defaultProps = {
  'aria-label': 'grid',
  'aria-readonly': true,
  autoContainerWidth: false,
  autoHeight: false,
  autoWidth: false,
  cellRangeRenderer: defaultCellRangeRenderer,
  containerRole: 'rowgroup',
  containerStyle: {},
  estimatedColumnSize: 100,
  estimatedRowSize: 30,
  getScrollbarSize: scrollbarSize$1,
  noContentRenderer: renderNull,
  onScroll: () => {},
  onScrollbarPresenceChange: () => {},
  onSectionRendered: () => {},
  overscanColumnCount: 0,
  overscanIndicesGetter: defaultOverscanIndicesGetter$1,
  overscanRowCount: 10,
  role: 'grid',
  scrollingResetTimeInterval: DEFAULT_SCROLLING_RESET_TIME_INTERVAL$1,
  scrollToAlignment: 'auto',
  scrollToColumn: -1,
  scrollToRow: -1,
  style: {},
  tabIndex: 0
};
const SCROLL_DIRECTION_FORWARD = 1;

/**
 * Calculates the number of cells to overscan before and after a specified range.
 * This function ensures that overscanning doesn't exceed the available cells.
 */

function defaultOverscanIndicesGetter({
  cellCount,
  overscanCellsCount,
  scrollDirection,
  startIndex,
  stopIndex
}) {
  // Make sure we render at least 1 cell extra before and after (except near boundaries)
  // This is necessary in order to support keyboard navigation (TAB/SHIFT+TAB) in some cases
  // For more info see issues #625
  overscanCellsCount = Math.max(1, overscanCellsCount);
  if (scrollDirection === SCROLL_DIRECTION_FORWARD) {
    return {
      overscanStartIndex: Math.max(0, startIndex - 1),
      overscanStopIndex: Math.min(cellCount - 1, stopIndex + overscanCellsCount)
    };
  } else {
    return {
      overscanStartIndex: Math.max(0, startIndex - overscanCellsCount),
      overscanStopIndex: Math.min(cellCount - 1, stopIndex + 1)
    };
  }
}

/**
 * Higher-order component that manages lazy-loading for "infinite" data.
 * This component decorates a virtual component and just-in-time prefetches rows as a user scrolls.
 * It is intended as a convenience component; fork it if you'd like finer-grained control over data-loading.
 */
class InfiniteLoader extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this._loadMoreRowsMemoizer = createCallbackMemoizer();
    this._onRowsRendered = this._onRowsRendered.bind(this);
    this._registerChild = this._registerChild.bind(this);
  }
  resetLoadMoreRowsCache(autoReload) {
    this._loadMoreRowsMemoizer = createCallbackMemoizer();
    if (autoReload) {
      this._doStuff(this._lastRenderedStartIndex, this._lastRenderedStopIndex);
    }
  }
  render() {
    const {
      children
    } = this.props;
    return children({
      onRowsRendered: this._onRowsRendered,
      registerChild: this._registerChild
    });
  }
  _loadUnloadedRanges(unloadedRanges) {
    const {
      loadMoreRows
    } = this.props;
    unloadedRanges.forEach(unloadedRange => {
      let promise = loadMoreRows(unloadedRange);
      if (promise) {
        promise.then(() => {
          // Refresh the visible rows if any of them have just been loaded.
          // Otherwise they will remain in their unloaded visual state.
          if (isRangeVisible({
            lastRenderedStartIndex: this._lastRenderedStartIndex,
            lastRenderedStopIndex: this._lastRenderedStopIndex,
            startIndex: unloadedRange.startIndex,
            stopIndex: unloadedRange.stopIndex
          })) {
            if (this._registeredChild) {
              forceUpdateReactVirtualizedComponent(this._registeredChild, this._lastRenderedStartIndex);
            }
          }
        });
      }
    });
  }
  _onRowsRendered({
    startIndex,
    stopIndex
  }) {
    this._lastRenderedStartIndex = startIndex;
    this._lastRenderedStopIndex = stopIndex;
    this._doStuff(startIndex, stopIndex);
  }
  _doStuff(startIndex, stopIndex) {
    const {
      isRowLoaded,
      minimumBatchSize,
      rowCount,
      threshold
    } = this.props;
    const unloadedRanges = scanForUnloadedRanges({
      isRowLoaded,
      minimumBatchSize,
      rowCount,
      startIndex: Math.max(0, startIndex - threshold),
      stopIndex: Math.min(rowCount - 1, stopIndex + threshold)
    });

    // For memoize comparison
    const squashedUnloadedRanges = unloadedRanges.reduce((reduced, unloadedRange) => reduced.concat([unloadedRange.startIndex, unloadedRange.stopIndex]), []);
    this._loadMoreRowsMemoizer({
      callback: () => {
        this._loadUnloadedRanges(unloadedRanges);
      },
      indices: {
        squashedUnloadedRanges
      }
    });
  }
  _registerChild(registeredChild) {
    this._registeredChild = registeredChild;
  }
}

/**
 * Determines if the specified start/stop range is visible based on the most recently rendered range.
 */
InfiniteLoader.defaultProps = {
  minimumBatchSize: 10,
  rowCount: 0,
  threshold: 15
};
InfiniteLoader.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Function responsible for rendering a virtualized component.
   * This function should implement the following signature:
   * ({ onRowsRendered, registerChild }) => PropTypes.element
   *
   * The specified :onRowsRendered function should be passed through to the child's :onRowsRendered property.
   * The :registerChild callback should be set as the virtualized component's :ref.
   */
  children: PropTypes.func.isRequired,
  /**
   * Function responsible for tracking the loaded state of each row.
   * It should implement the following signature: ({ index: number }): boolean
   */
  isRowLoaded: PropTypes.func.isRequired,
  /**
   * Callback to be invoked when more rows must be loaded.
   * It should implement the following signature: ({ startIndex, stopIndex }): Promise
   * The returned Promise should be resolved once row data has finished loading.
   * It will be used to determine when to refresh the list with the newly-loaded data.
   * This callback may be called multiple times in reaction to a single scroll event.
   */
  loadMoreRows: PropTypes.func.isRequired,
  /**
   * Minimum number of rows to be loaded at a time.
   * This property can be used to batch requests to reduce HTTP requests.
   */
  minimumBatchSize: PropTypes.number.isRequired,
  /**
   * Number of rows in list; can be arbitrary high number if actual number is unknown.
   */
  rowCount: PropTypes.number.isRequired,
  /**
   * Threshold at which to pre-fetch data.
   * A threshold X means that data will start loading when a user scrolls within X rows.
   * This value defaults to 15.
   */
  threshold: PropTypes.number.isRequired
} : {};
function isRangeVisible({
  lastRenderedStartIndex,
  lastRenderedStopIndex,
  startIndex,
  stopIndex
}) {
  return !(startIndex > lastRenderedStopIndex || stopIndex < lastRenderedStartIndex);
}

/**
 * Returns all of the ranges within a larger range that contain unloaded rows.
 */
function scanForUnloadedRanges({
  isRowLoaded,
  minimumBatchSize,
  rowCount,
  startIndex,
  stopIndex
}) {
  const unloadedRanges = [];
  let rangeStartIndex = null;
  let rangeStopIndex = null;
  for (let index = startIndex; index <= stopIndex; index++) {
    let loaded = isRowLoaded({
      index
    });
    if (!loaded) {
      rangeStopIndex = index;
      if (rangeStartIndex === null) {
        rangeStartIndex = index;
      }
    } else if (rangeStopIndex !== null) {
      unloadedRanges.push({
        startIndex: rangeStartIndex,
        stopIndex: rangeStopIndex
      });
      rangeStartIndex = rangeStopIndex = null;
    }
  }

  // If :rangeStopIndex is not null it means we haven't ran out of unloaded rows.
  // Scan forward to try filling our :minimumBatchSize.
  if (rangeStopIndex !== null) {
    const potentialStopIndex = Math.min(Math.max(rangeStopIndex, rangeStartIndex + minimumBatchSize - 1), rowCount - 1);
    for (let index = rangeStopIndex + 1; index <= potentialStopIndex; index++) {
      if (!isRowLoaded({
        index
      })) {
        rangeStopIndex = index;
      } else {
        break;
      }
    }
    unloadedRanges.push({
      startIndex: rangeStartIndex,
      stopIndex: rangeStopIndex
    });
  }

  // Check to see if our first range ended prematurely.
  // In this case we should scan backwards to try filling our :minimumBatchSize.
  if (unloadedRanges.length) {
    const firstUnloadedRange = unloadedRanges[0];
    while (firstUnloadedRange.stopIndex - firstUnloadedRange.startIndex + 1 < minimumBatchSize && firstUnloadedRange.startIndex > 0) {
      let index = firstUnloadedRange.startIndex - 1;
      if (!isRowLoaded({
        index
      })) {
        firstUnloadedRange.startIndex = index;
      } else {
        break;
      }
    }
  }
  return unloadedRanges;
}

/**
 * Since RV components use shallowCompare we need to force a render (even though props haven't changed).
 * However InfiniteLoader may wrap a Grid or it may wrap a Table or List.
 * In the first case the built-in React forceUpdate() method is sufficient to force a re-render,
 * But in the latter cases we need to use the RV-specific forceUpdateGrid() method.
 * Else the inner Grid will not be re-rendered and visuals may be stale.
 *
 * Additionally, while a Grid is scrolling the cells can be cached,
 * So it's important to invalidate that cache by recalculating sizes
 * before forcing a rerender.
 */
function forceUpdateReactVirtualizedComponent(component, currentIndex = 0) {
  const recomputeSize = typeof component.recomputeGridSize === 'function' ? component.recomputeGridSize : component.recomputeRowHeights;
  if (recomputeSize) {
    recomputeSize.call(component, currentIndex);
  } else {
    component.forceUpdate();
  }
}

/**
 * It is inefficient to create and manage a large list of DOM elements within a scrolling container
 * if only a few of those elements are visible. The primary purpose of this component is to improve
 * performance by only rendering the DOM nodes that a user is able to see based on their current
 * scroll position.
 *
 * This component renders a virtualized list of elements with either fixed or dynamic heights.
 */

class List extends React.PureComponent {
  constructor(...args) {
    super(...args);
    this._cellRenderer = ({
      parent,
      rowIndex,
      style,
      isScrolling,
      isVisible,
      key
    }) => {
      const {
        rowRenderer
      } = this.props;

      // TRICKY The style object is sometimes cached by Grid.
      // This prevents new style objects from bypassing shallowCompare().
      // However as of React 16, style props are auto-frozen (at least in dev mode)
      // Check to make sure we can still modify the style before proceeding.
      // https://github.com/facebook/react/commit/977357765b44af8ff0cfea327866861073095c12#commitcomment-20648713
      const {
        writable
      } = Object.getOwnPropertyDescriptor(style, 'width');
      if (writable) {
        // By default, List cells should be 100% width.
        // This prevents them from flowing under a scrollbar (if present).
        style.width = '100%';
      }
      return rowRenderer({
        index: rowIndex,
        style,
        isScrolling,
        isVisible,
        key,
        parent
      });
    };
    this._setRef = ref => {
      this.Grid = ref;
    };
    this._onScroll = ({
      clientHeight,
      scrollHeight,
      scrollTop
    }) => {
      const {
        onScroll
      } = this.props;
      onScroll({
        clientHeight,
        scrollHeight,
        scrollTop
      });
    };
    this._onSectionRendered = ({
      rowOverscanStartIndex,
      rowOverscanStopIndex,
      rowStartIndex,
      rowStopIndex
    }) => {
      const {
        onRowsRendered
      } = this.props;
      onRowsRendered({
        overscanStartIndex: rowOverscanStartIndex,
        overscanStopIndex: rowOverscanStopIndex,
        startIndex: rowStartIndex,
        stopIndex: rowStopIndex
      });
    };
  }
  forceUpdateGrid() {
    if (this.Grid) {
      this.Grid.forceUpdate();
    }
  }

  /** See Grid#getOffsetForCell */
  getOffsetForRow({
    alignment,
    index
  }) {
    if (this.Grid) {
      const {
        scrollTop
      } = this.Grid.getOffsetForCell({
        alignment,
        rowIndex: index,
        columnIndex: 0
      });
      return scrollTop;
    }
    return 0;
  }

  /** CellMeasurer compatibility */
  invalidateCellSizeAfterRender({
    columnIndex,
    rowIndex
  }) {
    if (this.Grid) {
      this.Grid.invalidateCellSizeAfterRender({
        rowIndex,
        columnIndex
      });
    }
  }

  /** See Grid#measureAllCells */
  measureAllRows() {
    if (this.Grid) {
      this.Grid.measureAllCells();
    }
  }

  /** CellMeasurer compatibility */
  recomputeGridSize({
    columnIndex = 0,
    rowIndex = 0
  } = {}) {
    if (this.Grid) {
      this.Grid.recomputeGridSize({
        rowIndex,
        columnIndex
      });
    }
  }

  /** See Grid#recomputeGridSize */
  recomputeRowHeights(index = 0) {
    if (this.Grid) {
      this.Grid.recomputeGridSize({
        rowIndex: index,
        columnIndex: 0
      });
    }
  }

  /** See Grid#scrollToPosition */
  scrollToPosition(scrollTop = 0) {
    if (this.Grid) {
      this.Grid.scrollToPosition({
        scrollTop
      });
    }
  }

  /** See Grid#scrollToCell */
  scrollToRow(index = 0) {
    if (this.Grid) {
      this.Grid.scrollToCell({
        columnIndex: 0,
        rowIndex: index
      });
    }
  }
  render() {
    const {
      className,
      noRowsRenderer,
      scrollToIndex,
      width
    } = this.props;
    const classNames = cn('ReactVirtualized__List', className);
    return /*#__PURE__*/React.createElement(Grid, Object.assign({}, this.props, {
      autoContainerWidth: true,
      cellRenderer: this._cellRenderer,
      className: classNames,
      columnWidth: width,
      columnCount: 1,
      noContentRenderer: noRowsRenderer,
      onScroll: this._onScroll,
      onSectionRendered: this._onSectionRendered,
      ref: this._setRef,
      scrollToRow: scrollToIndex
    }));
  }
}
List.defaultProps = {
  autoHeight: false,
  estimatedRowSize: 30,
  onScroll: () => {},
  noRowsRenderer: () => null,
  onRowsRendered: () => {},
  overscanIndicesGetter: defaultOverscanIndicesGetter,
  overscanRowCount: 10,
  scrollToAlignment: 'auto',
  scrollToIndex: -1,
  style: {}
};
function createCellPositioner({
  cellMeasurerCache,
  columnCount,
  columnWidth,
  spacer = 0
}) {
  let columnHeights;
  initOrResetDerivedValues();
  function cellPositioner(index) {
    // Find the shortest column and use it.
    let columnIndex = 0;
    for (let i = 1; i < columnHeights.length; i++) {
      if (columnHeights[i] < columnHeights[columnIndex]) {
        columnIndex = i;
      }
    }
    const left = columnIndex * (columnWidth + spacer);
    const top = columnHeights[columnIndex] || 0;
    columnHeights[columnIndex] = top + cellMeasurerCache.getHeight(index) + spacer;
    return {
      left,
      top
    };
  }
  function initOrResetDerivedValues() {
    // Track the height of each column.
    // Layout algorithm below always inserts into the shortest column.
    columnHeights = [];
    for (let i = 0; i < columnCount; i++) {
      columnHeights[i] = 0;
    }
  }
  function reset(params) {
    columnCount = params.columnCount;
    columnWidth = params.columnWidth;
    spacer = params.spacer;
    initOrResetDerivedValues();
  }
  cellPositioner.reset = reset;
  return cellPositioner;
}

/**
 * Binary Search Bounds
 * https://github.com/mikolalysenko/binary-search-bounds
 * Mikola Lysenko
 *
 * Inlined because of Content Security Policy issue caused by the use of `new Function(...)` syntax.
 * Issue reported here: https://github.com/mikolalysenko/binary-search-bounds/issues/5
 **/

function _GEA(a, l, h, y) {
  var i = h + 1;
  while (l <= h) {
    var m = l + h >>> 1,
      x = a[m];
    if (x >= y) {
      i = m;
      h = m - 1;
    } else {
      l = m + 1;
    }
  }
  return i;
}
function _GEP(a, l, h, y, c) {
  var i = h + 1;
  while (l <= h) {
    var m = l + h >>> 1,
      x = a[m];
    if (c(x, y) >= 0) {
      i = m;
      h = m - 1;
    } else {
      l = m + 1;
    }
  }
  return i;
}
function dispatchBsearchGE(a, y, c, l, h) {
  if (typeof c === 'function') {
    return _GEP(a, l === void 0 ? 0 : l | 0, h === void 0 ? a.length - 1 : h | 0, y, c);
  } else {
    return _GEA(a, c === void 0 ? 0 : c | 0, l === void 0 ? a.length - 1 : l | 0, y);
  }
}
function _GTA(a, l, h, y) {
  var i = h + 1;
  while (l <= h) {
    var m = l + h >>> 1,
      x = a[m];
    if (x > y) {
      i = m;
      h = m - 1;
    } else {
      l = m + 1;
    }
  }
  return i;
}
function _GTP(a, l, h, y, c) {
  var i = h + 1;
  while (l <= h) {
    var m = l + h >>> 1,
      x = a[m];
    if (c(x, y) > 0) {
      i = m;
      h = m - 1;
    } else {
      l = m + 1;
    }
  }
  return i;
}
function dispatchBsearchGT(a, y, c, l, h) {
  if (typeof c === 'function') {
    return _GTP(a, l === void 0 ? 0 : l | 0, h === void 0 ? a.length - 1 : h | 0, y, c);
  } else {
    return _GTA(a, c === void 0 ? 0 : c | 0, l === void 0 ? a.length - 1 : l | 0, y);
  }
}
function _LTA(a, l, h, y) {
  var i = l - 1;
  while (l <= h) {
    var m = l + h >>> 1,
      x = a[m];
    if (x < y) {
      i = m;
      l = m + 1;
    } else {
      h = m - 1;
    }
  }
  return i;
}
function _LTP(a, l, h, y, c) {
  var i = l - 1;
  while (l <= h) {
    var m = l + h >>> 1,
      x = a[m];
    if (c(x, y) < 0) {
      i = m;
      l = m + 1;
    } else {
      h = m - 1;
    }
  }
  return i;
}
function dispatchBsearchLT(a, y, c, l, h) {
  if (typeof c === 'function') {
    return _LTP(a, l === void 0 ? 0 : l | 0, h === void 0 ? a.length - 1 : h | 0, y, c);
  } else {
    return _LTA(a, c === void 0 ? 0 : c | 0, l === void 0 ? a.length - 1 : l | 0, y);
  }
}
function _LEA(a, l, h, y) {
  var i = l - 1;
  while (l <= h) {
    var m = l + h >>> 1,
      x = a[m];
    if (x <= y) {
      i = m;
      l = m + 1;
    } else {
      h = m - 1;
    }
  }
  return i;
}
function _LEP(a, l, h, y, c) {
  var i = l - 1;
  while (l <= h) {
    var m = l + h >>> 1,
      x = a[m];
    if (c(x, y) <= 0) {
      i = m;
      l = m + 1;
    } else {
      h = m - 1;
    }
  }
  return i;
}
function dispatchBsearchLE(a, y, c, l, h) {
  if (typeof c === 'function') {
    return _LEP(a, l === void 0 ? 0 : l | 0, h === void 0 ? a.length - 1 : h | 0, y, c);
  } else {
    return _LEA(a, c === void 0 ? 0 : c | 0, l === void 0 ? a.length - 1 : l | 0, y);
  }
}
function _EQA(a, l, h, y) {
  while (l <= h) {
    var m = l + h >>> 1,
      x = a[m];
    if (x === y) {
      return m;
    } else if (x <= y) {
      l = m + 1;
    } else {
      h = m - 1;
    }
  }
  return -1;
}
function _EQP(a, l, h, y, c) {
  while (l <= h) {
    var m = l + h >>> 1,
      x = a[m];
    var p = c(x, y);
    if (p === 0) {
      return m;
    } else if (p <= 0) {
      l = m + 1;
    } else {
      h = m - 1;
    }
  }
  return -1;
}
function dispatchBsearchEQ(a, y, c, l, h) {
  if (typeof c === 'function') {
    return _EQP(a, l === void 0 ? 0 : l | 0, h === void 0 ? a.length - 1 : h | 0, y, c);
  } else {
    return _EQA(a, c === void 0 ? 0 : c | 0, l === void 0 ? a.length - 1 : l | 0, y);
  }
}
var bounds = {
  ge: dispatchBsearchGE,
  gt: dispatchBsearchGT,
  lt: dispatchBsearchLT,
  le: dispatchBsearchLE,
  eq: dispatchBsearchEQ
};

/**
 * Binary Search Bounds
 * https://github.com/mikolalysenko/interval-tree-1d
 * Mikola Lysenko
 *
 * Inlined because of Content Security Policy issue caused by the use of `new Function(...)` syntax in an upstream dependency.
 * Issue reported here: https://github.com/mikolalysenko/binary-search-bounds/issues/5
 **/
var NOT_FOUND = 0;
var SUCCESS = 1;
var EMPTY = 2;
function IntervalTreeNode(mid, left, right, leftPoints, rightPoints) {
  this.mid = mid;
  this.left = left;
  this.right = right;
  this.leftPoints = leftPoints;
  this.rightPoints = rightPoints;
  this.count = (left ? left.count : 0) + (right ? right.count : 0) + leftPoints.length;
}
var proto = IntervalTreeNode.prototype;
function copy(a, b) {
  a.mid = b.mid;
  a.left = b.left;
  a.right = b.right;
  a.leftPoints = b.leftPoints;
  a.rightPoints = b.rightPoints;
  a.count = b.count;
}
function rebuild(node, intervals) {
  var ntree = createIntervalTree(intervals);
  node.mid = ntree.mid;
  node.left = ntree.left;
  node.right = ntree.right;
  node.leftPoints = ntree.leftPoints;
  node.rightPoints = ntree.rightPoints;
  node.count = ntree.count;
}
function rebuildWithInterval(node, interval) {
  var intervals = node.intervals([]);
  intervals.push(interval);
  rebuild(node, intervals);
}
function rebuildWithoutInterval(node, interval) {
  var intervals = node.intervals([]);
  var idx = intervals.indexOf(interval);
  if (idx < 0) {
    return NOT_FOUND;
  }
  intervals.splice(idx, 1);
  rebuild(node, intervals);
  return SUCCESS;
}
proto.intervals = function (result) {
  result.push.apply(result, this.leftPoints);
  if (this.left) {
    this.left.intervals(result);
  }
  if (this.right) {
    this.right.intervals(result);
  }
  return result;
};
proto.insert = function (interval) {
  var weight = this.count - this.leftPoints.length;
  this.count += 1;
  if (interval[1] < this.mid) {
    if (this.left) {
      if (4 * (this.left.count + 1) > 3 * (weight + 1)) {
        rebuildWithInterval(this, interval);
      } else {
        this.left.insert(interval);
      }
    } else {
      this.left = createIntervalTree([interval]);
    }
  } else if (interval[0] > this.mid) {
    if (this.right) {
      if (4 * (this.right.count + 1) > 3 * (weight + 1)) {
        rebuildWithInterval(this, interval);
      } else {
        this.right.insert(interval);
      }
    } else {
      this.right = createIntervalTree([interval]);
    }
  } else {
    var l = bounds.ge(this.leftPoints, interval, compareBegin);
    var r = bounds.ge(this.rightPoints, interval, compareEnd);
    this.leftPoints.splice(l, 0, interval);
    this.rightPoints.splice(r, 0, interval);
  }
};
proto.remove = function (interval) {
  var weight = this.count - this.leftPoints;
  if (interval[1] < this.mid) {
    if (!this.left) {
      return NOT_FOUND;
    }
    var rw = this.right ? this.right.count : 0;
    if (4 * rw > 3 * (weight - 1)) {
      return rebuildWithoutInterval(this, interval);
    }
    var r = this.left.remove(interval);
    if (r === EMPTY) {
      this.left = null;
      this.count -= 1;
      return SUCCESS;
    } else if (r === SUCCESS) {
      this.count -= 1;
    }
    return r;
  } else if (interval[0] > this.mid) {
    if (!this.right) {
      return NOT_FOUND;
    }
    var lw = this.left ? this.left.count : 0;
    if (4 * lw > 3 * (weight - 1)) {
      return rebuildWithoutInterval(this, interval);
    }
    var r = this.right.remove(interval);
    if (r === EMPTY) {
      this.right = null;
      this.count -= 1;
      return SUCCESS;
    } else if (r === SUCCESS) {
      this.count -= 1;
    }
    return r;
  } else {
    if (this.count === 1) {
      if (this.leftPoints[0] === interval) {
        return EMPTY;
      } else {
        return NOT_FOUND;
      }
    }
    if (this.leftPoints.length === 1 && this.leftPoints[0] === interval) {
      if (this.left && this.right) {
        var p = this;
        var n = this.left;
        while (n.right) {
          p = n;
          n = n.right;
        }
        if (p === this) {
          n.right = this.right;
        } else {
          var l = this.left;
          var r = this.right;
          p.count -= n.count;
          p.right = n.left;
          n.left = l;
          n.right = r;
        }
        copy(this, n);
        this.count = (this.left ? this.left.count : 0) + (this.right ? this.right.count : 0) + this.leftPoints.length;
      } else if (this.left) {
        copy(this, this.left);
      } else {
        copy(this, this.right);
      }
      return SUCCESS;
    }
    for (var l = bounds.ge(this.leftPoints, interval, compareBegin); l < this.leftPoints.length; ++l) {
      if (this.leftPoints[l][0] !== interval[0]) {
        break;
      }
      if (this.leftPoints[l] === interval) {
        this.count -= 1;
        this.leftPoints.splice(l, 1);
        for (var r = bounds.ge(this.rightPoints, interval, compareEnd); r < this.rightPoints.length; ++r) {
          if (this.rightPoints[r][1] !== interval[1]) {
            break;
          } else if (this.rightPoints[r] === interval) {
            this.rightPoints.splice(r, 1);
            return SUCCESS;
          }
        }
      }
    }
    return NOT_FOUND;
  }
};
function reportLeftRange(arr, hi, cb) {
  for (var i = 0; i < arr.length && arr[i][0] <= hi; ++i) {
    var r = cb(arr[i]);
    if (r) {
      return r;
    }
  }
}
function reportRightRange(arr, lo, cb) {
  for (var i = arr.length - 1; i >= 0 && arr[i][1] >= lo; --i) {
    var r = cb(arr[i]);
    if (r) {
      return r;
    }
  }
}
function reportRange(arr, cb) {
  for (var i = 0; i < arr.length; ++i) {
    var r = cb(arr[i]);
    if (r) {
      return r;
    }
  }
}
proto.queryPoint = function (x, cb) {
  if (x < this.mid) {
    if (this.left) {
      var r = this.left.queryPoint(x, cb);
      if (r) {
        return r;
      }
    }
    return reportLeftRange(this.leftPoints, x, cb);
  } else if (x > this.mid) {
    if (this.right) {
      var r = this.right.queryPoint(x, cb);
      if (r) {
        return r;
      }
    }
    return reportRightRange(this.rightPoints, x, cb);
  } else {
    return reportRange(this.leftPoints, cb);
  }
};
proto.queryInterval = function (lo, hi, cb) {
  if (lo < this.mid && this.left) {
    var r = this.left.queryInterval(lo, hi, cb);
    if (r) {
      return r;
    }
  }
  if (hi > this.mid && this.right) {
    var r = this.right.queryInterval(lo, hi, cb);
    if (r) {
      return r;
    }
  }
  if (hi < this.mid) {
    return reportLeftRange(this.leftPoints, hi, cb);
  } else if (lo > this.mid) {
    return reportRightRange(this.rightPoints, lo, cb);
  } else {
    return reportRange(this.leftPoints, cb);
  }
};
function compareNumbers(a, b) {
  return a - b;
}
function compareBegin(a, b) {
  var d = a[0] - b[0];
  if (d) {
    return d;
  }
  return a[1] - b[1];
}
function compareEnd(a, b) {
  var d = a[1] - b[1];
  if (d) {
    return d;
  }
  return a[0] - b[0];
}
function createIntervalTree(intervals) {
  if (intervals.length === 0) {
    return null;
  }
  var pts = [];
  for (var i = 0; i < intervals.length; ++i) {
    pts.push(intervals[i][0], intervals[i][1]);
  }
  pts.sort(compareNumbers);
  var mid = pts[pts.length >> 1];
  var leftIntervals = [];
  var rightIntervals = [];
  var centerIntervals = [];
  for (var i = 0; i < intervals.length; ++i) {
    var s = intervals[i];
    if (s[1] < mid) {
      leftIntervals.push(s);
    } else if (mid < s[0]) {
      rightIntervals.push(s);
    } else {
      centerIntervals.push(s);
    }
  }

  //Split center intervals
  var leftPoints = centerIntervals;
  var rightPoints = centerIntervals.slice();
  leftPoints.sort(compareBegin);
  rightPoints.sort(compareEnd);
  return new IntervalTreeNode(mid, createIntervalTree(leftIntervals), createIntervalTree(rightIntervals), leftPoints, rightPoints);
}

//User friendly wrapper that makes it possible to support empty trees
function IntervalTree(root) {
  this.root = root;
}
var tproto = IntervalTree.prototype;
tproto.insert = function (interval) {
  if (this.root) {
    this.root.insert(interval);
  } else {
    this.root = new IntervalTreeNode(interval[0], null, null, [interval], [interval]);
  }
};
tproto.remove = function (interval) {
  if (this.root) {
    var r = this.root.remove(interval);
    if (r === EMPTY) {
      this.root = null;
    }
    return r !== NOT_FOUND;
  }
  return false;
};
tproto.queryPoint = function (p, cb) {
  if (this.root) {
    return this.root.queryPoint(p, cb);
  }
};
tproto.queryInterval = function (lo, hi, cb) {
  if (lo <= hi && this.root) {
    return this.root.queryInterval(lo, hi, cb);
  }
};
Object.defineProperty(tproto, 'count', {
  get: function () {
    if (this.root) {
      return this.root.count;
    }
    return 0;
  }
});
Object.defineProperty(tproto, 'intervals', {
  get: function () {
    if (this.root) {
      return this.root.intervals([]);
    }
    return [];
  }
});
function createWrapper(intervals) {
  if (!intervals || intervals.length === 0) {
    return new IntervalTree(null);
  }
  return new IntervalTree(createIntervalTree(intervals));
}

// Position cache requirements:
//   O(log(n)) lookup of cells to render for a given viewport size
//   O(1) lookup of shortest measured column (so we know when to enter phase 1)
class PositionCache {
  constructor() {
    this._columnSizeMap = {};
    this._intervalTree = createWrapper();
    this._leftMap = {};
  } // Tracks the height of each column
  // Store tops and bottoms of each cell for fast intersection lookup.
  // Maps cell index to x coordinates for quick lookup.
  estimateTotalHeight(cellCount, columnCount, defaultCellHeight) {
    const unmeasuredCellCount = cellCount - this.count;
    return this.tallestColumnSize + Math.ceil(unmeasuredCellCount / columnCount) * defaultCellHeight;
  }

  // Render all cells visible within the viewport range defined.
  range(scrollTop, clientHeight, renderCallback) {
    this._intervalTree.queryInterval(scrollTop, scrollTop + clientHeight, ([top, _, index]) => renderCallback(index, this._leftMap[index], top));
  }
  setPosition(index, left, top, height) {
    this._intervalTree.insert([top, top + height, index]);
    this._leftMap[index] = left;
    const columnSizeMap = this._columnSizeMap;
    const columnHeight = columnSizeMap[left];
    if (columnHeight === undefined) {
      columnSizeMap[left] = top + height;
    } else {
      columnSizeMap[left] = Math.max(columnHeight, top + height);
    }
  }
  get count() {
    return this._intervalTree.count;
  }
  get shortestColumnSize() {
    const columnSizeMap = this._columnSizeMap;
    let size = 0;
    for (let i in columnSizeMap) {
      let height = columnSizeMap[i];
      size = size === 0 ? height : Math.min(size, height);
    }
    return size;
  }
  get tallestColumnSize() {
    const columnSizeMap = this._columnSizeMap;
    let size = 0;
    for (let i in columnSizeMap) {
      let height = columnSizeMap[i];
      size = Math.max(size, height);
    }
    return size;
  }
}
const emptyObject = {};

/**
 * Specifies the number of miliseconds during which to disable pointer events while a scroll is in progress.
 * This improves performance and makes scrolling smoother.
 */
const DEFAULT_SCROLLING_RESET_TIME_INTERVAL = 150;

/**
 * This component efficiently displays arbitrarily positioned cells using windowing techniques.
 * Cell position is determined by an injected `cellPositioner` property.
 * Windowing is vertical; this component does not support horizontal scrolling.
 *
 * Rendering occurs in two phases:
 * 1) First pass uses estimated cell sizes (provided by the cache) to determine how many cells to measure in a batch.
 *    Batch size is chosen using a fast, naive layout algorithm that stacks images in order until the viewport has been filled.
 *    After measurement is complete (componentDidMount or componentDidUpdate) this component evaluates positioned cells
 *    in order to determine if another measurement pass is required (eg if actual cell sizes were less than estimated sizes).
 *    All measurements are permanently cached (keyed by `keyMapper`) for performance purposes.
 * 2) Second pass uses the external `cellPositioner` to layout cells.
 *    At this time the positioner has access to cached size measurements for all cells.
 *    The positions it returns are cached by Masonry for fast access later.
 *    Phase one is repeated if the user scrolls beyond the current layout's bounds.
 *    If the layout is invalidated due to eg a resize, cached positions can be cleared using `recomputeCellPositions()`.
 *
 * Animation constraints:
 *   Simple animations are supported (eg translate/slide into place on initial reveal).
 *   More complex animations are not (eg flying from one position to another on resize).
 *
 * Layout constraints:
 *   This component supports multi-column layout.
 *   The height of each item may vary.
 *   The width of each item must not exceed the width of the column it is "in".
 *   The left position of all items within a column must align.
 *   (Items may not span multiple columns.)
 */
class Masonry extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this._invalidateOnUpdateStartIndex = null;
    this._invalidateOnUpdateStopIndex = null;
    this._positionCache = new PositionCache();
    this._startIndex = null;
    this._startIndexMemoized = null;
    this._stopIndex = null;
    this._stopIndexMemoized = null;
    this.state = {
      isScrolling: false,
      scrollTop: 0
    };
    this._debounceResetIsScrollingCallback = this._debounceResetIsScrollingCallback.bind(this);
    this._setScrollingContainerRef = this._setScrollingContainerRef.bind(this);
    this._onScroll = this._onScroll.bind(this);
  }
  clearCellPositions() {
    this._positionCache = new PositionCache();
    this.forceUpdate();
  }

  // HACK This method signature was intended for Grid
  invalidateCellSizeAfterRender({
    rowIndex: index
  }) {
    if (this._invalidateOnUpdateStartIndex === null) {
      this._invalidateOnUpdateStartIndex = index;
      this._invalidateOnUpdateStopIndex = index;
    } else {
      this._invalidateOnUpdateStartIndex = Math.min(this._invalidateOnUpdateStartIndex, index);
      this._invalidateOnUpdateStopIndex = Math.max(this._invalidateOnUpdateStopIndex, index);
    }
  }
  recomputeCellPositions() {
    const stopIndex = this._positionCache.count - 1;
    this._positionCache = new PositionCache();
    this._populatePositionCache(0, stopIndex);
    this.forceUpdate();
  }
  componentDidMount() {
    this._checkInvalidateOnUpdate();
    this._invokeOnScrollCallback();
    this._invokeOnCellsRenderedCallback();
  }
  componentDidUpdate() {
    this._checkInvalidateOnUpdate();
    this._invokeOnScrollCallback();
    this._invokeOnCellsRenderedCallback();
  }
  componentWillUnmount() {
    if (this._debounceResetIsScrollingId) {
      cancelAnimationTimeout(this._debounceResetIsScrollingId);
    }
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      scrollTop
    } = this.props;
    if (scrollTop !== nextProps.scrollTop) {
      this._debounceResetIsScrolling();
      this.setState({
        isScrolling: true,
        scrollTop: nextProps.scrollTop
      });
    }
  }
  render() {
    const {
      autoHeight,
      cellCount,
      cellMeasurerCache,
      cellRenderer,
      className,
      height,
      id,
      keyMapper,
      overscanByPixels,
      role,
      style,
      tabIndex,
      width
    } = this.props;
    const {
      isScrolling,
      scrollTop
    } = this.state;
    const children = [];
    const estimateTotalHeight = this._getEstimatedTotalHeight();
    const shortestColumnSize = this._positionCache.shortestColumnSize;
    const measuredCellCount = this._positionCache.count;
    let startIndex = 0;
    let stopIndex;
    this._positionCache.range(Math.max(0, scrollTop - overscanByPixels), height + overscanByPixels * 2, (index, left, top) => {
      if (typeof stopIndex === 'undefined') {
        startIndex = index;
        stopIndex = index;
      } else {
        startIndex = Math.min(startIndex, index);
        stopIndex = Math.max(stopIndex, index);
      }
      children.push(cellRenderer({
        index,
        isScrolling,
        key: keyMapper(index),
        parent: this,
        style: {
          height: cellMeasurerCache.getHeight(index),
          left,
          position: 'absolute',
          top,
          width: cellMeasurerCache.getWidth(index)
        }
      }));
    });

    // We need to measure additional cells for this layout
    if (shortestColumnSize < scrollTop + height + overscanByPixels && measuredCellCount < cellCount) {
      const batchSize = Math.min(cellCount - measuredCellCount, Math.ceil((scrollTop + height + overscanByPixels - shortestColumnSize) / cellMeasurerCache.defaultHeight * width / cellMeasurerCache.defaultWidth));
      for (let index = measuredCellCount; index < measuredCellCount + batchSize; index++) {
        stopIndex = index;
        children.push(cellRenderer({
          index: index,
          isScrolling,
          key: keyMapper(index),
          parent: this,
          style: {
            width: cellMeasurerCache.getWidth(index)
          }
        }));
      }
    }
    this._startIndex = startIndex;
    this._stopIndex = stopIndex;
    return /*#__PURE__*/React__default.createElement("div", {
      ref: this._setScrollingContainerRef,
      "aria-label": this.props['aria-label'],
      className: cn('ReactVirtualized__Masonry', className),
      id: id,
      onScroll: this._onScroll,
      role: role,
      style: Object.assign({
        boxSizing: 'border-box',
        direction: 'ltr',
        height: autoHeight ? 'auto' : height,
        overflowX: 'hidden',
        overflowY: estimateTotalHeight < height ? 'hidden' : 'auto',
        position: 'relative',
        width,
        WebkitOverflowScrolling: 'touch',
        willChange: 'transform'
      }, style),
      tabIndex: tabIndex
    }, /*#__PURE__*/React__default.createElement("div", {
      className: "ReactVirtualized__Masonry__innerScrollContainer",
      style: {
        width: '100%',
        height: estimateTotalHeight,
        maxWidth: '100%',
        maxHeight: estimateTotalHeight,
        overflow: 'hidden',
        pointerEvents: isScrolling ? 'none' : '',
        position: 'relative'
      }
    }, children));
  }
  _checkInvalidateOnUpdate() {
    if (typeof this._invalidateOnUpdateStartIndex === 'number') {
      const startIndex = this._invalidateOnUpdateStartIndex;
      const stopIndex = this._invalidateOnUpdateStopIndex;
      this._invalidateOnUpdateStartIndex = null;
      this._invalidateOnUpdateStopIndex = null;

      // Query external layout logic for position of newly-measured cells
      this._populatePositionCache(startIndex, stopIndex);
      this.forceUpdate();
    }
  }
  _debounceResetIsScrolling() {
    const {
      scrollingResetTimeInterval
    } = this.props;
    if (this._debounceResetIsScrollingId) {
      cancelAnimationTimeout(this._debounceResetIsScrollingId);
    }
    this._debounceResetIsScrollingId = requestAnimationTimeout(this._debounceResetIsScrollingCallback, scrollingResetTimeInterval);
  }
  _debounceResetIsScrollingCallback() {
    this.setState({
      isScrolling: false
    });
  }
  _getEstimatedTotalHeight() {
    const {
      cellCount,
      cellMeasurerCache,
      width
    } = this.props;
    const estimatedColumnCount = Math.max(1, Math.floor(width / cellMeasurerCache.defaultWidth));
    return this._positionCache.estimateTotalHeight(cellCount, estimatedColumnCount, cellMeasurerCache.defaultHeight);
  }
  _invokeOnScrollCallback() {
    const {
      height,
      onScroll
    } = this.props;
    const {
      scrollTop
    } = this.state;
    if (this._onScrollMemoized !== scrollTop) {
      onScroll({
        clientHeight: height,
        scrollHeight: this._getEstimatedTotalHeight(),
        scrollTop
      });
      this._onScrollMemoized = scrollTop;
    }
  }
  _invokeOnCellsRenderedCallback() {
    if (this._startIndexMemoized !== this._startIndex || this._stopIndexMemoized !== this._stopIndex) {
      const {
        onCellsRendered
      } = this.props;
      onCellsRendered({
        startIndex: this._startIndex,
        stopIndex: this._stopIndex
      });
      this._startIndexMemoized = this._startIndex;
      this._stopIndexMemoized = this._stopIndex;
    }
  }
  _populatePositionCache(startIndex, stopIndex) {
    const {
      cellMeasurerCache,
      cellPositioner
    } = this.props;
    for (let index = startIndex; index <= stopIndex; index++) {
      const {
        left,
        top
      } = cellPositioner(index);
      this._positionCache.setPosition(index, left, top, cellMeasurerCache.getHeight(index));
    }
  }
  _setScrollingContainerRef(ref) {
    this._scrollingContainer = ref;
  }
  _onScroll(event) {
    const {
      height
    } = this.props;
    const eventScrollTop = event.target.scrollTop;

    // When this component is shrunk drastically, React dispatches a series of back-to-back scroll events,
    // Gradually converging on a scrollTop that is within the bounds of the new, smaller height.
    // This causes a series of rapid renders that is slow for long lists.
    // We can avoid that by doing some simple bounds checking to ensure that scroll offsets never exceed their bounds.
    const scrollTop = Math.min(Math.max(0, this._getEstimatedTotalHeight() - height), eventScrollTop);

    // On iOS, we can arrive at negative offsets by swiping past the start or end.
    // Avoid re-rendering in this case as it can cause problems; see #532 for more.
    if (eventScrollTop !== scrollTop) {
      return;
    }

    // Prevent pointer events from interrupting a smooth scroll
    this._debounceResetIsScrolling();

    // Certain devices (like Apple touchpad) rapid-fire duplicate events.
    // Don't force a re-render if this is the case.
    // The mouse may move faster then the animation frame does.
    // Use requestAnimationFrame to avoid over-updating.
    if (this.state.scrollTop !== scrollTop) {
      this.setState({
        isScrolling: true,
        scrollTop
      });
    }
  }
}
Masonry.defaultProps = {
  autoHeight: false,
  keyMapper: identity,
  onCellsRendered: noop,
  onScroll: noop,
  overscanByPixels: 20,
  role: 'grid',
  scrollingResetTimeInterval: DEFAULT_SCROLLING_RESET_TIME_INTERVAL,
  style: emptyObject,
  tabIndex: 0
};
function identity(value) {
  return value;
}
function noop() {}

/**
 * Caches measurements for a given cell.
 */
class CellMeasurerCacheDecorator {
  constructor(params = {}) {
    this.columnWidth = ({
      index
    }) => {
      this._cellMeasurerCache.columnWidth({
        index: index + this._columnIndexOffset
      });
    };
    this.rowHeight = ({
      index
    }) => {
      this._cellMeasurerCache.rowHeight({
        index: index + this._rowIndexOffset
      });
    };
    const {
      cellMeasurerCache,
      columnIndexOffset = 0,
      rowIndexOffset = 0
    } = params;
    this._cellMeasurerCache = cellMeasurerCache;
    this._columnIndexOffset = columnIndexOffset;
    this._rowIndexOffset = rowIndexOffset;
  }
  clear(rowIndex, columnIndex) {
    this._cellMeasurerCache.clear(rowIndex + this._rowIndexOffset, columnIndex + this._columnIndexOffset);
  }
  clearAll() {
    this._cellMeasurerCache.clearAll();
  }
  get defaultHeight() {
    return this._cellMeasurerCache.defaultHeight;
  }
  get defaultWidth() {
    return this._cellMeasurerCache.defaultWidth;
  }
  hasFixedHeight() {
    return this._cellMeasurerCache.hasFixedHeight();
  }
  hasFixedWidth() {
    return this._cellMeasurerCache.hasFixedWidth();
  }
  getHeight(rowIndex, columnIndex = 0) {
    return this._cellMeasurerCache.getHeight(rowIndex + this._rowIndexOffset, columnIndex + this._columnIndexOffset);
  }
  getWidth(rowIndex, columnIndex = 0) {
    return this._cellMeasurerCache.getWidth(rowIndex + this._rowIndexOffset, columnIndex + this._columnIndexOffset);
  }
  has(rowIndex, columnIndex = 0) {
    return this._cellMeasurerCache.has(rowIndex + this._rowIndexOffset, columnIndex + this._columnIndexOffset);
  }
  set(rowIndex, columnIndex, width, height) {
    this._cellMeasurerCache.set(rowIndex + this._rowIndexOffset, columnIndex + this._columnIndexOffset, width, height);
  }
}
const SCROLLBAR_SIZE_BUFFER = 20;

/**
 * Renders 1, 2, or 4 Grids depending on configuration.
 * A main (body) Grid will always be rendered.
 * Optionally, 1-2 Grids for sticky header rows will also be rendered.
 * If no sticky columns, only 1 sticky header Grid will be rendered.
 * If sticky columns, 2 sticky header Grids will be rendered.
 */
class MultiGrid extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      scrollLeft: 0,
      scrollTop: 0,
      scrollbarSize: 0,
      showHorizontalScrollbar: false,
      showVerticalScrollbar: false
    };
    this._deferredInvalidateColumnIndex = null;
    this._deferredInvalidateRowIndex = null;
    this._bottomLeftGridRef = this._bottomLeftGridRef.bind(this);
    this._bottomRightGridRef = this._bottomRightGridRef.bind(this);
    this._cellRendererBottomLeftGrid = this._cellRendererBottomLeftGrid.bind(this);
    this._cellRendererBottomRightGrid = this._cellRendererBottomRightGrid.bind(this);
    this._cellRendererTopRightGrid = this._cellRendererTopRightGrid.bind(this);
    this._columnWidthRightGrid = this._columnWidthRightGrid.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onScrollbarPresenceChange = this._onScrollbarPresenceChange.bind(this);
    this._onScrollLeft = this._onScrollLeft.bind(this);
    this._onScrollTop = this._onScrollTop.bind(this);
    this._rowHeightBottomGrid = this._rowHeightBottomGrid.bind(this);
    this._topLeftGridRef = this._topLeftGridRef.bind(this);
    this._topRightGridRef = this._topRightGridRef.bind(this);
  }
  forceUpdateGrids() {
    this._bottomLeftGrid && this._bottomLeftGrid.forceUpdate();
    this._bottomRightGrid && this._bottomRightGrid.forceUpdate();
    this._topLeftGrid && this._topLeftGrid.forceUpdate();
    this._topRightGrid && this._topRightGrid.forceUpdate();
  }

  /** See Grid#invalidateCellSizeAfterRender */
  invalidateCellSizeAfterRender({
    columnIndex = 0,
    rowIndex = 0
  } = {}) {
    this._deferredInvalidateColumnIndex = typeof this._deferredInvalidateColumnIndex === 'number' ? Math.min(this._deferredInvalidateColumnIndex, columnIndex) : columnIndex;
    this._deferredInvalidateRowIndex = typeof this._deferredInvalidateRowIndex === 'number' ? Math.min(this._deferredInvalidateRowIndex, rowIndex) : rowIndex;
  }

  /** See Grid#measureAllCells */
  measureAllCells() {
    this._bottomLeftGrid && this._bottomLeftGrid.measureAllCells();
    this._bottomRightGrid && this._bottomRightGrid.measureAllCells();
    this._topLeftGrid && this._topLeftGrid.measureAllCells();
    this._topRightGrid && this._topRightGrid.measureAllCells();
  }

  /** See Grid#recomputeGridSize */
  recomputeGridSize({
    columnIndex = 0,
    rowIndex = 0
  } = {}) {
    const {
      fixedColumnCount,
      fixedRowCount
    } = this.props;
    const adjustedColumnIndex = Math.max(0, columnIndex - fixedColumnCount);
    const adjustedRowIndex = Math.max(0, rowIndex - fixedRowCount);
    this._bottomLeftGrid && this._bottomLeftGrid.recomputeGridSize({
      columnIndex,
      rowIndex: adjustedRowIndex
    });
    this._bottomRightGrid && this._bottomRightGrid.recomputeGridSize({
      columnIndex: adjustedColumnIndex,
      rowIndex: adjustedRowIndex
    });
    this._topLeftGrid && this._topLeftGrid.recomputeGridSize({
      columnIndex,
      rowIndex
    });
    this._topRightGrid && this._topRightGrid.recomputeGridSize({
      columnIndex: adjustedColumnIndex,
      rowIndex
    });
    this._leftGridWidth = null;
    this._topGridHeight = null;
    this._maybeCalculateCachedStyles(null, this.props, null, this.state);
  }
  componentDidMount() {
    const {
      scrollLeft,
      scrollTop
    } = this.props;
    if (scrollLeft > 0 || scrollTop > 0) {
      const newState = {};
      if (scrollLeft > 0) {
        newState.scrollLeft = scrollLeft;
      }
      if (scrollTop > 0) {
        newState.scrollTop = scrollTop;
      }
      this.setState(newState);
    }
    this._handleInvalidatedGridSize();
  }
  componentDidUpdate() {
    this._handleInvalidatedGridSize();
  }
  UNSAFE_componentWillMount() {
    const {
      deferredMeasurementCache,
      fixedColumnCount,
      fixedRowCount
    } = this.props;
    this._maybeCalculateCachedStyles(null, this.props, null, this.state);
    if (deferredMeasurementCache) {
      this._deferredMeasurementCacheBottomLeftGrid = fixedRowCount > 0 ? new CellMeasurerCacheDecorator({
        cellMeasurerCache: deferredMeasurementCache,
        columnIndexOffset: 0,
        rowIndexOffset: fixedRowCount
      }) : deferredMeasurementCache;
      this._deferredMeasurementCacheBottomRightGrid = fixedColumnCount > 0 || fixedRowCount > 0 ? new CellMeasurerCacheDecorator({
        cellMeasurerCache: deferredMeasurementCache,
        columnIndexOffset: fixedColumnCount,
        rowIndexOffset: fixedRowCount
      }) : deferredMeasurementCache;
      this._deferredMeasurementCacheTopRightGrid = fixedColumnCount > 0 ? new CellMeasurerCacheDecorator({
        cellMeasurerCache: deferredMeasurementCache,
        columnIndexOffset: fixedColumnCount,
        rowIndexOffset: 0
      }) : deferredMeasurementCache;
    }
  }
  UNSAFE_componentWillReceiveProps(nextProps, nextState) {
    const {
      columnWidth,
      fixedColumnCount,
      fixedRowCount,
      rowHeight
    } = this.props;
    if (columnWidth !== nextProps.columnWidth || fixedColumnCount !== nextProps.fixedColumnCount) {
      this._leftGridWidth = null;
    }
    if (fixedRowCount !== nextProps.fixedRowCount || rowHeight !== nextProps.rowHeight) {
      this._topGridHeight = null;
    }
    if (nextProps.scrollLeft !== this.props.scrollLeft || nextProps.scrollTop !== this.props.scrollTop) {
      const newState = {};
      if (nextProps.scrollLeft != null && nextProps.scrollLeft >= 0) {
        newState.scrollLeft = nextProps.scrollLeft;
      }
      if (nextProps.scrollTop != null && nextProps.scrollTop >= 0) {
        newState.scrollTop = nextProps.scrollTop;
      }
      this.setState(newState);
    }
    this._maybeCalculateCachedStyles(this.props, nextProps, this.state, nextState);
  }
  render() {
    const _this$props2 = this.props,
      {
        onScroll,
        onSectionRendered,
        // eslint-disable-line no-unused-vars
        scrollToColumn,
        // eslint-disable-line no-unused-vars
        scrollToRow
      } = _this$props2,
      rest = _objectWithoutPropertiesLoose(_this$props2, _excluded2);

    // Don't render any of our Grids if there are no cells.
    // This mirrors what Grid does,
    // And prevents us from recording inaccurage measurements when used with CellMeasurer.
    if (this.props.width === 0 || this.props.height === 0) {
      return null;
    }

    // scrollTop and scrollLeft props are explicitly filtered out and ignored

    const {
      scrollLeft,
      scrollTop
    } = this.state;
    return /*#__PURE__*/React__default.createElement("div", {
      style: this._containerOuterStyle
    }, /*#__PURE__*/React__default.createElement("div", {
      style: this._containerTopStyle
    }, this._renderTopLeftGrid(rest), this._renderTopRightGrid(Object.assign({}, rest, {
      onScroll,
      scrollLeft
    }))), /*#__PURE__*/React__default.createElement("div", {
      style: this._containerBottomStyle
    }, this._renderBottomLeftGrid(Object.assign({}, rest, {
      onScroll,
      scrollTop
    })), this._renderBottomRightGrid(Object.assign({}, rest, {
      onScroll,
      onSectionRendered,
      scrollLeft,
      scrollToColumn,
      scrollToRow,
      scrollTop
    }))));
  }
  _bottomLeftGridRef(ref) {
    this._bottomLeftGrid = ref;
  }
  _bottomRightGridRef(ref) {
    this._bottomRightGrid = ref;
  }
  _cellRendererBottomLeftGrid(_ref2) {
    let {
        rowIndex
      } = _ref2,
      rest = _objectWithoutPropertiesLoose(_ref2, _excluded3);
    const {
      cellRenderer,
      fixedRowCount,
      rowCount
    } = this.props;
    if (rowIndex === rowCount - fixedRowCount) {
      return /*#__PURE__*/React__default.createElement("div", {
        key: rest.key,
        style: Object.assign({}, rest.style, {
          height: SCROLLBAR_SIZE_BUFFER
        })
      });
    } else {
      return cellRenderer(Object.assign({}, rest, {
        parent: this,
        rowIndex: rowIndex + fixedRowCount
      }));
    }
  }
  _cellRendererBottomRightGrid(_ref3) {
    let {
        columnIndex,
        rowIndex
      } = _ref3,
      rest = _objectWithoutPropertiesLoose(_ref3, _excluded4);
    const {
      cellRenderer,
      fixedColumnCount,
      fixedRowCount
    } = this.props;
    return cellRenderer(Object.assign({}, rest, {
      columnIndex: columnIndex + fixedColumnCount,
      parent: this,
      rowIndex: rowIndex + fixedRowCount
    }));
  }
  _cellRendererTopRightGrid(_ref4) {
    let {
        columnIndex
      } = _ref4,
      rest = _objectWithoutPropertiesLoose(_ref4, _excluded5);
    const {
      cellRenderer,
      columnCount,
      fixedColumnCount
    } = this.props;
    if (columnIndex === columnCount - fixedColumnCount) {
      return /*#__PURE__*/React__default.createElement("div", {
        key: rest.key,
        style: Object.assign({}, rest.style, {
          width: SCROLLBAR_SIZE_BUFFER
        })
      });
    } else {
      return cellRenderer(Object.assign({}, rest, {
        columnIndex: columnIndex + fixedColumnCount,
        parent: this
      }));
    }
  }
  _columnWidthRightGrid({
    index
  }) {
    const {
      columnCount,
      fixedColumnCount,
      columnWidth
    } = this.props;
    const {
      scrollbarSize,
      showHorizontalScrollbar
    } = this.state;

    // An extra cell is added to the count
    // This gives the smaller Grid extra room for offset,
    // In case the main (bottom right) Grid has a scrollbar
    // If no scrollbar, the extra space is overflow:hidden anyway
    if (showHorizontalScrollbar && index === columnCount - fixedColumnCount) {
      return scrollbarSize;
    }
    return typeof columnWidth === 'function' ? columnWidth({
      index: index + fixedColumnCount
    }) : columnWidth;
  }
  _getBottomGridHeight(props) {
    const {
      height
    } = props;
    let topGridHeight = this._getTopGridHeight(props);
    return height - topGridHeight;
  }
  _getLeftGridWidth(props) {
    const {
      fixedColumnCount,
      columnWidth
    } = props;
    if (this._leftGridWidth == null) {
      if (typeof columnWidth === 'function') {
        let leftGridWidth = 0;
        for (let index = 0; index < fixedColumnCount; index++) {
          leftGridWidth += columnWidth({
            index
          });
        }
        this._leftGridWidth = leftGridWidth;
      } else {
        this._leftGridWidth = columnWidth * fixedColumnCount;
      }
    }
    return this._leftGridWidth;
  }
  _getRightGridWidth(props) {
    const {
      width
    } = props;
    let leftGridWidth = this._getLeftGridWidth(props);
    return width - leftGridWidth;
  }
  _getTopGridHeight(props) {
    const {
      fixedRowCount,
      rowHeight
    } = props;
    if (this._topGridHeight == null) {
      if (typeof rowHeight === 'function') {
        let topGridHeight = 0;
        for (let index = 0; index < fixedRowCount; index++) {
          topGridHeight += rowHeight({
            index
          });
        }
        this._topGridHeight = topGridHeight;
      } else {
        this._topGridHeight = rowHeight * fixedRowCount;
      }
    }
    return this._topGridHeight;
  }
  _handleInvalidatedGridSize() {
    if (typeof this._deferredInvalidateColumnIndex === 'number') {
      const columnIndex = this._deferredInvalidateColumnIndex;
      const rowIndex = this._deferredInvalidateRowIndex;
      this._deferredInvalidateColumnIndex = null;
      this._deferredInvalidateRowIndex = null;
      this.recomputeGridSize({
        columnIndex,
        rowIndex
      });
      this.forceUpdate();
    }
  }

  /**
   * Avoid recreating inline styles each render; this bypasses Grid's shallowCompare.
   * This method recalculates styles only when specific props change.
   */
  _maybeCalculateCachedStyles(prevProps, props) {
    const {
      columnWidth,
      enableFixedColumnScroll,
      enableFixedRowScroll,
      height,
      fixedColumnCount,
      fixedRowCount,
      rowHeight,
      style,
      styleBottomLeftGrid,
      styleBottomRightGrid,
      styleTopLeftGrid,
      styleTopRightGrid,
      width
    } = props;
    const firstRender = !prevProps;
    const sizeChange = firstRender || height !== prevProps.height || width !== prevProps.width;
    const leftSizeChange = firstRender || columnWidth !== prevProps.columnWidth || fixedColumnCount !== prevProps.fixedColumnCount;
    const topSizeChange = firstRender || fixedRowCount !== prevProps.fixedRowCount || rowHeight !== prevProps.rowHeight;
    if (firstRender || sizeChange || style !== prevProps.style) {
      this._containerOuterStyle = Object.assign({
        height,
        overflow: 'visible',
        // Let :focus outline show through
        width
      }, style);
    }
    if (firstRender || sizeChange || topSizeChange) {
      this._containerTopStyle = {
        height: this._getTopGridHeight(props),
        position: 'relative',
        width
      };
      this._containerBottomStyle = {
        height: height - this._getTopGridHeight(props),
        overflow: 'visible',
        // Let :focus outline show through
        position: 'relative',
        width
      };
    }
    if (firstRender || styleBottomLeftGrid !== prevProps.styleBottomLeftGrid) {
      this._bottomLeftGridStyle = Object.assign({
        left: 0,
        overflowX: 'hidden',
        overflowY: enableFixedColumnScroll ? 'auto' : 'hidden',
        position: 'absolute'
      }, styleBottomLeftGrid);
    }
    if (firstRender || leftSizeChange || styleBottomRightGrid !== prevProps.styleBottomRightGrid) {
      this._bottomRightGridStyle = Object.assign({
        left: this._getLeftGridWidth(props),
        position: 'absolute'
      }, styleBottomRightGrid);
    }
    if (firstRender || styleTopLeftGrid !== prevProps.styleTopLeftGrid) {
      this._topLeftGridStyle = Object.assign({
        left: 0,
        overflowX: 'hidden',
        overflowY: 'hidden',
        position: 'absolute',
        top: 0
      }, styleTopLeftGrid);
    }
    if (firstRender || leftSizeChange || styleTopRightGrid !== prevProps.styleTopRightGrid) {
      this._topRightGridStyle = Object.assign({
        left: this._getLeftGridWidth(props),
        overflowX: enableFixedRowScroll ? 'auto' : 'hidden',
        overflowY: 'hidden',
        position: 'absolute',
        top: 0
      }, styleTopRightGrid);
    }
  }
  _onScroll(scrollInfo) {
    const {
      scrollLeft,
      scrollTop
    } = scrollInfo;
    this.setState({
      scrollLeft,
      scrollTop
    });
    const onScroll = this.props.onScroll;
    if (onScroll) {
      onScroll(scrollInfo);
    }
  }
  _onScrollbarPresenceChange({
    horizontal,
    size,
    vertical
  }) {
    const {
      showHorizontalScrollbar,
      showVerticalScrollbar
    } = this.state;
    if (horizontal !== showHorizontalScrollbar || vertical !== showVerticalScrollbar) {
      this.setState({
        scrollbarSize: size,
        showHorizontalScrollbar: horizontal,
        showVerticalScrollbar: vertical
      });
    }
  }
  _onScrollLeft(scrollInfo) {
    const {
      scrollLeft
    } = scrollInfo;
    this._onScroll({
      scrollLeft,
      scrollTop: this.state.scrollTop
    });
  }
  _onScrollTop(scrollInfo) {
    const {
      scrollTop
    } = scrollInfo;
    this._onScroll({
      scrollTop,
      scrollLeft: this.state.scrollLeft
    });
  }
  _renderBottomLeftGrid(props) {
    const {
      enableFixedColumnScroll,
      fixedColumnCount,
      fixedRowCount,
      rowCount,
      scrollTop
    } = props;
    const {
      showVerticalScrollbar
    } = this.state;
    if (!fixedColumnCount) {
      return null;
    }
    const additionalRowCount = showVerticalScrollbar ? 1 : 0;
    return /*#__PURE__*/React__default.createElement(Grid, Object.assign({}, props, {
      cellRenderer: this._cellRendererBottomLeftGrid,
      className: this.props.classNameBottomLeftGrid,
      columnCount: fixedColumnCount,
      deferredMeasurementCache: this._deferredMeasurementCacheBottomLeftGrid,
      height: this._getBottomGridHeight(props),
      onScroll: enableFixedColumnScroll ? this._onScrollTop : undefined,
      ref: this._bottomLeftGridRef,
      rowCount: Math.max(0, rowCount - fixedRowCount) + additionalRowCount,
      rowHeight: this._rowHeightBottomGrid,
      scrollTop: scrollTop,
      style: this._bottomLeftGridStyle,
      tabIndex: null,
      width: this._getLeftGridWidth(props)
    }));
  }
  _renderBottomRightGrid(props) {
    const {
      columnCount,
      fixedColumnCount,
      fixedRowCount,
      rowCount,
      scrollToColumn,
      scrollToRow
    } = props;
    return /*#__PURE__*/React__default.createElement(Grid, Object.assign({}, props, {
      cellRenderer: this._cellRendererBottomRightGrid,
      className: this.props.classNameBottomRightGrid,
      columnCount: Math.max(0, columnCount - fixedColumnCount),
      columnWidth: this._columnWidthRightGrid,
      deferredMeasurementCache: this._deferredMeasurementCacheBottomRightGrid,
      height: this._getBottomGridHeight(props),
      onScroll: this._onScroll,
      onScrollbarPresenceChange: this._onScrollbarPresenceChange,
      ref: this._bottomRightGridRef,
      rowCount: Math.max(0, rowCount - fixedRowCount),
      rowHeight: this._rowHeightBottomGrid,
      scrollToColumn: scrollToColumn - fixedColumnCount,
      scrollToRow: scrollToRow - fixedRowCount,
      style: this._bottomRightGridStyle,
      width: this._getRightGridWidth(props)
    }));
  }
  _renderTopLeftGrid(props) {
    const {
      fixedColumnCount,
      fixedRowCount
    } = props;
    if (!fixedColumnCount || !fixedRowCount) {
      return null;
    }
    return /*#__PURE__*/React__default.createElement(Grid, Object.assign({}, props, {
      className: this.props.classNameTopLeftGrid,
      columnCount: fixedColumnCount,
      height: this._getTopGridHeight(props),
      ref: this._topLeftGridRef,
      rowCount: fixedRowCount,
      style: this._topLeftGridStyle,
      tabIndex: null,
      width: this._getLeftGridWidth(props)
    }));
  }
  _renderTopRightGrid(props) {
    const {
      columnCount,
      enableFixedRowScroll,
      fixedColumnCount,
      fixedRowCount,
      scrollLeft
    } = props;
    const {
      showHorizontalScrollbar
    } = this.state;
    if (!fixedRowCount) {
      return null;
    }
    const additionalColumnCount = showHorizontalScrollbar ? 1 : 0;
    return /*#__PURE__*/React__default.createElement(Grid, Object.assign({}, props, {
      cellRenderer: this._cellRendererTopRightGrid,
      className: this.props.classNameTopRightGrid,
      columnCount: Math.max(0, columnCount - fixedColumnCount) + additionalColumnCount,
      columnWidth: this._columnWidthRightGrid,
      deferredMeasurementCache: this._deferredMeasurementCacheTopRightGrid,
      height: this._getTopGridHeight(props),
      onScroll: enableFixedRowScroll ? this._onScrollLeft : undefined,
      ref: this._topRightGridRef,
      rowCount: fixedRowCount,
      scrollLeft: scrollLeft,
      style: this._topRightGridStyle,
      tabIndex: null,
      width: this._getRightGridWidth(props)
    }));
  }
  _rowHeightBottomGrid({
    index
  }) {
    const {
      fixedRowCount,
      rowCount,
      rowHeight
    } = this.props;
    const {
      scrollbarSize,
      showVerticalScrollbar
    } = this.state;

    // An extra cell is added to the count
    // This gives the smaller Grid extra room for offset,
    // In case the main (bottom right) Grid has a scrollbar
    // If no scrollbar, the extra space is overflow:hidden anyway
    if (showVerticalScrollbar && index === rowCount - fixedRowCount) {
      return scrollbarSize;
    }
    return typeof rowHeight === 'function' ? rowHeight({
      index: index + fixedRowCount
    }) : rowHeight;
  }
  _topLeftGridRef(ref) {
    this._topLeftGrid = ref;
  }
  _topRightGridRef(ref) {
    this._topRightGrid = ref;
  }
}
MultiGrid.defaultProps = {
  classNameBottomLeftGrid: '',
  classNameBottomRightGrid: '',
  classNameTopLeftGrid: '',
  classNameTopRightGrid: '',
  enableFixedColumnScroll: false,
  enableFixedRowScroll: false,
  fixedColumnCount: 0,
  fixedRowCount: 0,
  scrollToColumn: -1,
  scrollToRow: -1,
  style: {},
  styleBottomLeftGrid: {},
  styleBottomRightGrid: {},
  styleTopLeftGrid: {},
  styleTopRightGrid: {}
};
MultiGrid.propTypes = process.env.NODE_ENV !== "production" ? {
  classNameBottomLeftGrid: PropTypes.string.isRequired,
  classNameBottomRightGrid: PropTypes.string.isRequired,
  classNameTopLeftGrid: PropTypes.string.isRequired,
  classNameTopRightGrid: PropTypes.string.isRequired,
  enableFixedColumnScroll: PropTypes.bool.isRequired,
  enableFixedRowScroll: PropTypes.bool.isRequired,
  fixedColumnCount: PropTypes.number.isRequired,
  fixedRowCount: PropTypes.number.isRequired,
  style: PropTypes.object.isRequired,
  styleBottomLeftGrid: PropTypes.object.isRequired,
  styleBottomRightGrid: PropTypes.object.isRequired,
  styleTopLeftGrid: PropTypes.object.isRequired,
  styleTopRightGrid: PropTypes.object.isRequired
} : {};

/**
 * HOC that simplifies the process of synchronizing scrolling between two or more virtualized components.
 */
class ScrollSync extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      clientHeight: 0,
      clientWidth: 0,
      scrollHeight: 0,
      scrollLeft: 0,
      scrollTop: 0,
      scrollWidth: 0
    };
    this._onScroll = this._onScroll.bind(this);
  }
  render() {
    const {
      children
    } = this.props;
    const {
      clientHeight,
      clientWidth,
      scrollHeight,
      scrollLeft,
      scrollTop,
      scrollWidth
    } = this.state;
    return children({
      clientHeight,
      clientWidth,
      onScroll: this._onScroll,
      scrollHeight,
      scrollLeft,
      scrollTop,
      scrollWidth
    });
  }
  _onScroll({
    clientHeight,
    clientWidth,
    scrollHeight,
    scrollLeft,
    scrollTop,
    scrollWidth
  }) {
    this.setState({
      clientHeight,
      clientWidth,
      scrollHeight,
      scrollLeft,
      scrollTop,
      scrollWidth
    });
  }
}
ScrollSync.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Function responsible for rendering 2 or more virtualized components.
   * This function should implement the following signature:
   * ({ onScroll, scrollLeft, scrollTop }) => PropTypes.element
   */
  children: PropTypes.func.isRequired
} : {};
function createMultiSort(sortCallback, {
  defaultSortBy,
  defaultSortDirection = {}
} = {}) {
  if (!sortCallback) {
    throw Error(`Required parameter "sortCallback" not specified`);
  }
  const sortBy = defaultSortBy || [];
  const sortDirection = {};
  sortBy.forEach(dataKey => {
    sortDirection[dataKey] = defaultSortDirection.hasOwnProperty(dataKey) ? defaultSortDirection[dataKey] : 'ASC';
  });
  function sort({
    defaultSortDirection,
    event,
    sortBy: dataKey
  }) {
    if (event.shiftKey) {
      // Shift + click appends a column to existing criteria
      if (sortDirection.hasOwnProperty(dataKey)) {
        sortDirection[dataKey] = sortDirection[dataKey] === 'ASC' ? 'DESC' : 'ASC';
      } else {
        sortDirection[dataKey] = defaultSortDirection;
        sortBy.push(dataKey);
      }
    } else if (event.ctrlKey || event.metaKey) {
      // Control + click removes column from sort (if pressent)
      const index = sortBy.indexOf(dataKey);
      if (index >= 0) {
        sortBy.splice(index, 1);
        delete sortDirection[dataKey];
      }
    } else {
      sortBy.length = 0;
      sortBy.push(dataKey);
      if (sortDirection.hasOwnProperty(dataKey)) {
        sortDirection[dataKey] = sortDirection[dataKey] === 'ASC' ? 'DESC' : 'ASC';
      } else {
        sortDirection[dataKey] = defaultSortDirection;
      }
    }

    // Notify application code
    sortCallback({
      sortBy,
      sortDirection
    });
  }
  return {
    sort,
    sortBy,
    sortDirection
  };
}

/**
 * Default accessor for returning a cell value for a given attribute.
 * This function expects to operate on either a vanilla Object or an Immutable Map.
 * You should override the column's cellDataGetter if your data is some other type of object.
 */
function defaultCellDataGetter({
  dataKey,
  rowData
}) {
  if (typeof rowData.get === 'function') {
    return rowData.get(dataKey);
  } else {
    return rowData[dataKey];
  }
}

/**
 * Default cell renderer that displays an attribute as a simple string
 * You should override the column's cellRenderer if your data is some other type of object.
 */
function defaultCellRenderer({
  cellData
}) {
  if (cellData == null) {
    return '';
  } else {
    return String(cellData);
  }
}
function defaultHeaderRowRenderer({
  className,
  columns,
  style
}) {
  return /*#__PURE__*/React__default.createElement("div", {
    className: className,
    role: "row",
    style: style
  }, columns);
}
const SortDirection = {
  /**
   * Sort items in ascending order.
   * This means arranging from the lowest value to the highest (e.g. a-z, 0-9).
   */
  ASC: 'ASC',
  /**
   * Sort items in descending order.
   * This means arranging from the highest value to the lowest (e.g. z-a, 9-0).
   */
  DESC: 'DESC'
};

/**
 * Displayed beside a header to indicate that a Table is currently sorted by this column.
 */
function SortIndicator({
  sortDirection
}) {
  const classNames = cn('ReactVirtualized__Table__sortableHeaderIcon', sortDirection === SortDirection.ASC && 'ReactVirtualized__Table__sortableHeaderIcon--ASC', sortDirection === SortDirection.DESC && 'ReactVirtualized__Table__sortableHeaderIcon--DESC');
  return /*#__PURE__*/React__default.createElement("svg", {
    className: classNames,
    width: 18,
    height: 18,
    viewBox: "0 0 24 24"
  }, sortDirection === SortDirection.ASC ? /*#__PURE__*/React__default.createElement("path", {
    d: "M7 14l5-5 5 5z"
  }) : /*#__PURE__*/React__default.createElement("path", {
    d: "M7 10l5 5 5-5z"
  }), /*#__PURE__*/React__default.createElement("path", {
    d: "M0 0h24v24H0z",
    fill: "none"
  }));
}
SortIndicator.propTypes = process.env.NODE_ENV !== "production" ? {
  sortDirection: PropTypes.oneOf([SortDirection.ASC, SortDirection.DESC])
} : {};

/**
 * Default table header renderer.
 */
function defaultHeaderRenderer({
  dataKey,
  label,
  sortBy,
  sortDirection
}) {
  const showSortIndicator = sortBy === dataKey;
  const children = [/*#__PURE__*/React__default.createElement("span", {
    className: "ReactVirtualized__Table__headerTruncatedText",
    key: "label",
    title: label
  }, label)];
  if (showSortIndicator) {
    children.push( /*#__PURE__*/React__default.createElement(SortIndicator, {
      key: "SortIndicator",
      sortDirection: sortDirection
    }));
  }
  return children;
}

/**
 * Default row renderer for Table.
 */
function defaultRowRenderer({
  className,
  columns,
  index,
  key,
  onRowClick,
  onRowDoubleClick,
  onRowMouseOut,
  onRowMouseOver,
  onRowRightClick,
  rowData,
  style
}) {
  const a11yProps = {};
  if (onRowClick || onRowDoubleClick || onRowMouseOut || onRowMouseOver || onRowRightClick) {
    a11yProps['aria-label'] = 'row';
    a11yProps.tabIndex = 0;
    if (onRowClick) {
      a11yProps.onClick = event => onRowClick({
        event,
        index,
        rowData
      });
    }
    if (onRowDoubleClick) {
      a11yProps.onDoubleClick = event => onRowDoubleClick({
        event,
        index,
        rowData
      });
    }
    if (onRowMouseOut) {
      a11yProps.onMouseOut = event => onRowMouseOut({
        event,
        index,
        rowData
      });
    }
    if (onRowMouseOver) {
      a11yProps.onMouseOver = event => onRowMouseOver({
        event,
        index,
        rowData
      });
    }
    if (onRowRightClick) {
      a11yProps.onContextMenu = event => onRowRightClick({
        event,
        index,
        rowData
      });
    }
  }
  return /*#__PURE__*/React__default.createElement("div", Object.assign({}, a11yProps, {
    className: className,
    key: key,
    role: "row",
    style: style
  }), columns);
}

/**
 * Describes the header and cell contents of a table column.
 */
class Column extends Component {}
Column.defaultProps = {
  cellDataGetter: defaultCellDataGetter,
  cellRenderer: defaultCellRenderer,
  defaultSortDirection: SortDirection.ASC,
  flexGrow: 0,
  flexShrink: 1,
  headerRenderer: defaultHeaderRenderer,
  style: {}
};
Column.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Optional aria-label value to set on the column header */
  'aria-label': PropTypes.string,
  /**
   * Callback responsible for returning a cell's data, given its :dataKey
   * ({ columnData: any, dataKey: string, rowData: any }): any
   */
  cellDataGetter: PropTypes.func,
  /**
   * Callback responsible for rendering a cell's contents.
   * ({ cellData: any, columnData: any, dataKey: string, rowData: any, rowIndex: number }): node
   */
  cellRenderer: PropTypes.func,
  /** Optional CSS class to apply to cell */
  className: PropTypes.string,
  /** Optional additional data passed to this column's :cellDataGetter */
  columnData: PropTypes.object,
  /** Uniquely identifies the row-data attribute corresponding to this cell */
  dataKey: PropTypes.any.isRequired,
  /** Optional direction to be used when clicked the first time */
  defaultSortDirection: PropTypes.oneOf([SortDirection.ASC, SortDirection.DESC]),
  /** If sort is enabled for the table at large, disable it for this column */
  disableSort: PropTypes.bool,
  /** Flex grow style; defaults to 0 */
  flexGrow: PropTypes.number,
  /** Flex shrink style; defaults to 1 */
  flexShrink: PropTypes.number,
  /** Optional CSS class to apply to this column's header */
  headerClassName: PropTypes.string,
  /**
   * Optional callback responsible for rendering a column header contents.
   * ({ columnData: object, dataKey: string, disableSort: boolean, label: node, sortBy: string, sortDirection: string }): PropTypes.node
   */
  headerRenderer: PropTypes.func.isRequired,
  /** Optional inline style to apply to this column's header */
  headerStyle: PropTypes.object,
  /** Optional id to set on the column header */
  id: PropTypes.string,
  /** Header label for this column */
  label: PropTypes.node,
  /** Maximum width of column; this property will only be used if :flexGrow is > 0. */
  maxWidth: PropTypes.number,
  /** Minimum width of column. */
  minWidth: PropTypes.number,
  /** Optional inline style to apply to cell */
  style: PropTypes.object,
  /** Flex basis (width) for this column; This value can grow or shrink based on :flexGrow and :flexShrink properties. */
  width: PropTypes.number.isRequired
} : {};

/**
 * Table component with fixed headers and virtualized rows for improved performance with large data sets.
 * This component expects explicit width, height, and padding parameters.
 */
class Table extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      scrollbarWidth: 0
    };
    this._createColumn = this._createColumn.bind(this);
    this._createRow = this._createRow.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onSectionRendered = this._onSectionRendered.bind(this);
    this._setRef = this._setRef.bind(this);
    this._setGridElementRef = this._setGridElementRef.bind(this);
  }
  forceUpdateGrid() {
    if (this.Grid) {
      this.Grid.forceUpdate();
    }
  }

  /** See Grid#getOffsetForCell */
  getOffsetForRow({
    alignment,
    index
  }) {
    if (this.Grid) {
      const {
        scrollTop
      } = this.Grid.getOffsetForCell({
        alignment,
        rowIndex: index
      });
      return scrollTop;
    }
    return 0;
  }

  /** CellMeasurer compatibility */
  invalidateCellSizeAfterRender({
    columnIndex,
    rowIndex
  }) {
    if (this.Grid) {
      this.Grid.invalidateCellSizeAfterRender({
        rowIndex,
        columnIndex
      });
    }
  }

  /** See Grid#measureAllCells */
  measureAllRows() {
    if (this.Grid) {
      this.Grid.measureAllCells();
    }
  }

  /** CellMeasurer compatibility */
  recomputeGridSize({
    columnIndex = 0,
    rowIndex = 0
  } = {}) {
    if (this.Grid) {
      this.Grid.recomputeGridSize({
        rowIndex,
        columnIndex
      });
    }
  }

  /** See Grid#recomputeGridSize */
  recomputeRowHeights(index = 0) {
    if (this.Grid) {
      this.Grid.recomputeGridSize({
        rowIndex: index
      });
    }
  }

  /** See Grid#scrollToPosition */
  scrollToPosition(scrollTop = 0) {
    if (this.Grid) {
      this.Grid.scrollToPosition({
        scrollTop
      });
    }
  }

  /** See Grid#scrollToCell */
  scrollToRow(index = 0) {
    if (this.Grid) {
      this.Grid.scrollToCell({
        columnIndex: 0,
        rowIndex: index
      });
    }
  }
  componentDidMount() {
    this._setScrollbarWidth();
  }
  componentDidUpdate() {
    this._setScrollbarWidth();
  }
  render() {
    const {
      children,
      className,
      disableHeader,
      gridClassName,
      gridStyle,
      headerHeight,
      headerRowRenderer,
      height,
      id,
      noRowsRenderer,
      rowClassName,
      rowStyle,
      scrollToIndex,
      style,
      width
    } = this.props;
    const {
      scrollbarWidth
    } = this.state;
    const availableRowsHeight = disableHeader ? height : height - headerHeight;
    const rowClass = typeof rowClassName === 'function' ? rowClassName({
      index: -1
    }) : rowClassName;
    const rowStyleObject = typeof rowStyle === 'function' ? rowStyle({
      index: -1
    }) : rowStyle;

    // Precompute and cache column styles before rendering rows and columns to speed things up
    this._cachedColumnStyles = [];
    React__default.Children.toArray(children).forEach((column, index) => {
      const flexStyles = this._getFlexStyleForColumn(column, column.props.style);
      this._cachedColumnStyles[index] = Object.assign({}, flexStyles, {
        overflow: 'hidden'
      });
    });

    // Note that we specify :rowCount, :scrollbarWidth, :sortBy, and :sortDirection as properties on Grid even though these have nothing to do with Grid.
    // This is done because Grid is a pure component and won't update unless its properties or state has changed.
    // Any property that should trigger a re-render of Grid then is specified here to avoid a stale display.
    return /*#__PURE__*/React__default.createElement("div", {
      className: cn('ReactVirtualized__Table', className),
      id: id,
      role: "grid",
      style: style
    }, !disableHeader && headerRowRenderer({
      className: cn('ReactVirtualized__Table__headerRow', rowClass),
      columns: this._getHeaderColumns(),
      style: Object.assign({}, rowStyleObject, {
        height: headerHeight,
        overflow: 'hidden',
        paddingRight: scrollbarWidth,
        width: width
      })
    }), /*#__PURE__*/React__default.createElement(Grid, Object.assign({}, this.props, {
      elementRef: this._setGridElementRef,
      autoContainerWidth: true,
      className: cn('ReactVirtualized__Table__Grid', gridClassName),
      cellRenderer: this._createRow,
      columnWidth: width,
      columnCount: 1,
      height: availableRowsHeight,
      id: undefined,
      noContentRenderer: noRowsRenderer,
      onScroll: this._onScroll,
      onSectionRendered: this._onSectionRendered,
      ref: this._setRef,
      role: "rowgroup",
      scrollbarWidth: scrollbarWidth,
      scrollToRow: scrollToIndex,
      style: Object.assign({}, gridStyle, {
        overflowX: 'hidden'
      })
    })));
  }
  _createColumn({
    column,
    columnIndex,
    isScrolling,
    parent,
    rowData,
    rowIndex
  }) {
    const {
      cellDataGetter,
      cellRenderer,
      className,
      columnData,
      dataKey,
      id
    } = column.props;
    const cellData = cellDataGetter({
      columnData,
      dataKey,
      rowData
    });
    const renderedCell = cellRenderer({
      cellData,
      columnData,
      columnIndex,
      dataKey,
      isScrolling,
      parent,
      rowData,
      rowIndex
    });
    const style = this._cachedColumnStyles[columnIndex];
    const title = typeof renderedCell === 'string' ? renderedCell : null;

    // Avoid using object-spread syntax with multiple objects here,
    // Since it results in an extra method call to 'babel-runtime/helpers/extends'
    // See PR https://github.com/bvaughn/react-virtualized/pull/942
    return /*#__PURE__*/React__default.createElement("div", {
      "aria-describedby": id,
      className: cn('ReactVirtualized__Table__rowColumn', className),
      key: 'Row' + rowIndex + '-' + 'Col' + columnIndex,
      role: "gridcell",
      style: style,
      title: title
    }, renderedCell);
  }
  _createHeader({
    column,
    index
  }) {
    const {
      headerClassName,
      headerStyle,
      onHeaderClick,
      sort,
      sortBy,
      sortDirection
    } = this.props;
    const {
      columnData,
      dataKey,
      defaultSortDirection,
      disableSort,
      headerRenderer,
      id,
      label
    } = column.props;
    const sortEnabled = !disableSort && sort;
    const classNames = cn('ReactVirtualized__Table__headerColumn', headerClassName, column.props.headerClassName, sortEnabled && "ReactVirtualized__Table__sortableHeaderColumn");
    const style = this._getFlexStyleForColumn(column, Object.assign({}, headerStyle, column.props.headerStyle));
    const renderedHeader = headerRenderer({
      columnData,
      dataKey,
      disableSort,
      label,
      sortBy,
      sortDirection
    });
    let headerOnClick, headerOnKeyDown, headerTabIndex, headerAriaSort, headerAriaLabel;
    if (sortEnabled || onHeaderClick) {
      // If this is a sortable header, clicking it should update the table data's sorting.
      const isFirstTimeSort = sortBy !== dataKey;

      // If this is the firstTime sort of this column, use the column default sort order.
      // Otherwise, invert the direction of the sort.
      const newSortDirection = isFirstTimeSort ? defaultSortDirection : sortDirection === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC;
      const onClick = event => {
        sortEnabled && sort({
          defaultSortDirection,
          event,
          sortBy: dataKey,
          sortDirection: newSortDirection
        });
        onHeaderClick && onHeaderClick({
          columnData,
          dataKey,
          event
        });
      };
      const onKeyDown = event => {
        if (event.key === 'Enter' || event.key === ' ') {
          onClick(event);
        }
      };
      headerAriaLabel = column.props['aria-label'] || label || dataKey;
      headerTabIndex = 0;
      headerOnClick = onClick;
      headerOnKeyDown = onKeyDown;
    }
    if (sortBy === dataKey) {
      headerAriaSort = sortDirection === SortDirection.ASC ? 'ascending' : 'descending';
    }

    // Avoid using object-spread syntax with multiple objects here,
    // Since it results in an extra method call to 'babel-runtime/helpers/extends'
    // See PR https://github.com/bvaughn/react-virtualized/pull/942
    return /*#__PURE__*/React__default.createElement("div", {
      "aria-label": headerAriaLabel,
      "aria-sort": headerAriaSort,
      className: classNames,
      id: id,
      key: 'Header-Col' + index,
      onClick: headerOnClick,
      onKeyDown: headerOnKeyDown,
      role: "columnheader",
      style: style,
      tabIndex: headerTabIndex
    }, renderedHeader);
  }
  _createRow({
    rowIndex: index,
    isScrolling,
    key,
    parent,
    style
  }) {
    const {
      children,
      onRowClick,
      onRowDoubleClick,
      onRowRightClick,
      onRowMouseOver,
      onRowMouseOut,
      rowClassName,
      rowGetter,
      rowRenderer,
      rowStyle
    } = this.props;
    const {
      scrollbarWidth
    } = this.state;
    const rowClass = typeof rowClassName === 'function' ? rowClassName({
      index
    }) : rowClassName;
    const rowStyleObject = typeof rowStyle === 'function' ? rowStyle({
      index
    }) : rowStyle;
    const rowData = rowGetter({
      index
    });
    const columns = React__default.Children.toArray(children).map((column, columnIndex) => this._createColumn({
      column,
      columnIndex,
      isScrolling,
      parent,
      rowData,
      rowIndex: index,
      scrollbarWidth
    }));
    const className = cn('ReactVirtualized__Table__row', rowClass);
    const flattenedStyle = Object.assign({}, style, rowStyleObject, {
      height: this._getRowHeight(index),
      overflow: 'hidden',
      paddingRight: scrollbarWidth
    });
    return rowRenderer({
      className,
      columns,
      index,
      isScrolling,
      key,
      onRowClick,
      onRowDoubleClick,
      onRowRightClick,
      onRowMouseOver,
      onRowMouseOut,
      rowData,
      style: flattenedStyle
    });
  }

  /**
   * Determines the flex-shrink, flex-grow, and width values for a cell (header or column).
   */
  _getFlexStyleForColumn(column, customStyle = {}) {
    const flexValue = `${column.props.flexGrow} ${column.props.flexShrink} ${column.props.width}px`;
    const style = Object.assign({}, customStyle, {
      flex: flexValue,
      msFlex: flexValue,
      WebkitFlex: flexValue
    });
    if (column.props.maxWidth) {
      style.maxWidth = column.props.maxWidth;
    }
    if (column.props.minWidth) {
      style.minWidth = column.props.minWidth;
    }
    return style;
  }
  _getHeaderColumns() {
    const {
      children,
      disableHeader
    } = this.props;
    const items = disableHeader ? [] : React__default.Children.toArray(children);
    return items.map((column, index) => this._createHeader({
      column,
      index
    }));
  }
  _getRowHeight(rowIndex) {
    const {
      rowHeight
    } = this.props;
    return typeof rowHeight === 'function' ? rowHeight({
      index: rowIndex
    }) : rowHeight;
  }
  _onScroll({
    clientHeight,
    scrollHeight,
    scrollTop
  }) {
    const {
      onScroll
    } = this.props;
    onScroll({
      clientHeight,
      scrollHeight,
      scrollTop
    });
  }
  _onSectionRendered({
    rowOverscanStartIndex,
    rowOverscanStopIndex,
    rowStartIndex,
    rowStopIndex
  }) {
    const {
      onRowsRendered
    } = this.props;
    onRowsRendered({
      overscanStartIndex: rowOverscanStartIndex,
      overscanStopIndex: rowOverscanStopIndex,
      startIndex: rowStartIndex,
      stopIndex: rowStopIndex
    });
  }
  _setRef(ref) {
    this.Grid = ref;
  }
  _setGridElementRef(ref) {
    this.GridElement = ref;
  }
  _setScrollbarWidth() {
    if (this.GridElement) {
      const Grid = this.GridElement;
      const clientWidth = Grid.clientWidth || 0;
      const offsetWidth = Grid.offsetWidth || 0;
      const scrollbarWidth = offsetWidth - clientWidth;
      this.setState({
        scrollbarWidth
      });
    }
  }
}
Table.defaultProps = {
  disableHeader: false,
  estimatedRowSize: 30,
  headerHeight: 0,
  headerStyle: {},
  noRowsRenderer: () => null,
  onRowsRendered: () => null,
  onScroll: () => null,
  overscanIndicesGetter: defaultOverscanIndicesGetter,
  overscanRowCount: 10,
  rowRenderer: defaultRowRenderer,
  headerRowRenderer: defaultHeaderRowRenderer,
  rowStyle: {},
  scrollToAlignment: 'auto',
  scrollToIndex: -1,
  style: {}
};
Table.propTypes = process.env.NODE_ENV !== "production" ? {
  'aria-label': PropTypes.string,
  /**
   * Removes fixed height from the scrollingContainer so that the total height
   * of rows can stretch the window. Intended for use with WindowScroller
   */
  autoHeight: PropTypes.bool,
  /** One or more Columns describing the data displayed in this row */
  children: props => {
    const children = React__default.Children.toArray(props.children);
    for (let i = 0; i < children.length; i++) {
      const childType = children[i].type;
      if (childType !== Column && !(childType.prototype instanceof Column)) {
        return new Error('Table only accepts children of type Column');
      }
    }
  },
  /** Optional CSS class name */
  className: PropTypes.string,
  /** Disable rendering the header at all */
  disableHeader: PropTypes.bool,
  /**
   * Used to estimate the total height of a Table before all of its rows have actually been measured.
   * The estimated total height is adjusted as rows are rendered.
   */
  estimatedRowSize: PropTypes.number.isRequired,
  /** Optional custom CSS class name to attach to inner Grid element. */
  gridClassName: PropTypes.string,
  /** Optional inline style to attach to inner Grid element. */
  gridStyle: PropTypes.object,
  /** Optional CSS class to apply to all column headers */
  headerClassName: PropTypes.string,
  /** Fixed height of header row */
  headerHeight: PropTypes.number.isRequired,
  /**
   * Responsible for rendering a table row given an array of columns:
   * Should implement the following interface: ({
   *   className: string,
   *   columns: any[],
   *   style: any
   * }): PropTypes.node
   */
  headerRowRenderer: PropTypes.func,
  /** Optional custom inline style to attach to table header columns. */
  headerStyle: PropTypes.object,
  /** Fixed/available height for out DOM element */
  height: PropTypes.number.isRequired,
  /** Optional id */
  id: PropTypes.string,
  /** Optional renderer to be used in place of table body rows when rowCount is 0 */
  noRowsRenderer: PropTypes.func,
  /**
   * Optional callback when a column's header is clicked.
   * ({ columnData: any, dataKey: string }): void
   */
  onHeaderClick: PropTypes.func,
  /**
   * Callback invoked when a user clicks on a table row.
   * ({ index: number }): void
   */
  onRowClick: PropTypes.func,
  /**
   * Callback invoked when a user double-clicks on a table row.
   * ({ index: number }): void
   */
  onRowDoubleClick: PropTypes.func,
  /**
   * Callback invoked when the mouse leaves a table row.
   * ({ index: number }): void
   */
  onRowMouseOut: PropTypes.func,
  /**
   * Callback invoked when a user moves the mouse over a table row.
   * ({ index: number }): void
   */
  onRowMouseOver: PropTypes.func,
  /**
   * Callback invoked when a user right-clicks on a table row.
   * ({ index: number }): void
   */
  onRowRightClick: PropTypes.func,
  /**
   * Callback invoked with information about the slice of rows that were just rendered.
   * ({ startIndex, stopIndex }): void
   */
  onRowsRendered: PropTypes.func,
  /**
   * Callback invoked whenever the scroll offset changes within the inner scrollable region.
   * This callback can be used to sync scrolling between lists, tables, or grids.
   * ({ clientHeight, scrollHeight, scrollTop }): void
   */
  onScroll: PropTypes.func.isRequired,
  /** See Grid#overscanIndicesGetter */
  overscanIndicesGetter: PropTypes.func.isRequired,
  /**
   * Number of rows to render above/below the visible bounds of the list.
   * These rows can help for smoother scrolling on touch devices.
   */
  overscanRowCount: PropTypes.number.isRequired,
  /**
   * Optional CSS class to apply to all table rows (including the header row).
   * This property can be a CSS class name (string) or a function that returns a class name.
   * If a function is provided its signature should be: ({ index: number }): string
   */
  rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /**
   * Callback responsible for returning a data row given an index.
   * ({ index: number }): any
   */
  rowGetter: PropTypes.func.isRequired,
  /**
   * Either a fixed row height (number) or a function that returns the height of a row given its index.
   * ({ index: number }): number
   */
  rowHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.func]).isRequired,
  /** Number of rows in table. */
  rowCount: PropTypes.number.isRequired,
  /**
   * Responsible for rendering a table row given an array of columns:
   * Should implement the following interface: ({
   *   className: string,
   *   columns: Array,
   *   index: number,
   *   isScrolling: boolean,
   *   onRowClick: ?Function,
   *   onRowDoubleClick: ?Function,
   *   onRowMouseOver: ?Function,
   *   onRowMouseOut: ?Function,
   *   rowData: any,
   *   style: any
   * }): PropTypes.node
   */
  rowRenderer: PropTypes.func,
  /** Optional custom inline style to attach to table rows. */
  rowStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
  /** See Grid#scrollToAlignment */
  scrollToAlignment: PropTypes.oneOf(['auto', 'end', 'start', 'center']).isRequired,
  /** Row index to ensure visible (by forcefully scrolling if necessary) */
  scrollToIndex: PropTypes.number.isRequired,
  /** Vertical offset. */
  scrollTop: PropTypes.number,
  /**
   * Sort function to be called if a sortable header is clicked.
   * Should implement the following interface: ({
   *   defaultSortDirection: 'ASC' | 'DESC',
   *   event: MouseEvent,
   *   sortBy: string,
   *   sortDirection: SortDirection
   * }): void
   */
  sort: PropTypes.func,
  /** Table data is currently sorted by this :dataKey (if it is sorted at all) */
  sortBy: PropTypes.string,
  /** Table data is currently sorted in this direction (if it is sorted at all) */
  sortDirection: PropTypes.oneOf([SortDirection.ASC, SortDirection.DESC]),
  /** Optional inline style */
  style: PropTypes.object,
  /** Tab index for focus */
  tabIndex: PropTypes.number,
  /** Width of list */
  width: PropTypes.number.isRequired
} : {};
let mountedInstances = [];
let originalBodyPointerEvents = null;
let disablePointerEventsTimeoutId = null;
function enablePointerEventsIfDisabled() {
  if (disablePointerEventsTimeoutId) {
    disablePointerEventsTimeoutId = null;
    if (document.body && originalBodyPointerEvents != null) {
      document.body.style.pointerEvents = originalBodyPointerEvents;
    }
    originalBodyPointerEvents = null;
  }
}
function enablePointerEventsAfterDelayCallback() {
  enablePointerEventsIfDisabled();
  mountedInstances.forEach(instance => instance.__resetIsScrolling());
}
function enablePointerEventsAfterDelay() {
  if (disablePointerEventsTimeoutId) {
    cancelAnimationTimeout(disablePointerEventsTimeoutId);
  }
  var maximumTimeout = 0;
  mountedInstances.forEach(instance => {
    maximumTimeout = Math.max(maximumTimeout, instance.props.scrollingResetTimeInterval);
  });
  disablePointerEventsTimeoutId = requestAnimationTimeout(enablePointerEventsAfterDelayCallback, maximumTimeout);
}
function onScrollWindow(event) {
  if (event.currentTarget === window && originalBodyPointerEvents == null && document.body) {
    originalBodyPointerEvents = document.body.style.pointerEvents;
    document.body.style.pointerEvents = 'none';
  }
  enablePointerEventsAfterDelay();
  mountedInstances.forEach(instance => {
    if (instance.props.scrollElement === event.currentTarget) {
      instance.__handleWindowScrollEvent();
    }
  });
}
function registerScrollListener(component, element) {
  if (!mountedInstances.some(instance => instance.props.scrollElement === element)) {
    element.addEventListener('scroll', onScrollWindow);
  }
  mountedInstances.push(component);
}
function unregisterScrollListener(component, element) {
  mountedInstances = mountedInstances.filter(instance => instance !== component);
  if (!mountedInstances.length) {
    element.removeEventListener('scroll', onScrollWindow);
    if (disablePointerEventsTimeoutId) {
      cancelAnimationTimeout(disablePointerEventsTimeoutId);
      enablePointerEventsIfDisabled();
    }
  }
}

/**
 * Gets the dimensions of the element, accounting for API differences between
 * `window` and other DOM elements.
 */
// TODO Move this into WindowScroller and import from there
const isWindow = element => element === window;
const getBoundingBox = element => element.getBoundingClientRect();
function getDimensions(scrollElement, props) {
  if (!scrollElement) {
    return {
      height: props.serverHeight,
      width: props.serverWidth
    };
  } else if (isWindow(scrollElement)) {
    const {
      innerHeight,
      innerWidth
    } = window;
    return {
      height: typeof innerHeight === 'number' ? innerHeight : 0,
      width: typeof innerWidth === 'number' ? innerWidth : 0
    };
  } else {
    return getBoundingBox(scrollElement);
  }
}

/**
 * Gets the vertical and horizontal position of an element within its scroll container.
 * Elements that have been “scrolled past” return negative values.
 * Handles edge-case where a user is navigating back (history) from an already-scrolled page.
 * In this case the body’s top or left position will be a negative number and this element’s top or left will be increased (by that amount).
 */
function getPositionOffset(element, container) {
  if (isWindow(container) && document.documentElement) {
    const containerElement = document.documentElement;
    const elementRect = getBoundingBox(element);
    const containerRect = getBoundingBox(containerElement);
    return {
      top: elementRect.top - containerRect.top,
      left: elementRect.left - containerRect.left
    };
  } else {
    const scrollOffset = getScrollOffset(container);
    const elementRect = getBoundingBox(element);
    const containerRect = getBoundingBox(container);
    return {
      top: elementRect.top + scrollOffset.top - containerRect.top,
      left: elementRect.left + scrollOffset.left - containerRect.left
    };
  }
}

/**
 * Gets the vertical and horizontal scroll amount of the element, accounting for IE compatibility
 * and API differences between `window` and other DOM elements.
 */
function getScrollOffset(element) {
  if (isWindow(element) && document.documentElement) {
    return {
      top: 'scrollY' in window ? window.scrollY : document.documentElement.scrollTop,
      left: 'scrollX' in window ? window.scrollX : document.documentElement.scrollLeft
    };
  } else {
    return {
      top: element.scrollTop,
      left: element.scrollLeft
    };
  }
}

/**
 * Specifies the number of miliseconds during which to disable pointer events while a scroll is in progress.
 * This improves performance and makes scrolling smoother.
 */
const IS_SCROLLING_TIMEOUT = 150;
const getWindow = () => typeof window !== 'undefined' ? window : undefined;
class WindowScroller extends React.PureComponent {
  constructor(...args) {
    super(...args);
    this._window = getWindow();
    this._isMounted = false;
    this._positionFromTop = 0;
    this._positionFromLeft = 0;
    this._windowScrollerRef = /*#__PURE__*/React.createRef();
    this.state = Object.assign({}, getDimensions(this.props.scrollElement, this.props), {
      isScrolling: false,
      scrollLeft: 0,
      scrollTop: 0
    });
    this._registerChild = element => {
      if (element && !(element instanceof Element)) {
        console.warn('WindowScroller registerChild expects to be passed Element or null');
      }
      this._child = element;
      this.updatePosition();
    };
    this._onChildScroll = ({
      scrollTop
    }) => {
      if (this.state.scrollTop === scrollTop) {
        return;
      }
      const scrollElement = this.props.scrollElement;
      if (scrollElement) {
        if (typeof scrollElement.scrollTo === 'function') {
          scrollElement.scrollTo(0, scrollTop + this._positionFromTop);
        } else {
          scrollElement.scrollTop = scrollTop + this._positionFromTop;
        }
      }
    };
    this._registerResizeListener = element => {
      if (element === window) {
        window.addEventListener('resize', this._onResize, false);
      } else {
        this._detectElementResize.addResizeListener(element, this._onResize);
      }
    };
    this._unregisterResizeListener = element => {
      if (element === window) {
        window.removeEventListener('resize', this._onResize, false);
      } else if (element) {
        this._detectElementResize.removeResizeListener(element, this._onResize);
      }
    };
    this._onResize = () => {
      this.updatePosition();
    };
    this.__handleWindowScrollEvent = () => {
      if (!this._isMounted) {
        return;
      }
      const {
        onScroll
      } = this.props;
      const scrollElement = this.props.scrollElement;
      if (scrollElement) {
        const scrollOffset = getScrollOffset(scrollElement);
        const scrollLeft = Math.max(0, scrollOffset.left - this._positionFromLeft);
        const scrollTop = Math.max(0, scrollOffset.top - this._positionFromTop);
        this.setState({
          isScrolling: true,
          scrollLeft,
          scrollTop
        });
        onScroll({
          scrollLeft,
          scrollTop
        });
      }
    };
    this.__resetIsScrolling = () => {
      this.setState({
        isScrolling: false
      });
    };
  }
  updatePosition(scrollElement = this.props.scrollElement, props = this.props) {
    const {
      onResize
    } = this.props;
    const {
      height,
      width
    } = this.state;
    const thisNode = this._child || this._windowScrollerRef.current;
    if (thisNode instanceof Element && scrollElement) {
      const offset = getPositionOffset(thisNode, scrollElement);
      this._positionFromTop = offset.top;
      this._positionFromLeft = offset.left;
    }
    const dimensions = getDimensions(scrollElement, props);
    if (height !== dimensions.height || width !== dimensions.width) {
      this.setState({
        height: dimensions.height,
        width: dimensions.width
      });
      onResize({
        height: dimensions.height,
        width: dimensions.width
      });
    }
  }
  componentDidMount() {
    const scrollElement = this.props.scrollElement;
    this._detectElementResize = createDetectElementResize();
    this.updatePosition(scrollElement);
    if (scrollElement) {
      registerScrollListener(this, scrollElement);
      this._registerResizeListener(scrollElement);
    }
    this._isMounted = true;
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    const scrollElement = this.props.scrollElement;
    const nextScrollElement = nextProps.scrollElement;
    if (scrollElement !== nextScrollElement && scrollElement && nextScrollElement) {
      this.updatePosition(nextScrollElement, nextProps);
      unregisterScrollListener(this, scrollElement);
      registerScrollListener(this, nextScrollElement);
      this._unregisterResizeListener(scrollElement);
      this._registerResizeListener(nextScrollElement);
    }
  }
  componentWillUnmount() {
    const scrollElement = this.props.scrollElement;
    if (scrollElement) {
      unregisterScrollListener(this, scrollElement);
      this._unregisterResizeListener(scrollElement);
    }
    this._isMounted = false;
  }
  render() {
    const {
      children
    } = this.props;
    const {
      isScrolling,
      scrollTop,
      scrollLeft,
      height,
      width
    } = this.state;
    return /*#__PURE__*/React.createElement('div', {
      ref: this._windowScrollerRef
    }, children({
      onChildScroll: this._onChildScroll,
      registerChild: this._registerChild,
      height,
      isScrolling,
      scrollLeft,
      scrollTop,
      width
    }));
  }

  // Referenced by utils/onScroll

  // Referenced by utils/onScroll
}
WindowScroller.defaultProps = {
  onResize: () => {},
  onScroll: () => {},
  scrollingResetTimeInterval: IS_SCROLLING_TIMEOUT,
  scrollElement: getWindow(),
  serverHeight: 0,
  serverWidth: 0
};
export { ArrowKeyStepper, AutoSizer, CellMeasurer, CellMeasurerCache, Collection, Column, ColumnSizer, Grid, InfiniteLoader, List, Masonry, MultiGrid, ScrollSync, SortDirection, SortIndicator, Table, WindowScroller, defaultOverscanIndicesGetter as accessibilityOverscanIndicesGetter, createCellPositioner as createMasonryCellPositioner, createMultiSort as createTableMultiSort, defaultCellRangeRenderer, defaultOverscanIndicesGetter$1 as defaultOverscanIndicesGetter, defaultCellDataGetter as defaultTableCellDataGetter, defaultCellRenderer as defaultTableCellRenderer, defaultHeaderRenderer as defaultTableHeaderRenderer, defaultHeaderRowRenderer as defaultTableHeaderRowRenderer, defaultRowRenderer as defaultTableRowRenderer };